/**
 * App Builderin reaktiivinen selainruntime (AB-WP2.1). Boottaa palvelin-
 * renderöidyn sivun (AB-WP1.3:n AppRenderer) päälle:
 *
 *  - siementää vars/params-nimiavaruudet,
 *  - kytkee input-slotit DOM-eventteihin (text-input.value/isFocused,
 *    form.values/isDirty/isValid/errors),
 *  - rekisteröi <script class="sp-app-bindings"> -lohkojen lausekkeet:
 *    parse kerran, riippuvuudet {@link collectDeps}illa, re-evaluointi
 *    kun jokin riippuvuus muuttuu, tulos DOMiin slotin semantiikalla,
 *  - kytkee tabs/modal/alert-interaktiot ja Buttonin lifecycle-actionit
 *    (on-click; trigger-api-ajoitukset kytkeytyvät WP4.1:n bridgeen).
 *
 * Runtime on additiivinen: komponentti joka ei julkaise bindingejä eikä
 * slotteja jää koskematta. Verkkopolut (form submit, dynamic-table fetch,
 * trigger-api) kulkevat window.fasthub.bridge-rajapinnan kautta, jonka
 * AB-WP4.1 toteuttaa — ilman bridgeä ne no-oppaavat siististi.
 */

import { collectDeps, evalAst, evalExpression, parse, rewriteScopedIds } from './expression';
import type { AstNode, ExprState } from './expression';
import {
  applyRowScope,
  collectScopeIds,
  padToMinRows,
  parseRepeaterRows,
  presetRowInputs,
  readRowInputs,
  rowKeyOf,
  rowStateId,
  scopedId,
} from './repeater';
import { createStore, type Store } from './store';
import {
  createBridgeClient, NO_IDENTITY, type BridgeClient, type InvokeResult,
} from './bridge';
import {
  buildAnswer, formatDeadline, isOverdue, isPending, parseUserActions,
  type UserActionAnswer, type UserActionItem,
} from './userActions';
import { buildEmbedderMessage } from './embedderMessage';
import { createTracer, type Tracer } from './trace';
import { attachTraceStream } from './traceStream';
import {
  buildRequestParams,
  buildRowActionBody,
  ERROR_MESSAGE_MAX,
  extractErrorMessage,
  extractRows,
  parseResponseBody,
  renderTableRows,
  resolvePath,
  type RequestParameter,
  type RowAction,
  type RowActionMappingEntry,
  type TableColumn,
} from './dataSources';
import {
  applyPagination, coerceRows, cycleSort, filterRows, formatCellValue, parseColumns, sortRows,
  toCsv, type GridColumn, type SortDir,
} from './dataGrid';
import {
  cardsInColumn, columnCounts, moveCard, parseCards, parseColumns as parseKanbanColumns,
  type KanbanCard, type KanbanColumn,
} from './kanban';
import {
  axisTicks, layoutTask, parseTasks, pxPerDayFor, taskSpan,
  type GanttScale, type GanttTask,
} from './gantt';
import {
  chartStaticRows, drawChart, resolvePalette, toChartPoints, toChartSeries,
  type ChartOptions, type ChartType,
} from './chart';
import { evaluateRules, parseRules } from './formValidation';
import { configureMessages, currentLocale, resolveMessage } from './messages';
import { clampPage, ELLIPSIS, offsetOf, pageCountOf, pageItems, rangeText } from './pagination';
import { formatDelta, formatStatValue, resolveDeltaDirection } from './stat';
import { colorFromName, initialsOf } from './avatar';
import { keepSelection, parseOptionList } from './options';
import { sanitizeUrl } from './url';
import { daysBetween, detectPreset, resolvePreset } from './dateRange';
import { describeSkipped, extractBase64, isWithinSize, type PickedFile } from './fileUpload';
import { addCompleted, canClickTo } from './stepper';
import { nowLocal, todayLocal } from './dateFunctions';

interface AppVariable {
  name: string;
  defaultValue?: unknown;
  persist?: 'none' | 'local';
}

/**
 * Appia katsova käyttäjä (WP-ABP5). Palvelin siementää autentikoidulle;
 * anonyymille kentät ovat tyhjiä ja roolit tyhjä taulukko.
 */
export interface AppCurrentUser {
  userId: string;
  username: string;
  displayName: string;
  email: string;
  roles: string[];
}

declare global {
  interface Window {
    fasthub?: {
      appId?: string;
      appVariables?: AppVariable[];
      currentUser?: Partial<AppCurrentUser>;
      appOnLoad?: unknown[];
      /** Appin kieli + viestiohitukset (WP-VM2); puuttuu kun metadataa ei ole. */
      appMessages?: { locale?: string; overrides?: Record<string, string> };
      runtime?: unknown;
      bridge?: unknown;
      /** Kausaatiotracer (WP-DT2) — F12: `window.fasthub._trace.export()`. */
      _trace?: Tracer;
    };
  }
}

/**
 * `persist: "local"` -muuttujien localStorage-avaimen etuliite. Avain on
 * `fh-app-var:<appId>:<nimi>` — **App-kohtainen**, koska kaikki julkaistut Appit
 * tarjoillaan samalta originilta: pelkkään nimeen perustuva avain sai kaksi eri
 * Appia joissa oli samanniminen muuttuja (`laskuri`, `selectedId`, `theme`)
 * lukemaan ja ylikirjoittamaan toistensa arvon.
 *
 * Tuntematon appId (palvelin ei siementänyt) skouppaa avaimen tyhjään
 * merkkijonoon. Se on sama jaettu avaruus kuin ennen, mutta ei enää törmää
 * skoopattuihin avaimiin — eikä sitä pitäisi enää syntyä, koska kaikki kolme
 * renderöintipolkua (standalone-serving, portaalin runner, editorin esikatselu)
 * siementävät id:n.
 */
const VAR_STORAGE_PREFIX = 'fh-app-var:';
/** Näkyvän toast-pinon katto — vanhimmat karsitaan kun tämä ylittyy. */
const TOAST_STACK_MAX = 4;

// ---------- Näkyvyys ----------

/**
 * Näytettävän elementin display-arvo. Renderöijä kirjoittaa displayn
 * inline-tyyliin, joten alun perin piilotetulle ('none') arvo päätellään
 * komponenttiluokasta; muuten muistetaan boot-hetken arvo datasetiin.
 */
function shownDisplayFor(el: HTMLElement): string {
  if (el.dataset.fhDisplay !== undefined) return el.dataset.fhDisplay;
  let display = el.style.display;
  if (!display || display === 'none') {
    if (el.classList.contains('sp-container')) {
      display = el.style.gridTemplateColumns ? 'grid' : 'flex';
    } else if (el.classList.contains('sp-dynamic-table')) {
      display = 'block';
    } else if (el.classList.contains('sp-alert') || el.classList.contains('sp-paragraph')
      || el.classList.contains('sp-heading')) {
      display = ''; // CSS-luokan oletus
    } else if (el.classList.contains('sp-badge') || el.classList.contains('sp-avatar')) {
      display = 'inline-flex';
    } else {
      display = 'flex'; // card/form/tabs/modal
    }
  }
  el.dataset.fhDisplay = display;
  return display;
}

function setVisible(el: HTMLElement, visible: boolean): void {
  el.style.display = visible ? shownDisplayFor(el) : (shownDisplayFor(el), 'none');
}

/**
 * Number-inputin arvon parsinta (WP-F1): tyhjä → null, roska → null, muutoin
 * luku. `Number('')` on 0, joten tyhjä kenttä on pakko käsitellä ennen
 * konversiota — muuten tyhjä pakollinen numerokenttä läpäisisi validoinnin
 * nollana. Jaettu slot-kytkennän ja formin recomputen kesken, jotta molemmat
 * eivät voi ajautua eri tulkintoihin samasta merkkijonosta.
 */
function parseNumberValue(raw: string): number | null {
  const trimmed = raw.trim();
  if (trimmed === '') return null;
  const parsed = Number(trimmed);
  return Number.isFinite(parsed) ? parsed : null;
}

// ---------- DOM-kirjoittajat slotin semantiikalla ----------

/** Kirjoita ensimmäinen tekstisolmu (säilyttää elementin sisäiset scriptit). */
function writeFirstTextNode(el: HTMLElement, text: string): void {
  for (const node of Array.from(el.childNodes)) {
    if (node.nodeType === Node.TEXT_NODE) {
      node.nodeValue = text;
      return;
    }
  }
  el.insertBefore(document.createTextNode(text), el.firstChild);
}

function writeChildText(host: HTMLElement, selector: string, text: string): void {
  const target = host.querySelector(selector);
  if (target) target.textContent = text;
}

/**
 * Rakenna valintakomponentin vaihtoehdot uudelleen sidotusta arvosta
 * (WP-ABP1). Palauttaa uuden valinnan, jonka kutsuja julkaisee
 * `value`-slottiin — tai `null` kun arvo oli lukukelvoton eikä DOMiin
 * koskettu lainkaan.
 *
 * <p>DOM rakennetaan `createElement`illä ja `textContent`illa, ei
 * merkkijonokonkatenaatiolla: datalähteestä tuleva label on epäluotettavaa
 * syötettä, eikä sitä saa päästää innerHTML-polulle.
 */
function rebuildSelectOptions(host: HTMLElement, value: unknown): string | null {
  const sel = host.querySelector<HTMLSelectElement>('select.sp-select');
  if (!sel) return null;
  const options = parseOptionList(value);
  if (!options) return null;
  const doc = sel.ownerDocument;
  const current = sel.value;
  // Placeholder ("ei valintaa") on osa komponentin sopimusta, ei dataa —
  // se säilyy uudelleenrakennuksen yli jotta required erottaa yhä
  // "ei valittu" -tilan oikeasta valinnasta.
  const placeholder = sel.querySelector('option[disabled][value=""]');
  sel.textContent = '';
  if (placeholder) sel.appendChild(placeholder);
  for (const o of options) {
    const el = doc.createElement('option');
    el.value = o.value;
    el.textContent = o.label;
    sel.appendChild(el);
  }
  // Tyhjä valinta asetetaan eksplisiittisesti: ilman tätä selain valitsisi
  // uuden listan ensimmäisen vaihtoehdon käyttäjän puolesta, eli lomake
  // lähettäisi arvon jota kukaan ei valinnut.
  sel.value = keepSelection(options, current);
  return sel.value;
}

/**
 * Suodata pudotusvalikon vaihtoehdot kirjoitetulla tekstillä (WP-KL8, löydös
 * `E-6`). Natiivi `<select>` jää sellaisenaan — sen näppäimistö-, ruudunlukija-
 * ja lomakekäytös on juuri se mitä käsin kirjoitetussa comboboxissa
 * menetettäisiin — ja piilotus tehdään `hidden`-attribuutilla.
 *
 * <p>Kaksi sääntöä: valittu vaihtoehto ei koskaan piiloudu (muuten valikko
 * näyttäisi tyhjältä omasta valinnastaan), eikä placeholder piiloudu (se on
 * komponentin sopimus, ei dataa).
 */
function applySelectFilter(sel: HTMLSelectElement, query: string): number {
  const q = query.trim().toLowerCase();
  let shown = 0;
  for (const opt of Array.from(sel.options)) {
    const keep = q === ''
      || opt.value === sel.value
      || opt.disabled
      || (opt.textContent || '').toLowerCase().includes(q);
    opt.hidden = !keep;
    if (keep && !opt.disabled) shown += 1;
  }
  return shown;
}

/** Radio-ryhmän vastine {@link rebuildSelectOptions}ille (WP-ABP1). */
function rebuildRadioOptions(host: HTMLElement, value: unknown): string | null {
  const box = host.querySelector<HTMLElement>('.sp-radio-options');
  if (!box) return null;
  const options = parseOptionList(value);
  if (!options) return null;
  const doc = box.ownerDocument;
  let current = '';
  box.querySelectorAll<HTMLInputElement>('input.sp-radio-input').forEach((r) => {
    if (r.checked) current = r.value;
  });
  const next = keepSelection(options, current);
  // Kenttäattribuutit luetaan säiliöstä eikä ensimmäisestä radiosta: sidottu
  // ryhmä voi renderöityä tyhjänä, jolloin lähdettä ei olisi (RadioGroupRenderer).
  const fieldName = box.getAttribute('data-app-radio-name') || '';
  const required = box.getAttribute('data-app-radio-required') || '0';
  const bindTo = box.getAttribute('data-app-radio-bind-to') || '';
  // WP-HV1: ryhmätunnus seuraa kentän nimeä kuten palvelimellakin
  // (RadioGroupRenderer/MultiSelectRenderer). Jos nämä eroaisivat, sidotusta
  // lähteestä rakennettu ryhmä käyttäytyisi eri tavalla kuin palvelimelta
  // tullut — ja ero näkyisi vasta kun optiot vaihtuvat ajossa.
  const groupName = fieldName || host.getAttribute('data-app-id') || '';
  box.textContent = '';
  for (const o of options) {
    const label = doc.createElement('label');
    label.className = 'sp-radio-option';
    const input = doc.createElement('input');
    input.type = 'radio';
    input.className = 'sp-radio-input';
    input.name = groupName;
    input.value = o.value;
    input.setAttribute('data-app-input-name', fieldName);
    input.setAttribute('data-app-input-required', required);
    input.setAttribute('data-app-input-bind-to', bindTo);
    if (o.value === next && next !== '') input.checked = true;
    const text = doc.createElement('span');
    text.className = 'sp-radio-label';
    text.textContent = o.label;
    label.appendChild(input);
    label.appendChild(text);
    box.appendChild(label);
  }
  return next;
}

/**
 * Monivalinnan vaihtoehdot uudelleen (WP-ABP9). Palauttaa säilyneen
 * valintajoukon — säilyy vain se osajoukko joka on yhä listalla, samasta
 * syystä kuin yksivalinnassa: listalta pudonnut arvo lähtisi muuten
 * payloadiin ilman että käyttäjä näkee sitä.
 */
function rebuildMultiOptions(host: HTMLElement, value: unknown): string[] | null {
  const box = host.querySelector<HTMLElement>('.sp-multi-options');
  if (!box) return null;
  const options = parseOptionList(value);
  if (!options) return null;
  const doc = box.ownerDocument;
  const current = new Set<string>();
  box.querySelectorAll<HTMLInputElement>('input.sp-multi-input').forEach((cb) => {
    if (cb.checked) current.add(cb.value);
  });
  const fieldName = box.getAttribute('data-app-multi-name') || '';
  const required = box.getAttribute('data-app-multi-required') || '0';
  const bindTo = box.getAttribute('data-app-multi-bind-to') || '';
  // WP-HV1: ryhmätunnus seuraa kentän nimeä kuten palvelimellakin
  // (RadioGroupRenderer/MultiSelectRenderer). Jos nämä eroaisivat, sidotusta
  // lähteestä rakennettu ryhmä käyttäytyisi eri tavalla kuin palvelimelta
  // tullut — ja ero näkyisi vasta kun optiot vaihtuvat ajossa.
  const groupName = fieldName || host.getAttribute('data-app-id') || '';
  box.textContent = '';
  const kept: string[] = [];
  for (const o of options) {
    const label = doc.createElement('label');
    label.className = 'sp-multi-option';
    const input = doc.createElement('input');
    input.type = 'checkbox';
    input.className = 'sp-multi-input';
    input.name = groupName;
    input.value = o.value;
    input.setAttribute('data-app-input-name', fieldName);
    input.setAttribute('data-app-input-required', required);
    input.setAttribute('data-app-input-bind-to', bindTo);
    if (current.has(o.value)) {
      input.checked = true;
      kept.push(o.value);
    }
    const text = doc.createElement('span');
    text.className = 'sp-multi-label';
    text.textContent = o.label;
    label.appendChild(input);
    label.appendChild(text);
    box.appendChild(label);
  }
  return kept;
}

/**
 * Esitäytä input sidotusta arvosta (WP-ABP2, päätös `D2` = A). Tämä on se
 * palikka jolla olemassa olevan tietueen muokkaus onnistuu: gridissä valittu
 * rivi virtaa lomakkeen kenttiin.
 *
 * <p>Kirjoituksen jälkeen **dispatchataan sama event jota komponentin oma
 * kytkentä jo kuuntelee** (`input` tekstikentille, `change`
 * valintakontrolleille) sen sijaan että slot julkaistaisiin tässä. Syy: vain
 * kytkentä tietää slotin oikean tyypin — number-input julkaisee luvun,
 * checkbox boolean, muut merkkijonon — ja samalla ympäröivän Formin
 * `recompute()` herää. Ilman eventtiä lomake näyttäisi täytetyltä mutta
 * lähettäisi tyhjän.
 *
 * <p>Ei-muutos ei dispatchaa mitään. Se on samalla **silmukkavahti**: jos
 * lauseke vahingossa riippuu kentän omasta arvosta, toinen kirjoitus on
 * no-op eikä ketju jatku.
 *
 * <p>Päätös `D3` = A: ylikirjoitetaan aina, myös käyttäjän kirjoittaman
 * tekstin päälle. Lauseke muuttuu käytännössä vain kun valinta vaihtuu, ja
 * vanhan syötteen jääminen uuden tietueen päälle on vaarallisempi yllätys
 * kuin tekstin katoaminen.
 */
function prefillInput(host: HTMLElement, value: unknown): void {
  const text = value === null || value === undefined ? '' : String(value);

  // Monivalinta (WP-ABP9): arvo on joukko, ei skalaari. Hyväksytään sekä
  // taulukko (lauseke) että JSON-taulukko merkkijonona (katalogin textarea).
  if (host.classList.contains('sp-multi')) {
    const wanted = new Set(
      (Array.isArray(value) ? value : safeJsonArray(value)).map((v) => String(v)),
    );
    const boxes = host.querySelectorAll<HTMLInputElement>('input.sp-multi-input');
    let changed = false;
    boxes.forEach((cb) => {
      const next = wanted.has(cb.value);
      if (cb.checked !== next) {
        cb.checked = next;
        changed = true;
      }
    });
    if (changed) {
      (boxes[0] ?? host).dispatchEvent(new Event('change', { bubbles: true }));
    }
    return;
  }

  // Radio-ryhmä: ei yhtä elementtiä vaan joukko, joten valinta asetetaan
  // rastimalla osuva radio (ja purkamalla muut).
  if (host.classList.contains('sp-radio-group')) {
    const radios = host.querySelectorAll<HTMLInputElement>('input.sp-radio-input');
    let current = '';
    radios.forEach((r) => {
      if (r.checked) current = r.value;
    });
    if (current === text) return;
    let hit: HTMLInputElement | null = null;
    radios.forEach((r) => {
      r.checked = r.value === text;
      if (r.checked) hit = r;
    });
    (hit ?? radios[0])?.dispatchEvent(new Event('change', { bubbles: true }));
    return;
  }

  const select = host.querySelector<HTMLSelectElement>('select.sp-select');
  if (select) {
    if (select.value === text) return;
    select.value = text;
    select.dispatchEvent(new Event('change', { bubbles: true }));
    return;
  }

  const textarea = host.querySelector<HTMLTextAreaElement>('textarea.sp-textarea');
  if (textarea) {
    if (textarea.value === text) return;
    textarea.value = text;
    textarea.dispatchEvent(new Event('input', { bubbles: true }));
    return;
  }

  const input = host.querySelector<HTMLInputElement>('input.sp-input');
  if (input) {
    if (input.value === text) return;
    input.value = text;
    input.dispatchEvent(new Event('input', { bubbles: true }));
  }
}

/** Lue JSON-taulukko merkkijonosta; muu syöte → tyhjä (WP-ABP9). */
function safeJsonArray(value: unknown): unknown[] {
  if (typeof value !== 'string' || !value.trim()) return [];
  try {
    const parsed = JSON.parse(value);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

/** Checkboxin esitäyttö (WP-ABP2); arvo on boolean, ei merkkijono. */
function prefillCheckbox(host: HTMLElement, value: unknown): void {
  const cb = host.querySelector<HTMLInputElement>('input.sp-checkbox-input');
  if (!cb) return;
  const next = !!value;
  if (cb.checked === next) return;
  cb.checked = next;
  cb.dispatchEvent(new Event('change', { bubbles: true }));
}

/**
 * Sovella bindingin/interaktion tulos DOMiin slotin mukaan.
 *
 * <p>`publish` kirjoittaa isännän toiseen slottiin — tarpeen kun DOM-muutos
 * muuttaa johdettua tilaa (WP-ABP1: vaihtoehtojen vaihtuminen voi tyhjentää
 * valinnan, jolloin `value` on julkaistava uudelleen).
 */
/**
 * Sidottu JSON-runko slotista, attribuutti alkuarvona (WP-AR4).
 *
 * <p>Korvaa poistetun `${form.x}` / `${params.x}` -merkkijonotemplatoinnin.
 * Sitä ei koskaan toteutettu payloadille eikä lomakkeelle — katalogi lupasi,
 * palvelin renderöi, eikä runtime lukenut (löydökset F-A ja F-E) — ja
 * rinnakkainen templatointikieli oli juuri se syy miksi toinen mekanismi ehti
 * jäädä kytkemättä. Nyt runko kirjoitetaan samalla lausekekielellä kuin kaikki
 * muukin: `{"$bind": true, "expr": "{id: rows.selectedRowId}"}`.
 *
 * <p>Slotin arvo voi olla valmis objekti (lauseke tuotti sen) tai merkkijono
 * (literaali JSON). Ei-JSON merkkijono lähetetään raakana, kuten ennenkin.
 */
/** Kutsun vastaus slotteina / korttitilana (WP-VV2, WP-VV3). */
interface ResponseOutcome {
  ok: boolean;
  /** HTTP-status; `0` kun kutsu ei saanut vastausta lainkaan. */
  status: number;
  /** Parsittu runko (JSON tai raaka merkkijono); `null` ilman vastausta. */
  response: unknown;
  /** Käyttäjälle näytettävä virheteksti; `''` onnistumisesta. */
  errorMessage: string;
}

/**
 * Tulkitse sillan vastaus (tai sen puuttuminen) käyttäjän näkökulmasta.
 * D1 = B: 4xx on sopimuksen mukaan käyttäjälle kirjoitettu viesti ja kulkee
 * sellaisenaan (`''` jos rungossa ei ole tunnistettavaa viestiä); 5xx saa
 * katalogitekstin, koska sen runko on juuri se paikka josta vuotaa sisäisiä
 * nimiä ja pinoja. Raaka runko on silti luettavissa `response`sta.
 *
 * <p>Hylätty kutsu (`res === null`) saa AINA katalogitekstin `requestFailed`.
 * Hylkäyksen oma viesti ei kelpaa käyttäjälle: portaalin runnerissa se on
 * `ApiError`in `403 Forbidden: {…raaka runko…}`, siis juuri sitä mitä D1
 * kieltää. Syy ei katoa — `traceBridge` kirjaa sen Debug-aikajanaan.
 */
function describeResponse(res: InvokeResult | null): ResponseOutcome {
  if (!res) {
    return { ok: false, status: 0, response: null, errorMessage: resolveMessage('requestFailed') };
  }
  const status = res.statusCode;
  const ok = status >= 200 && status < 300;
  let errorMessage = '';
  if (status >= 400 && status < 500) errorMessage = extractErrorMessage(res.responseBody);
  else if (!ok) errorMessage = resolveMessage('serverError');
  return { ok, status, response: parseResponseBody(res.responseBody), errorMessage };
}

/**
 * Vastaus korttien evaluointitilaksi (WP-VV3) — rivitoiminnolla ja kanbanin
 * siirrolla ei ole omaa id:tä, joten arvot kulkevat korteille samoin kuin
 * `row`: `status`, `response`, `errorMessage`. Ei "last"-etuliitettä, koska
 * kortti ajetaan juuri tämän kutsun päätteeksi.
 */
function responseState(outcome: ResponseOutcome): ExprState {
  return { status: outcome.status, response: outcome.response, errorMessage: outcome.errorMessage };
}

/** Käyttäjälle näytettävän tekstin katto (D3 = A): `…` mukaan lukien. */
function truncateMessage(text: string): string {
  return text.length > ERROR_MESSAGE_MAX
    ? `${text.slice(0, ERROR_MESSAGE_MAX - 1).trimEnd()}…` : text;
}

/** Julkaise vastaus komponentin `lastStatus`/`lastResponse`/`lastErrorMessage`-slotteihin. */
function publishResponseSlots(store: Store, id: string | null, outcome: ResponseOutcome): void {
  if (!id) return;
  store.setSlot(id, 'lastStatus', outcome.status);
  store.setSlot(id, 'lastResponse', outcome.response);
  store.setSlot(id, 'lastErrorMessage', outcome.errorMessage);
}

/** Vastausslottien alkutila: ei vielä kutsua. */
function initResponseSlots(store: Store, id: string | null): void {
  if (!id) return;
  store.setSlot(id, 'lastStatus', 0);
  store.setSlot(id, 'lastResponse', null);
  store.setSlot(id, 'lastErrorMessage', '');
}

function resolveJsonPayload(
  hostId: string | null,
  slot: string,
  store: Store,
  attributeFallback: string,
): unknown {
  const bound = hostId ? store.getSlot(hostId, slot) : undefined;
  const raw = bound === undefined || bound === null || bound === '' ? attributeFallback : bound;
  if (raw === undefined || raw === null || raw === '') return undefined;
  if (typeof raw !== 'string') return raw;
  if (!raw.trim()) return undefined;
  try {
    return JSON.parse(raw);
  } catch {
    return raw;
  }
}

/** Napin trigger-api-runko. Oma funktio, jotta kattavuusportti näkee kytkennän. */
function buttonPayload(button: HTMLElement, store: Store): unknown {
  return resolveJsonPayload(
    button.getAttribute('data-app-id'), 'payload', store,
    button.getAttribute('data-app-payload') || '',
  );
}

/** Lomakkeen kenttien päälle mergattavat lisäkentät. */
function formExtraFields(form: HTMLElement, id: string, store: Store): unknown {
  return resolveJsonPayload(
    id, 'submitExtraFields', store,
    form.getAttribute('data-app-form-extra-fields') || '',
  );
}

function applySlotToDom(
  host: HTMLElement,
  slot: string,
  value: unknown,
  publish?: (slot: string, value: unknown) => void,
): void {
  switch (slot) {
    case 'options': {
      if (host.classList.contains('sp-multi')) {
        const values = rebuildMultiOptions(host, value);
        if (values !== null) {
          publish?.('values', values);
          publish?.('count', values.length);
        }
        return;
      }
      const next = host.classList.contains('sp-radio-group')
        ? rebuildRadioOptions(host, value)
        : rebuildSelectOptions(host, value);
      // null = lukukelvoton arvo → edelliset vaihtoehdot ja valinta jäivät
      // voimaan, eikä value-slottia siis saa koskea.
      if (next !== null) publish?.('value', next);
      return;
    }
    case 'defaultValue':
      prefillInput(host, value);
      return;
    case 'defaultChecked':
      prefillCheckbox(host, value);
      return;
    case 'isVisible':
      setVisible(host, !!value);
      return;
    case 'isOpen':
      setVisible(host, !!value);
      return;
    case 'text': {
      // Markdown-kappale ei re-renderöidy reaktiivisesti (sisältö on
      // palvelimen sanitoimaa HTML:ää — tekstikirjoitus pyyhkisi sen).
      if (host.classList.contains('sp-paragraph--markdown')) return;
      const text = String(value ?? '');
      if (host.classList.contains('sp-paragraph') || host.classList.contains('sp-heading')
        || host.classList.contains('sp-link')) {
        writeFirstTextNode(host, text);
      }
      return;
    }
    case 'href': {
      // Sidottu href kirjoitetaan DOMiin VASTA selaimessa, joten palvelimen
      // sanitointi ei ole koskenut tähän arvoon (WP-ABP8). Ilman tätä
      // lauseke voisi tuottaa javascript:-URLin suoraan ankkuriin.
      if (!host.classList.contains('sp-link')) return;
      const safe = sanitizeUrl(value, 'link');
      host.setAttribute('href', safe || '#');
      return;
    }
    case 'src': {
      if (!host.classList.contains('sp-image')) return;
      const img = host.querySelector<HTMLImageElement>('img.sp-image-img');
      const fallback = host.querySelector<HTMLElement>('[data-app-image-fallback]');
      if (!img) return;
      const safe = sanitizeUrl(value, 'image');
      if (safe) {
        img.setAttribute('src', safe);
        img.style.display = '';
        if (fallback) fallback.style.display = 'none';
      } else {
        img.removeAttribute('src');
        img.style.display = 'none';
        if (fallback) fallback.style.display = '';
      }
      return;
    }
    case 'alt': {
      if (!host.classList.contains('sp-image')) return;
      const img = host.querySelector<HTMLImageElement>('img.sp-image-img');
      if (img) img.alt = String(value ?? '');
      return;
    }
    case 'label':
      if (host.classList.contains('sp-button')) writeFirstTextNode(host, String(value ?? ''));
      else if (host.classList.contains('sp-search')) writeChildText(host, '.sp-search-label', String(value ?? ''));
      else if (host.classList.contains('sp-badge')) writeChildText(host, '.sp-badge-label', String(value ?? ''));
      else if (host.classList.contains('sp-stat')) writeChildText(host, '.sp-stat-label', String(value ?? ''));
      // WP-AR6: progress puuttui listalta, joten sidottu label ei koskaan
      // päivittynyt — palkin ARVO liikkui mutta teksti jäi palvelimen
      // renderöimään alkuarvoon (löydös F-I, todennettu selaimessa).
      else if (host.classList.contains('sp-progress')) writeChildText(host, '.sp-progress-label', String(value ?? ''));
      return;
    case 'variant':
      // Bindattava badge-sävy: vaihda modifier-luokka (whitelist).
      if (host.classList.contains('sp-badge')) {
        const v = ['neutral', 'info', 'success', 'warning', 'danger'].includes(String(value))
          ? String(value) : 'neutral';
        host.className = 'sp-badge sp-badge--' + v;
      }
      return;
    case 'placeholder': {
      // Search-inputin bindattu placeholder → input-attribuutti.
      const input = host.querySelector<HTMLInputElement>('.sp-search-input');
      if (input) input.placeholder = String(value ?? '');
      return;
    }
    case 'hint':
      if (host.classList.contains('sp-search')) writeChildText(host, '.sp-search-hint', String(value ?? ''));
      return;
    case 'name': {
      // Avatarin nimi → initiaalit + väri + alt (kaikki johdettuja).
      if (!host.classList.contains('sp-avatar')) return;
      const nm = String(value ?? '');
      const initEl = host.querySelector<HTMLElement>('.sp-avatar-initials');
      if (initEl) {
        initEl.textContent = initialsOf(nm);
        initEl.style.background = colorFromName(nm);
      }
      const img = host.querySelector<HTMLImageElement>('img.sp-avatar-img');
      if (img) img.alt = nm;
      return;
    }
    case 'imageUrl': {
      if (!host.classList.contains('sp-avatar')) return;
      const img = host.querySelector<HTMLImageElement>('img.sp-avatar-img');
      const initEl = host.querySelector<HTMLElement>('.sp-avatar-initials');
      const url = String(value ?? '');
      if (!img) return;
      if (url) {
        img.src = url;
        img.style.display = '';
        if (initEl) initEl.style.display = 'none';
      } else {
        img.removeAttribute('src');
        img.style.display = 'none';
        if (initEl) initEl.style.display = '';
      }
      return;
    }
    case 'statusDot': {
      if (!host.classList.contains('sp-avatar')) return;
      const dot = host.querySelector<HTMLElement>('.sp-avatar-dot');
      if (!dot) return;
      const allowed = ['success', 'warning', 'danger', 'info', 'neutral'];
      const s = allowed.includes(String(value)) ? String(value) : 'none';
      dot.className = 'sp-avatar-dot sp-avatar-dot--' + s;
      dot.style.display = s === 'none' ? 'none' : '';
      return;
    }
    case 'title': {
      const text = String(value ?? '');
      if (host.classList.contains('sp-card')) writeChildText(host, '.sp-card-title', text);
      else if (host.classList.contains('sp-alert')) writeChildText(host, '.sp-alert-title', text);
      else if (host.classList.contains('sp-form')) writeChildText(host, '.sp-form-title', text);
      else if (host.classList.contains('sp-chart')) writeChildText(host, '.sp-chart-title', text);
      return;
    }
    case 'subtitle':
      if (host.classList.contains('sp-card')) writeChildText(host, '.sp-card-subtitle', String(value ?? ''));
      return;
    case 'description':
      if (host.classList.contains('sp-form')) writeChildText(host, '.sp-form-desc', String(value ?? ''));
      return;
    case 'message': {
      const target = host.querySelector('.sp-alert-message');
      if (target && !target.classList.contains('sp-alert-message--markdown')) {
        target.textContent = String(value ?? '');
      }
      return;
    }
    default:
      // Tuntematon slot: arvo elää tilassa (setSlot hoiti), ei DOM-kirjoitusta.
  }
}

/**
 * Repeaterin rivimuutos joka EI ole käyttäjän muokkaus (ensimmäinen
 * renderöinti, sidotun datan saapuminen). Lomake laskee arvonsa uudelleen
 * mutta ei merkitse itseään likaiseksi.
 */
const REPEATER_ROWS_EVENT = 'fh:repeater-rows';

/**
 * Rivitoiminnon lifecycle-ajuri. `boot` omistaa toteutuksen (se näkee
 * reloaders-rekisterin, muuttujat ja sillan); `wireTableFetch` saa sen
 * parametrina, koska se elää moduulitasolla sulkeuman ulkopuolella.
 *
 * <p>`extra` (WP-KH3) on lausekkeisiin yhdistettävä lisätila — rivitoiminto
 * antaa siinä klikatun rivin nimellä `row`. Ilman sitä kortit näkevät vain
 * globaalin storen, kuten napin kortit.
 */
type RowLifecycleRunner = (
  actions: unknown[],
  host: HTMLElement | null,
  timing: string,
  extra?: ExprState,
) => void;

// ---------- Runtime ----------

export interface AppRuntime {
  version: string;
  store: Store;
  /** Kausaatiotracer (WP-DT2); sama olio kuin `window.fasthub._trace`. */
  trace: Tracer;
  evalExpression(src: string, extraState?: ExprState): unknown;
}

/**
 * Vastausrungosta talletettava enimmäispituus (WP-DT2). Koko runko menisi
 * full-moodissa rengaspuskuriin sellaisenaan, ja 500 eventtiä × iso JSON on
 * megatavuja; paneeli näyttää joka tapauksessa vain alun.
 */
const API_BODY_TRACE_MAX = 4000;

/**
 * Kääri silta tracen alle (WP-DT2). Jokainen datalähdekutsu kirjaa
 * `api-start`in, vastaus `api-end`in, ja **kutsujan `.then`-käsittelijät
 * ajetaan `api-end`in kausaatiokontekstissa** — näin vastauksen käynnistämä
 * slot-kaskadi asettuu puussa kutsun alle eikä ala uutena juurena.
 *
 * <p>Toteutus paikkaa palautetun Promisen oman `then`-metodin sen sijaan että
 * palauttaisi tekothenablen: olio on aito Promise, joten `catch` ja `finally`
 * (jotka delegoivat `then`iin) periytyvät oikein eikä `Promise.all` riko
 * mitään. Kutsupaikkoja ei tarvitse muuttaa lainkaan.
 *
 * <p><b>GOTCHA:</b> `await` EI kulje paikatun `then`in kautta — spec ottaa
 * natiiville Promiselle nopean polun. Awaitin jälkeiset eventit kirjautuvat
 * siis juuriksi (eivät katoa). Kaikki nykyiset kutsupaikat käyttävät
 * `.then(onOk, onFail)`-muotoa; jos uusi kutsupaikka awaittaa, se menettää
 * vain kausaatiovanhemman.
 */
function traceBridge(inner: BridgeClient, tracer: Tracer): BridgeClient {
  return {
    invoke(sourceId, payload, query) {
      const start = tracer.record('api-start', '', sourceId, { payload });
      // Kutsujan käsittelijän vanhempi: api-end (tai api-start jos tracer on
      // pois päältä). Asetetaan omassa käsittelijässämme, joka ajetaan ketjussa
      // ennen kutsujan omaa.
      let handlerParent: number | null = start?.id ?? null;
      const recorded = inner.invoke(sourceId, payload, query).then(
        (res: InvokeResult) => {
          const end = tracer.withParent(start?.id ?? null, () =>
            tracer.record('api-end', '', sourceId, {
              statusCode: res.statusCode,
              duration: res.durationMs,
              payload: typeof res.responseBody === 'string'
                ? res.responseBody.slice(0, API_BODY_TRACE_MAX)
                : res.responseBody,
            }));
          handlerParent = end?.id ?? handlerParent;
          return res;
        },
        (err: unknown) => {
          const failed = tracer.withParent(start?.id ?? null, () =>
            tracer.record('error', '', sourceId, {
              message: err instanceof Error ? err.message : String(err),
            }));
          handlerParent = failed?.id ?? handlerParent;
          throw err;
        },
      );
      const nativeThen = recorded.then.bind(recorded);
      recorded.then = ((onOk?: unknown, onFail?: unknown) =>
        nativeThen(
          typeof onOk === 'function'
            ? (v: InvokeResult) => tracer.withParent(handlerParent, () => (onOk as (r: InvokeResult) => unknown)(v))
            : undefined,
          typeof onFail === 'function'
            ? (e: unknown) => tracer.withParent(handlerParent, () => (onFail as (e: unknown) => unknown)(e))
            : undefined,
        )) as typeof recorded.then;
      return recorded;
    },
    openLink: (url, target) => inner.openLink(url, target),
    openDocument: (sourceId, payload) => inner.openDocument(sourceId, payload),
    downloadDocument: (sourceId, payload) => inner.downloadDocument(sourceId, payload),
    saveText: (filename, content, format) => inner.saveText(filename, content, format),
    notifyEmbedder: (message) => inner.notifyEmbedder(message),
    // Käyttäjätoimet (WP-UA4) delegoidaan sellaisenaan. HUOM: tämä kääre on
    // kokonaan uusi olio, ei proxy — sillan uusi metodi joka unohtuu tästä
    // KATOAA ajossa täysin hiljaa (komponentti näkisi "not a function"), joten
    // jokainen BridgeClientin lisäys on lisättävä myös tähän.
    listUserActions: (queue, includeResolved) => inner.listUserActions(queue, includeResolved),
    respondUserAction: (actionId, answer) => inner.respondUserAction(actionId, answer),
  };
}

export function boot(root: Document | HTMLElement = document): AppRuntime {
  const store = createStore();
  const doc = root instanceof Document ? root : root.ownerDocument;

  // --- Kausaatiotracer (WP-DT2) ---
  // Metadata-moodi on päällä myös julkaistussa Apissa (päätös D1/D2): eventit
  // ovat id:itä ja tyyppejä, eivät katsojan dataa. Full-moodi (payloadit)
  // kytkeytyy vain editorin ohjausviestillä — ks. WP-DT3.
  const tracer = createTracer({ appId: window.fasthub?.appId });
  window.fasthub = window.fasthub || {};
  window.fasthub._trace = tracer;
  // Striimi editorille (WP-DT3): vaikenee kunnes parent pyytää nauhoitusta.
  attachTraceStream(tracer);

  // setSlot tracen alle: KAIKKI slot-emitit kulkevat tästä, ja koska
  // muutoksesta ilmoitetaan tilaajille (= bindingien re-evaluointi) vasta
  // eventin kirjaamisen jälkeen, kaskadi asettuu puussa emitin alle.
  //
  // Storen oma "sama arvo → ei mitään" -ehto toistetaan tässä, jotta
  // muuttumaton kirjoitus ei tuota eventtiä. Ilman sitä puu täyttyisi
  // riveillä joissa ei tapahtunut mitään.
  const writeSlot = store.setSlot.bind(store);
  store.setSlot = (id: string, slot: string, value: unknown): void => {
    const previous = store.getSlot(id, slot);
    if (previous === value) return;
    const event = tracer.record('slot-emit', id, `${id}.${slot}`, {
      slot,
      payload: value,
      prevValue: previous,
    });
    if (!event) {
      writeSlot(id, slot, value);
      return;
    }
    tracer.withParent(event.id, () => writeSlot(id, slot, value));
  };

  /**
   * Avaa ketju: seuraavat eventit saavat vanhemmakseen `event`in, kunnes
   * ketju suljetaan.
   *
   * <p><b>GOTCHA (löytyi selainverifioinnissa, WP-DT6).</b> Sulkeminen EI voi
   * tapahtua mikrotaskissa. Selain tekee mikrotask-tarkistuspisteen aina kun
   * JS-pino tyhjenee — siis **jokaisen event-kuuntelijan välissä** —, joten
   * capture-vaiheessa jonoon pantu nollaus ehtii ajoon ennen kuin napin oma
   * kupliva käsittelijä alkaa. Ketju katkesi juuri siitä kohdasta jonka takia
   * se avattiin: API-kutsu kirjautui juureksi eikä klikin lapseksi.
   * jsdom ei paljasta tätä, koska siellä koko dispatch ajetaan yhden
   * `dispatchEvent`-kutsun sisällä eikä pino tyhjene välissä.
   *
   * <p>Sulkeminen tehdään siksi kuplimisvaiheen kuuntelijalla (ajetaan kaikkien
   * elementtikäsittelijöiden jälkeen) ja varmistimena makrotaskissa siltä
   * varalta ettei kuplimista tapahdu (`stopPropagation`). Varmistin nollaa vain
   * oman ketjunsa, jottei se katkaise jo alkanutta uutta.
   */
  let openChainId: number | null = null;
  const closeChain = (id: number): void => {
    if (openChainId !== id) return;
    openChainId = null;
    tracer.setCurrentParent(null);
  };
  const openChain = (event: { id: number } | null): void => {
    if (!event) return;
    openChainId = event.id;
    tracer.setCurrentParent(event.id);
    setTimeout(() => closeChain(event.id), 0);
  };

  // Käyttäjän klikkaus on ketjun juuri (WP-DT2). Yksi capture-vaiheen
  // kuuntelija riittää kaikille komponenteille: napit, taulukon rivitoiminnot,
  // välilehdet, kanban-kortit — kaikki ovat jonkin data-app-id-elementin
  // sisällä, eikä yhtäkään klikkikäsittelijää tarvitse instrumentoida
  // erikseen. Capture ajetaan ennen kuplivia käsittelijöitä, joten ne näkevät
  // valmiin kontekstin.
  doc?.addEventListener('click', (e) => {
    const target = (e.target as Element | null)?.closest?.('[data-app-id]') as HTMLElement | null;
    const clickedId = target?.getAttribute('data-app-id');
    if (!clickedId) return;
    openChain(tracer.record('user-click', clickedId, '', { isRoot: true }));
  }, true);
  // Kuplimisvaihe dokumentin tasolla = viimeinen käsittelijä. Ketju suljetaan
  // tässä, joten se kattaa täsmälleen dispatchin synkronisen osan.
  doc?.addEventListener('click', () => {
    if (openChainId != null) closeChain(openChainId);
  }, false);

  // Sivun lataus on oma ketjunsa: siementäminen, sidontojen ensievaluointi ja
  // komponenttien kytkentä asettuvat mount-juuren alle. Ilman tätä jokainen
  // boot-aikainen emitti olisi oma irrallinen juurensa. Konteksti suljetaan
  // vasta bootin lopussa (ei mikrotaskissa) — boot on synkroninen.
  const mountEvent = tracer.record('mount', 'page', '', { isRoot: true });
  if (mountEvent) tracer.setCurrentParent(mountEvent.id);

  // Datalähteiden silta (AB-WP4.1): null jos kehystämätön → verkkopolut
  // no-oppaavat. Parent (serving-host / editorin esikatselu) pitää tokenin.
  const rawBridge: BridgeClient | null = createBridgeClient();
  const bridge: BridgeClient | null = rawBridge ? traceBridge(rawBridge, tracer) : null;

  // id → uudelleenhaku, ks. WP-ABP3 (refresh-lifecycle-operaatio).
  const reloaders = new Map<string, () => void>();

  const hostOf = (el: Element): { host: HTMLElement; id: string } | null => {
    const host = el.closest('[data-app-id]') as HTMLElement | null;
    if (!host) return null;
    const id = host.getAttribute('data-app-id') || '';
    return id ? { host, id } : null;
  };
  // Attribuuttiselektorin escapointi ilman CSS.escapea (jsdom ei tarjoa sitä).
  const byAppId = (id: string): HTMLElement | null =>
    root.querySelector(`[data-app-id="${id.replace(/\\/g, '\\\\').replace(/"/g, '\\"')}"]`);

  // --- sisäänrakennetut tekstit (window.fasthub.appMessages) ---
  // Ennen kaikkea muuta: validointi ja latausilmoitukset lukevat katalogia heti
  // ensimmäisestä renderöinnistä alkaen. Ilman siementä jäädään englantiin.
  configureMessages(window.fasthub?.appMessages);

  // --- vars-nimiavaruus (window.fasthub.appVariables) ---
  const appVars: AppVariable[] = window.fasthub?.appVariables ?? [];
  const varKey = (name: string): string =>
    `${VAR_STORAGE_PREFIX}${window.fasthub?.appId ?? ''}:${name}`;
  const persistedVars = new Set<string>();
  for (const v of appVars) {
    if (!v || !v.name) continue;
    let value = v.defaultValue;
    if (v.persist === 'local') {
      persistedVars.add(v.name);
      try {
        const stored = localStorage.getItem(varKey(v.name));
        if (stored !== null) value = JSON.parse(stored);
      } catch {
        // localStorage estetty / korruptoitunut arvo — käytä oletusta.
      }
    }
    store.setSlot('vars', v.name, value);
  }
  function setVariable(name: string, value: unknown): void {
    store.setSlot('vars', name, value);
    if (persistedVars.has(name)) {
      try {
        localStorage.setItem(varKey(name), JSON.stringify(value));
      } catch {
        // Tallennus epäonnistui — arvo elää silti sessiossa.
      }
    }
  }

  // --- currentUser-nimiavaruus (WP-ABP5) ---
  // Palvelin siementää tämän vain autentikoidulle katsojalle; anonyymille
  // (julkinen App standalone-osoitteessa) käytetään tyhjää katsojaa. Tyhjä
  // EI ole sama kuin puuttuva: roles on aina taulukko, jotta
  // `currentUser.roles.includes(...)` ei kaadu vaan on yksinkertaisesti false.
  //
  // TURVARAJA: nämä kentät ovat käyttöliittymän personointia, EIVÄT
  // pääsynhallintaa — koko nimiavaruus elää selaimessa. Aito raja on API
  // Gateway -reitin tasolla (päätös D7).
  const seededUser = window.fasthub?.currentUser;
  store.state['currentUser'] = {
    userId: '', username: '', displayName: '', email: '', roles: [],
    ...(seededUser && typeof seededUser === 'object' ? seededUser : {}),
  };

  // --- params-nimiavaruus (URL-kyselyparametrit) ---
  // REAKTIIVINEN WP-ABP7:stä alkaen (päätös D5 = A). Aiemmin arvot
  // kirjoitettiin kerran suoraan store.stateen ohi setSlotin, joten niillä ei
  // ollut tilaajia: navigate vaihtoi osoitteen mutta mikään ruudulla ei
  // muuttunut. Nyt sivut voi ilmaista olemassa olevilla palikoilla —
  // container jonka initiallyVisible on ${params.view === 'orders'}.
  store.state['params'] = {};
  const readParams = (): Record<string, string> => {
    const out: Record<string, string> = {};
    try {
      new URLSearchParams(window.location.search).forEach((v, k) => {
        out[k] = v;
      });
    } catch {
      // Ei window-kontekstia (testit) — params jää tyhjäksi.
    }
    return out;
  };
  const syncParams = (): void => {
    const next = readParams();
    const current = (store.state['params'] ?? {}) as Record<string, unknown>;
    // Pudonneet avaimet nollataan undefinediksi, jotta niihin sidotut
    // lausekkeet evaluoituvat uudelleen eivätkä jää haamuarvoon.
    for (const key of Object.keys(current)) {
      if (!(key in next)) store.setSlot('params', key, undefined);
    }
    for (const [key, value] of Object.entries(next)) {
      store.setSlot('params', key, value);
    }
  };
  syncParams();
  // Selaimen takaisin/eteenpäin on yhtä lailla navigointi.
  window.addEventListener('popstate', syncParams);

  /**
   * Syötekenttien slot-kytkentä yhdelle alipuulle.
   *
   * <p>Erotettu funktioksi WP-HV5:ssä. Bootissa alipuu on koko dokumentti,
   * mutta `repeater` luo rivinsä vasta ajossa — ilman uudelleenajettavaa
   * kytkentää ajossa syntyneen rivin kentät eivät julkaisisi slottejaan
   * lainkaan, ja rivin sidonnat lukisivat tyhjää. Parametrin nimi on `root`,
   * koska se ON kytkettävän alipuun juuri; ulomman `root`in varaan jäävät
   * `byAppId` ja muut koko dokumentin apurit.
   */
  const wireFormFields = (root: ParentNode): void => {
  // --- Input-slotit: text-input (search-input on oma lohkonsa alempana) ---
  // EXCLUDe-lista = ne input-variantit joiden slot ei ole merkkijonoarvo tai
  // joiden kytkentä eroaa muuten: checkbox julkaisee checked-boolean ja
  // number-input numeron, joten kumpikaan ei saa mennä tämän läpi (input.value
  // olisi 'on' / merkkijononumero).
  root.querySelectorAll<HTMLInputElement>(
    'input[data-app-input-name]:not(.sp-search-input):not(.sp-daterange-input):not(.sp-file-input)'
      + ':not(.sp-radio-input):not(.sp-checkbox-input):not(.sp-number-input)',
  ).forEach((input) => {
    const h = hostOf(input);
    if (!h) return;
    store.setSlot(h.id, 'value', input.value);
    store.setSlot(h.id, 'isFocused', false);
    store.setSlot(h.id, 'isVisible', true);
    input.addEventListener('input', () => store.setSlot(h.id, 'value', input.value));
    input.addEventListener('focus', () => store.setSlot(h.id, 'isFocused', true));
    input.addEventListener('blur', () => store.setSlot(h.id, 'isFocused', false));
  });

  // --- Search-input: value (joka näppäily) + debouncedValue (tauon jälkeen)
  //     + clear-nappi + autoFocus. Erillään yleisestä input-lohkosta, koska
  //     debounce/clear/näkyvyys eroavat. data-app-search-debounce = wire-up-
  //     marker (guardrail etsii tämän bundlesta). ---
  root.querySelectorAll<HTMLInputElement>('input.sp-search-input').forEach((input) => {
    const h = hostOf(input);
    if (!h) return;
    const id = h.id;
    const debounceMs = parseInt(input.getAttribute('data-app-search-debounce') || '300', 10) || 0;
    const clearBtn = h.host.querySelector<HTMLElement>('.sp-search-clear');
    const updateClear = () => {
      if (clearBtn) clearBtn.style.display = input.value ? '' : 'none';
    };
    const publishDebounced = () => store.setSlot(id, 'debouncedValue', input.value);

    store.setSlot(id, 'value', input.value);
    store.setSlot(id, 'debouncedValue', input.value);
    store.setSlot(id, 'isFocused', false);
    store.setSlot(id, 'isVisible', h.host.style.display !== 'none');
    updateClear();

    let timer: ReturnType<typeof setTimeout> | undefined;
    input.addEventListener('input', () => {
      store.setSlot(id, 'value', input.value);
      updateClear();
      if (debounceMs <= 0) {
        publishDebounced();
        return;
      }
      if (timer) clearTimeout(timer);
      timer = setTimeout(publishDebounced, debounceMs);
    });
    input.addEventListener('focus', () => store.setSlot(id, 'isFocused', true));
    input.addEventListener('blur', () => store.setSlot(id, 'isFocused', false));
    clearBtn?.addEventListener('click', () => {
      input.value = '';
      if (timer) clearTimeout(timer);
      store.setSlot(id, 'value', '');
      store.setSlot(id, 'debouncedValue', '');
      updateClear();
      input.focus();
    });
    if (input.getAttribute('data-app-autofocus') === '1') {
      // Late-loaded/sandbox-iframessa natiivi autofocus ei aina laukea.
      try {
        input.focus();
      } catch {
        // fokusointi estetty — ei kriittinen.
      }
    }
  });

  // --- Lomakekenttien täydennys (WP-F1) ---
  // Neljä uutta kenttätyyppiä, joista kolme tarvitsee OMAN kytkentänsä:
  // select ja textarea eivät ole <input>-elementtejä (yleinen lohko ei osu
  // niihin), checkboxin arvo on boolean eikä 'on', ja number-inputin arvo on
  // luku eikä merkkijono. Kaikki neljä osallistuvat silti Formin payloadiin
  // data-app-input-name-attribuutilla kuten muutkin kentät.
  //
  // Valinta-tyyppiset kentät (select, checkbox) julkaisevat slotin CHANGE-
  // eventistä ja herättävät ympäröivän Formin kuplivalla input-eventillä —
  // sama kuvio kuin wireRadioGroupissa. Ilman herätystä formin recompute
  // jäisi selaimen input-eventin varaan, jota kaikki selaimet eivät emitoi
  // valintakontrolleille.
  // Haettavan selectin suodatinkenttä (WP-KL8).
  root.querySelectorAll<HTMLInputElement>('input.sp-select-filter').forEach((filter) => {
    const host = filter.closest('[data-app-id]');
    const sel = host?.querySelector<HTMLSelectElement>('select.sp-select');
    if (!sel) return;
    const apply = () => applySelectFilter(sel, filter.value);
    filter.addEventListener('input', apply);
    // Sidotut vaihtoehdot rakentavat <option>-listan uudelleen, jolloin suodatus
    // katoaisi ilman uudelleenajoa (WP-ABP1:n DOM-uudelleenrakennus).
    const hostId = host?.getAttribute('data-app-id');
    if (hostId) store.subscribe(`${hostId}.options`, () => queueMicrotask(apply));
    apply();
  });

  root.querySelectorAll<HTMLSelectElement>('select.sp-select').forEach((sel) => {
    const h = hostOf(sel);
    if (!h) return;
    store.setSlot(h.id, 'value', sel.value);
    store.setSlot(h.id, 'isVisible', h.host.style.display !== 'none');
    sel.addEventListener('change', () => {
      store.setSlot(h.id, 'value', sel.value);
      sel.dispatchEvent(new Event('input', { bubbles: true }));
    });
  });

  root.querySelectorAll<HTMLTextAreaElement>('textarea.sp-textarea').forEach((ta) => {
    const h = hostOf(ta);
    if (!h) return;
    store.setSlot(h.id, 'value', ta.value);
    store.setSlot(h.id, 'isFocused', false);
    store.setSlot(h.id, 'isVisible', h.host.style.display !== 'none');
    ta.addEventListener('input', () => store.setSlot(h.id, 'value', ta.value));
    ta.addEventListener('focus', () => store.setSlot(h.id, 'isFocused', true));
    ta.addEventListener('blur', () => store.setSlot(h.id, 'isFocused', false));
  });

  root.querySelectorAll<HTMLInputElement>('input.sp-checkbox-input').forEach((cb) => {
    const h = hostOf(cb);
    if (!h) return;
    store.setSlot(h.id, 'checked', cb.checked);
    store.setSlot(h.id, 'isVisible', h.host.style.display !== 'none');
    cb.addEventListener('change', () => {
      store.setSlot(h.id, 'checked', cb.checked);
      cb.dispatchEvent(new Event('input', { bubbles: true }));
    });
  });

  root.querySelectorAll<HTMLInputElement>('input.sp-number-input').forEach((num) => {
    const h = hostOf(num);
    if (!h) return;
    const publish = () => store.setSlot(h.id, 'value', parseNumberValue(num.value));
    publish();
    store.setSlot(h.id, 'isVisible', h.host.style.display !== 'none');
    num.addEventListener('input', publish);
  });
  };
  wireFormFields(root);

  // Ohjelmallinen lähetys synteettisellä submit-eventillä — EI requestSubmit():
  // sandbox-iframe (allow-scripts ilman allow-forms, AppRunner/PreviewPane)
  // keskeyttää natiivin form submission -algoritmin ennen submit-eventtiä,
  // jolloin requestSubmit no-oppaisi. Synteettinen dispatch ohittaa algoritmin:
  // alla oleva form-käsittelijä validoi ja POSTaa, eikä navigointia voi syntyä.
  function submitFormProgrammatically(form: HTMLFormElement): void {
    form.dispatchEvent(new Event('submit', { bubbles: true, cancelable: true }));
  }

  // --- Form-slotit: values/isDirty/errors/isValid + submit-tilat ---
  root.querySelectorAll<HTMLFormElement>('form.sp-form[data-app-id]').forEach((form) => {
    const id = form.getAttribute('data-app-id') || '';
    if (!id) return;
    // Sääntövalidointi (WP-F2): FormRenderer serialisoi validators-propin tähän
    // lohkoon; ennen tätä sillä ei ollut kuluttajaa lainkaan.
    const rules = parseRules(
      form.querySelector<HTMLScriptElement>('script.sp-app-form-validators')?.textContent,
    );
    // Virheiden NÄYTTÖ-moodi (arvot ja isValid lasketaan aina, jotta bindingit
    // toimivat alusta asti — moodi koskee vain inline-tekstin näkyvyyttä):
    //   always       — heti kirjoittaessa
    //   after-submit — vasta 1. submit-yrityksen jälkeen, sen jälkeen elävästi
    //   never        — vain submit-hetkellä (kirjoittaminen ei virkistä)
    const errorMode = form.getAttribute('data-app-form-validate-on-blur') || 'after-submit';
    let submitAttempted = false;

    /**
     * Kirjoita virheet kenttien alle. Tyhjä teksti piilottaa (CSS :empty).
     *
     * <p>WP-HV6: `repeater`in rivillä sama kenttänimi esiintyy N kertaa, joten
     * paikan avain on rivin oma indeksoitu polku (`rows[0].hours`). Ilman tätä
     * yhden rivin virhe maalautuisi jokaiseen riviin — käyttäjä näkisi
     * "Required" viidessä rivissä kun yksi on tyhjä, eikä tietäisi mitä korjata.
     */
    const paintErrors = (errors: Record<string, string>) => {
      form.querySelectorAll<HTMLElement>('[data-app-error-for]').forEach((slot) => {
        const field = slot.getAttribute('data-app-error-for') || '';
        const row = slot.closest<HTMLElement>('.sp-repeater-row');
        if (row) {
          const rep = row.closest<HTMLElement>('.sp-repeater[data-app-repeater-name]');
          const key = rep?.getAttribute('data-app-repeater-name') || '';
          const index = row.getAttribute('data-app-row-index') || '';
          slot.textContent = key ? errors[`${key}[${index}].${field}`] || '' : '';
          return;
        }
        slot.textContent = errors[field] || '';
      });
    };

    /**
     * Onko kenttä piilotettu — itse tai jonkin lomakkeen sisäisen esivanhempansa
     * takia (WP-SF3, löydös T-B). Katsotaan inline-`display`, koska juuri sen
     * runtime kirjoittaa näkyvyyssidonnasta ja show/hide-lifecyclestä.
     */
    const isHiddenField = (el: HTMLElement): boolean => {
      let node: HTMLElement | null = el;
      while (node && node !== form.parentElement) {
        if (node.style.display === 'none') return true;
        node = node.parentElement;
      }
      return false;
    };

    /**
     * Lue yksi kenttä arvo- ja virhekasaan (WP-HV6). Erotettu funktioksi, jotta
     * `repeater`in rivi voi kerätä samoilla säännöillä omaan objektiinsa —
     * ilman jakoa rivien validointi olisi ollut lomakkeen logiikan kopio, ja
     * kopio ajautuu.
     *
     * <p>`hiddenSink` kerää piilotettujen kenttien nimet `evaluateRules`ille. Rivillä
     * nimi on paljas kenttänimi, jonka `collectRepeaters` muuntaa rivin indeksoiduksi
     * avaimeksi (`rows[0].hours`) rivisääntöjä (`rows[].hours`) varten.
     */
    const collectField = (
      input: HTMLInputElement,
      name: string,
      values: Record<string, unknown>,
      errors: Record<string, string>,
      hiddenSink: Set<string> | null,
    ): void => {
      // WP-SF3 (D3 = A): piilotettu kenttä ei ole kysytty kenttä. Ennen tätä
      // sen arvo kerättiin ja `required` validoitiin normaalisti, jolloin
      // ehdollisesti piilotettu pakollinen kenttä lukitsi lomakkeen
      // lähettämättömäksi virheellä jota käyttäjä ei voinut nähdä eikä
      // korjata — nappi joka ei tee mitään. Arvo jää myös pois payloadista:
      // lomake ei saa lähettää kenttää jota käyttäjä ei nähnyt.
      if (isHiddenField(input)) {
        hiddenSink?.add(name);
        return;
      }
      const required = input.getAttribute('data-app-input-required') === '1';
      // File-upload: arvo on files-slotin taulukko, ei natiivi polkustringi.
      if (input.type === 'file') {
        const h = hostOf(input);
        const files = (h && store.getSlot(h.id, 'files')) || [];
        values[name] = files;
        if (required && (!Array.isArray(files) || files.length === 0)) {
          errors[name] = resolveMessage('required');
        }
        return;
      }
      // Radio-group: monta radiota jakaa nimen — arvo on valitun radion arvo
      // (value-slotista). Käsittele ryhmä vain kerran (nimi jo kerätty).
      if (input.type === 'radio') {
        if (name in values) return;
        const h = hostOf(input);
        const picked = h ? store.getSlot(h.id, 'value') : '';
        values[name] = typeof picked === 'string' ? picked : '';
        if (required && (!picked || picked === '')) {
          errors[name] = resolveMessage('required');
        }
        return;
      }
      // Monivalinta (WP-ABP9): monta rastia jakaa nimen ja arvo on TAULUKKO.
      // Tämän on oltava ENNEN checkbox-haaraa — muuten ryhmän rastit
      // menisivät yksittäisinä booleaneina läpi ja payloadiin jäisi
      // viimeisen rastin tila. Arvo luetaan values-slotista (sama kuvio
      // kuin radio-groupilla); required = vähintään yksi.
      if (input.classList.contains('sp-multi-input')) {
        if (name in values) return;
        const h = hostOf(input);
        const picked = h ? store.getSlot(h.id, 'values') : [];
        const list = Array.isArray(picked) ? picked : [];
        values[name] = list;
        if (required && list.length === 0) {
          errors[name] = resolveMessage('required');
        }
        return;
      }
      // Checkbox (WP-F1): arvo on rasti, EI input.value ('on'). Required
      // tarkoittaa tässä "pakko rastittaa" (ehtojen hyväksyntä).
      if (input.type === 'checkbox') {
        values[name] = input.checked;
        if (required && !input.checked) {
          errors[name] = resolveMessage('required');
        }
        return;
      }
      // Number-input (WP-F1): arvo on luku tai null, ja min/max pakotetaan
      // TÄSSÄ — submit on ohjelmallinen, joten selaimen natiivi
      // constraint-validointi ei koskaan aja.
      if (input.type === 'number') {
        const parsed = parseNumberValue(input.value);
        values[name] = parsed;
        if (parsed === null) {
          if (input.value.trim() !== '') {
            errors[name] = resolveMessage('notANumber');
          } else if (required) {
            errors[name] = resolveMessage('required');
          }
          return;
        }
        const min = input.getAttribute('data-app-input-min');
        const max = input.getAttribute('data-app-input-max');
        if (min !== null && parsed < Number(min)) {
          errors[name] = resolveMessage('min', min);
        } else if (max !== null && parsed > Number(max)) {
          errors[name] = resolveMessage('max', max);
        }
        return;
      }
      // select ja textarea eivät tarvitse omaa haaraa: molemmat tarjoavat
      // saman .value-rajapinnan kuin tekstikenttä (select tyhjä = "" kun
      // placeholder-optio on valittuna, joten required puree).
      values[name] = input.value;
      if (required && input.value.trim() === '') {
        errors[name] = resolveMessage('required');
      }
    };

    /**
     * Kerää `repeater`ien rivit sisäkkäisiksi taulukoiksi (WP-HV6, D7 = A).
     *
     * <p>Arvo: `values[<repeaterin name>] = [{kenttä: arvo}, …]` — yksi objekti
     * per rivi. Virhe: LITTEÄ mutta indeksoitu avain `rows[0].hours`, jotta
     * `isValid` pysyy yhtenä laskuna (`errors`in avainmäärä) ja
     * `paintErrors` löytää oikean rivin oikean kentän. Sisäkkäinen
     * virherakenne olisi rikkonut molemmat.
     *
     * <p>Nimetön repeater ohitetaan: ilman avainta rivit eivät voisi päätyä
     * payloadiin mihinkään, ja arvaus olisi väärä hiljaa.
     */
    const collectRepeaters = (
      values: Record<string, unknown>,
      errors: Record<string, string>,
      hiddenFields: Set<string>,
    ): void => {
      form.querySelectorAll<HTMLElement>('.sp-repeater[data-app-repeater-name]').forEach((rep) => {
        const key = rep.getAttribute('data-app-repeater-name') || '';
        if (!key) return;
        // Sisäkkäinen repeater kuuluu ULOIMMAN rivinsä keräykseen, ei tähän.
        if (rep.parentElement?.closest('.sp-repeater-row')) return;
        const collected: Record<string, unknown>[] = [];
        rep.querySelectorAll<HTMLElement>(':scope > [data-app-repeater-rows] > .sp-repeater-row')
          .forEach((rowEl, index) => {
            const rowValues: Record<string, unknown> = {};
            const rowErrors: Record<string, string> = {};
            const rowHidden = new Set<string>();
            rowEl.querySelectorAll<HTMLInputElement>('[data-app-input-name]').forEach((input) => {
              const name = input.getAttribute('data-app-input-name') || '';
              if (!name) return;
              collectField(input, name, rowValues, rowErrors, rowHidden);
            });
            collected.push(rowValues);
            for (const [field, message] of Object.entries(rowErrors)) {
              errors[`${key}[${index}].${field}`] = message;
            }
            // Piilotettu rivikenttä ei ole kysytty kenttä myöskään rivisäännölle.
            for (const field of rowHidden) {
              hiddenFields.add(`${key}[${index}].${field}`);
            }
          });
        values[key] = collected;
      });
    };

    const recompute = (dirty: boolean, fromSubmit = false) => {
      const values: Record<string, unknown> = {};
      const errors: Record<string, string> = {};
      /** Piilotetut kentät: nimi tunnetaan, mutta arvoa ei kerätä eikä validoida. */
      const hiddenFields = new Set<string>();
      form.querySelectorAll<HTMLInputElement>('[data-app-input-name]').forEach((input) => {
        const name = input.getAttribute('data-app-input-name') || '';
        if (!name) return;
        // Rivien kentät kerätään erikseen sisäkkäisinä (collectRepeaters):
        // litteänä N riviä samannimisiä kenttiä kirjoittaisi toistensa päälle.
        if (input.closest('.sp-repeater-row')) return;
        collectField(input, name, values, errors, hiddenFields);
      });
      collectRepeaters(values, errors, hiddenFields);
      // Sääntövalidointi per-kenttä-requiredin JÄLKEEN: kenttä joka on jo
      // virheellinen ei saa toista virhettä (ensimmäinen osuma voittaa).
      const withRules = evaluateRules(rules, values, errors, hiddenFields);
      store.setSlot(id, 'values', values);
      store.setSlot(id, 'errors', withRules);
      store.setSlot(id, 'isValid', Object.keys(withRules).length === 0);
      if (dirty) store.setSlot(id, 'isDirty', true);

      if (errorMode === 'always' || (errorMode === 'after-submit' && submitAttempted)) {
        paintErrors(withRules);
      } else if (fromSubmit) {
        // 'never': virheet ilmestyvät vain submit-yrityksestä ja jäävät
        // näkyviin seuraavaan yritykseen asti.
        paintErrors(withRules);
      }
    };
    store.setSlot(id, 'isDirty', false);
    store.setSlot(id, 'isSubmitting', false);
    store.setSlot(id, 'submitCount', 0);
    store.setSlot(id, 'lastSubmitStatus', 'idle');
    initResponseSlots(store, id);
    recompute(false);
    form.addEventListener('input', () => recompute(true));
    // change-kuuntelu valintakontrolleille (select, checkbox): niiden oma
    // kytkentä herättää jo kuplivan input-eventin, mutta tämä kattaa myös
    // kentät joita runtime ei ole ehtinyt kytkeä (late-loaded markup).
    form.addEventListener('change', () => recompute(true));
    // Repeaterin rivit ilmestyvät form-passin JÄLKEEN — ks. REPEATER_ROWS_EVENT.
    form.addEventListener(REPEATER_ROWS_EVENT, () => recompute(false));
    // Submit-POST kulkee AB-WP4.1:n sillan kautta. Estetään aina natiivi-
    // submit (sivun reload); ilman siltaa (kehystämätön) submit no-oppaa.
    const submitApi = form.getAttribute('data-app-form-submit-api') || '';
    const resetOnSuccess = form.getAttribute('data-app-form-reset-on-success') === '1';

    // Lifecycle-ajoitukset lomakkeen lähetykselle (WP-ABP4). Ennen tätä
    // on-success/on-api-fail laukesivat VAIN trigger-api-napista, joten
    // submit-form-napille määritellyt kortit eivät koskaan lauenneet — vaikka
    // editori antoi ne määritellä. Ilman tätä tallennuksen jälkeen ei voinut
    // näyttää kuittausta, sulkea modaalia eikä päivittää listaa.
    //
    // Lähettäjä ei tule submit-eventistä (dispatch on synteettinen, joten
    // e.submitter on tyhjä), joten viimeisin klikattu submit-nappi otetaan
    // talteen. Enter-lähetyksellä sitä ei ole → käytetään lomakkeen
    // ensimmäistä submit-nappia.
    let lastSubmitter: HTMLElement | null = null;
    form.addEventListener('click', (e) => {
      const btn = (e.target as HTMLElement | null)?.closest?.('button[data-app-action="submit-form"]');
      if (btn) lastSubmitter = btn as HTMLElement;
    });
    const submitterOf = (): HTMLElement | null =>
      lastSubmitter ?? form.querySelector<HTMLElement>('button[data-app-action="submit-form"]');
    const runSubmitTiming = (timing: string): void => {
      const btn = submitterOf();
      if (btn) runLifecycleActionsByTiming(btn, timing);
    };

    // Kaksoislähetyksen esto (upotussivun-tapahtumaviesti-plan.md WP-UV8, D6 = B):
    // lomakkeen submit-form-napit pois käytöstä kutsun ajaksi, kuten
    // rivitoiminnossa. Palautetaan vain ne napit jotka tämä itse otti pois
    // käytöstä. `finishSubmit` ajetaan sekä vastauskäsittelijässä (samassa
    // kohdassa kuin isSubmitting nollattiin ennenkin) että varmistuksena ketjun
    // lopussa: jos käsittelijä heittäisi ennen nollausta, lomake jäisi muuten
    // pysyvästi lukkoon. Lähetyskohtainen tunniste tekee siitä idempotentin
    // eikä myöhästynyt kutsu voi vapauttaa seuraavan lähetyksen lukkoa.
    let busyButtons: HTMLButtonElement[] = [];
    let activeSubmit = 0;
    let submitSeq = 0;
    const startSubmit = (): number => {
      submitSeq += 1;
      activeSubmit = submitSeq;
      store.setSlot(id, 'isSubmitting', true);
      busyButtons = Array.from(form.querySelectorAll<HTMLButtonElement>(
        'button[data-app-action="submit-form"]')).filter((b) => !b.disabled);
      for (const b of busyButtons) {
        b.disabled = true;
        b.setAttribute('data-app-busy', '1');
      }
      return activeSubmit;
    };
    const finishSubmit = (ticket: number): void => {
      if (ticket !== activeSubmit) return;
      activeSubmit = 0;
      store.setSlot(id, 'isSubmitting', false);
      for (const b of busyButtons) {
        b.disabled = false;
        b.removeAttribute('data-app-busy');
      }
      busyButtons = [];
    };

    form.addEventListener('submit', (e) => {
      e.preventDefault();
      // Uusi lähetys kesken edellisen ohitetaan ENNEN validointia, kortteja ja
      // kutsua (WP-UV8, D6 = B). Tuplaklikkaus tai Enter kesken kutsun tuottaisi
      // muuten kaksi tallennusta, kaksi ilmoitussähköpostia ja kaksi
      // konversiotapahtumaa. Ohitus kirjataan aikajanaan, jotta "klikkasin eikä
      // mitään tapahtunut" on selitettävissä.
      if (activeSubmit !== 0) {
        tracer.record('form-submit', id, submitApi, {
          isRoot: tracer.currentParent == null,
          message: 'ignored: the previous submit is still in progress',
        });
        return;
      }
      // Lomakkeen lähetys traceen (WP-DT2). Juuri vain jos ketjua ei ole jo
      // avattu: nappipainalluksesta lähtevä submit on klikin lapsi, mutta
      // Enter-lähetys ja ohjelmallinen submit aloittavat oman ketjunsa.
      openChain(tracer.record('form-submit', id, submitApi, {
        isRoot: tracer.currentParent == null,
      }));
      // Yritys kirjataan ENNEN laskentaa, jotta after-submit-moodi maalaa
      // virheet jo tästä samasta ajosta eikä vasta seuraavasta näppäilystä.
      submitAttempted = true;
      recompute(true, true);
      const errs = store.getSlot(id, 'errors') as Record<string, string> | undefined;
      if (errs && Object.keys(errs).length > 0) {
        runSubmitTiming('on-validation-fail');
        return; // invalidi ei lähetä
      }
      if (!bridge || !submitApi) return;
      const values = (store.getSlot(id, 'values') as Record<string, unknown>) || {};
      // Sidottu submitExtraFields mergataan kenttien PÄÄLLE (WP-AR4). Tämä on
      // ainoa tapa lähettää arvo jota käyttäjä ei näppäile: entinen
      // submitPayloadTemplate renderöitiin DOMiin muttei luettu koskaan, joten
      // HR-portaalissa employeeId jouduttiin panemaan näkyväksi, muokattavaksi
      // kentäksi — juuri se kenttä jonka ei pitäisi olla kumpaakaan (löydös F-E).
      const extra = formExtraFields(form, id, store);
      const body = extra && typeof extra === 'object' && !Array.isArray(extra)
        ? { ...values, ...(extra as Record<string, unknown>) }
        : values;
      const ticket = startSubmit();
      store.setSlot(id, 'lastSubmitStatus', 'pending');
      bridge.invoke(submitApi, body).then(
        (res) => {
          const outcome = describeResponse(res);
          const ok = outcome.ok;
          // Vastausslotit ENNEN statusta: `lastSubmitStatus == 'error'`iin
          // sidottu banneri lukee `lastErrorMessage`n samalla kierroksella.
          publishResponseSlots(store, id, outcome);
          finishSubmit(ticket);
          store.setSlot(id, 'lastSubmitStatus', ok ? 'success' : 'error');
          store.setSlot(id, 'submitCount', ((store.getSlot(id, 'submitCount') as number) || 0) + 1);
          runSubmitTiming(ok ? 'on-success' : 'on-api-fail');
          if (ok && resetOnSuccess) {
            form.reset();
            // Tyhjennetty lomake ei ole "epäonnistunut yritys": nollataan
            // yrityslippu ja pyyhitään maalatut virheet, muuten juuri
            // lähetetty lomake näyttäisi heti "Required" jokaisessa
            // pakollisessa kentässä (WP-F2).
            submitAttempted = false;
            recompute(false);
            paintErrors({});
          }
        },
        () => {
          publishResponseSlots(store, id, describeResponse(null));
          finishSubmit(ticket);
          store.setSlot(id, 'lastSubmitStatus', 'error');
          runSubmitTiming('on-api-fail');
        },
      ).finally(() => finishSubmit(ticket));
    });
    // Enter-lähetys kuuluu runtimelle: selaimen implisiittinen lähetys toimii
    // vain yhden kentän lomakkeissa eikä lainkaan sandbox-iframessa, joten
    // Enter kytketään attribuutista — ja natiivipolku estetään aina, ettei
    // lähetys tuplaannu (B1).
    const submitOnEnter = form.getAttribute('data-app-form-submit-on-enter') !== '0';
    form.addEventListener('keydown', (e) => {
      if (e.key !== 'Enter' || !(e.target instanceof HTMLInputElement)) return;
      e.preventDefault();
      if (submitOnEnter) submitFormProgrammatically(form);
    });
  });

  // --- Bindings-lohkot ---
  // Lohkon arvo on joko paljas lauseke ("expr") tai {e: lauseke, f: fallback}
  // (WP-SF1). Kumpikin muoto luetaan: paljas muoto on vanha, ja lohkoja voi olla
  // selaimen välimuistissa vanhemmalta renderöinniltä.
  interface Binding {
    hostId: string;
    slot: string;
    ast: AstNode;
    /** Lausekkeen lähdeteksti — vain tracea varten (WP-DT2). */
    expr: string;
    /** Erillinen lippu, koska fallback saa olla myös null/0/''/false. */
    hasFallback: boolean;
    fallback: unknown;
  }
  /**
   * Evaluoi sidonta ja kirjaa se traceen (WP-DT2). `trigger` on se
   * riippuvuusavain joka laukaisi uudelleenevaluoinnin (tyhjä = ensiajo
   * bootissa), joten puusta näkee mikä muutos ketjun aloitti.
   *
   * Jos evaluointi ei tuota yhtään slot-emittiä, event jää lapsettomaksi —
   * juuri se on paneelin no-op-suodattimen tunnistin.
   */
  const reEval = (b: Binding, trigger = '') => {
    const event = tracer.record('binding-eval', trigger || 'init', `${b.hostId}.${b.slot}`, {
      slot: b.slot,
      expression: b.expr,
    });
    if (!event) {
      evaluateBinding(b);
      return;
    }
    tracer.withParent(event.id, () => evaluateBinding(b));
  };
  const evaluateBinding = (b: Binding) => {
    let value: unknown;
    try {
      value = evalAst(b.ast, store.state);
    } catch {
      value = undefined;
    }
    // WP-SF1 (D1 = A, löydös T-G): `fallback` oli aiemmin VAIN palvelimen
    // renderöimä alkuarvo, joka katosi ensimmäisessä evaluoinnissa — vaikka
    // SKILL.md §3 lupaa sen sekä kaatuvalle että undefined-tulokselle. Nyt se
    // kannetaan bindings-lohkossa ja käytetään molemmissa tapauksissa.
    // `null`, `0`, `''` ja `false` ovat OIKEITA arvoja eivätkä laukaise sitä:
    // tyhjä hakutulos ei ole virhe, ja nollan korvaaminen fallbackilla olisi
    // sama hiljainen väärä luku jota tämä korjaus poistaa.
    if (value === undefined && b.hasFallback) {
      value = b.fallback;
    }
    const host = byAppId(b.hostId);
    if (host) {
      applySlotToDom(host, b.slot, value, (slot, v) => store.setSlot(b.hostId, slot, v));
    }
    store.setSlot(b.hostId, b.slot, value);
  };

  /**
   * Lue alipuun `sp-app-bindings`-lohkot, tilaa ne riippuvuuksilleen ja
   * evaluoi kerran.
   *
   * <p>Erotettu funktioksi WP-HV5:ssä samasta syystä kuin
   * {@link wireFormFields}: `repeater`in rivit syntyvät bootin jälkeen, ja
   * ilman uudelleenajoa niiden sidonnat eivät olisi olemassa lainkaan.
   *
   * <p>`scopeMap` kääntää rivin lausekkeet rivin omaan nimiavaruuteen
   * (`total` → `total__r2`, `row` → rivin tilaobjekti). Uudelleenkirjoitus
   * tehdään ASTille — ks. {@link rewriteScopedIds}.
   */
  const registerBindings = (
    root: ParentNode,
    scopeMap?: ReadonlyMap<string, string>,
  ): void => {
    const bindings: Binding[] = [];
    root.querySelectorAll<HTMLScriptElement>('script.sp-app-bindings').forEach((script) => {
      const h = hostOf(script);
      if (!h) return;
      let map: Record<string, unknown>;
      try {
        map = JSON.parse(script.textContent || '{}');
      } catch {
        return; // rikkinäinen lohko — skippaa, muu sivu boottaa
      }
      for (const [slot, spec] of Object.entries(map)) {
        let expr: string;
        let hasFallback = false;
        let fallback: unknown;
        if (typeof spec === 'string') {
          expr = spec;
        } else if (spec !== null && typeof spec === 'object'
          && typeof (spec as { e?: unknown }).e === 'string') {
          expr = (spec as { e: string }).e;
          hasFallback = 'f' in (spec as Record<string, unknown>);
          fallback = (spec as { f?: unknown }).f;
        } else {
          continue;
        }
        let ast: AstNode;
        try {
          ast = parse(expr);
        } catch {
          continue; // parse-virhe → binding pois päältä, alkuarvo jää
        }
        if (scopeMap) ast = rewriteScopedIds(ast, scopeMap);
        bindings.push({ hostId: h.id, slot, ast, expr, hasFallback, fallback });
      }
    });
    for (const b of bindings) {
      for (const dep of Object.keys(collectDeps(b.ast))) {
        // Tilaaja saa laukaisseen dep-avaimen — se kannetaan traceen, jotta
        // rivistä näkee MIKÄ muutos käynnisti tämän evaluoinnin.
        store.subscribe(dep, (depKey) => reEval(b, depKey));
      }
      reEval(b);
    }
  };
  registerBindings(root);

  // --- Tabs ---
  // ARIA (WP-TA1): näkyvä tila ja avustavan teknologian tila päivitetään SAMASSA
  // kohdassa. Jos aria-selected jäisi palvelimen renderöimään arvoon, ruudunlukija
  // kertoisi ensimmäisen välilehden olevan valittuna koko istunnon ajan.
  // WP-AR7: välilehden näkyvyys = sen paneelin SISÄLLÖN näkyvyys. Paneeli on
  // renderöijän kuori; App sitoo näkyvyyden lapsikonttiin, joten sitä on
  // katsottava. Ilman tätä roolikohtainen välilehti jätti Appin tyhjäksi:
  // nappi näkyi, mutta sen klikkaus piilotti edellisen paneelin eikä näyttänyt
  // uutta (löydös F-L).
  function panelContent(panel: HTMLElement): HTMLElement | null {
    return panel.querySelector<HTMLElement>(':scope > [data-app-id]');
  }
  function tabIsVisible(panel: HTMLElement): boolean {
    const content = panelContent(panel);
    return content ? content.style.display !== 'none' : true;
  }
  function activateTab(tabs: HTMLElement, index: number): void {
    const buttons = tabs.querySelectorAll<HTMLElement>('.sp-tabs-button');
    const panels = tabs.querySelectorAll<HTMLElement>('.sp-tabs-panel');
    let clamped = Math.max(0, Math.min(index, panels.length - 1));
    // Piilotettua välilehteä ei voi aktivoida — valitaan lähin näkyvä, jottei
    // näkymä koskaan jää tyhjäksi.
    if (panels[clamped] && !tabIsVisible(panels[clamped])) {
      const fallback = Array.from(panels).findIndex((p) => tabIsVisible(p));
      if (fallback < 0) return; // kaikki piilossa: jätetään tila ennalleen
      clamped = fallback;
    }
    buttons.forEach((b) => {
      const active = Number(b.getAttribute('data-app-tab-index')) === clamped;
      b.classList.toggle('sp-tabs-button--active', active);
      b.setAttribute('aria-selected', String(active));
      // Roving tabindex: vain aktiivinen välilehti on tabulaattorijärjestyksessä.
      b.setAttribute('tabindex', active ? '0' : '-1');
    });
    panels.forEach((p) => {
      p.style.display = Number(p.getAttribute('data-app-tab-index')) === clamped ? '' : 'none';
    });
  }
  root.querySelectorAll<HTMLElement>('.sp-tabs').forEach((tabs) => {
    const buttons = Array.from(tabs.querySelectorAll<HTMLElement>('.sp-tabs-button'));
    const panels = Array.from(tabs.querySelectorAll<HTMLElement>('.sp-tabs-panel'));

    // Napin näkyvyys seuraa paneelin sisällön näkyvyyttä, ja jos aktiivinen
    // välilehti katoaa, siirrytään ensimmäiseen näkyvään.
    const syncTabButtons = (): void => {
      let activeHidden = false;
      panels.forEach((panel, i) => {
        const visible = tabIsVisible(panel);
        const btn = buttons[i];
        if (btn) setVisible(btn, visible);
        if (!visible && panel.style.display !== 'none') activeHidden = true;
      });
      if (activeHidden) {
        const next = panels.findIndex((p) => tabIsVisible(p));
        if (next >= 0) activateTab(tabs, next);
      }
    };
    syncTabButtons();
    for (const panel of panels) {
      const content = panelContent(panel);
      const contentId = content?.getAttribute('data-app-id');
      if (contentId) store.subscribe(`${contentId}.isVisible`, syncTabButtons);
    }
    buttons.forEach((btn, position) => {
      btn.addEventListener('click', () =>
        activateTab(tabs, Number(btn.getAttribute('data-app-tab-index'))));
      // Nuolinäppäimet siirtävät sekä fokuksen että valinnan (WAI-ARIA:n
      // "automatic activation" — yksinkertaisin kuvio, ja välilehtien vaihto on
      // tässä halpaa, koska paneelit on jo renderöity).
      btn.addEventListener('keydown', (e) => {
        const key = (e as KeyboardEvent).key;
        let target: number | null = null;
        if (key === 'ArrowRight') target = (position + 1) % buttons.length;
        else if (key === 'ArrowLeft') target = (position - 1 + buttons.length) % buttons.length;
        else if (key === 'Home') target = 0;
        else if (key === 'End') target = buttons.length - 1;
        if (target === null) return;
        e.preventDefault();
        const next = buttons[target];
        activateTab(tabs, Number(next.getAttribute('data-app-tab-index')));
        next.focus();
      });
    });
  });

  // --- Modal ---
  function setModalOpen(modal: HTMLElement, open: boolean): void {
    setVisible(modal, open);
    const id = modal.getAttribute('data-app-id');
    if (id) store.setSlot(id, 'isOpen', open);
  }
  root.querySelectorAll<HTMLElement>('[data-app-modal="1"]').forEach((modal) => {
    modal.querySelector('.sp-modal-close')?.addEventListener('click', () => setModalOpen(modal, false));
    if (modal.getAttribute('data-app-modal-close-on-backdrop') === '1') {
      modal.querySelector('[data-app-modal-backdrop]')
        ?.addEventListener('click', () => setModalOpen(modal, false));
    }
  });
  doc?.addEventListener('keydown', (e) => {
    if ((e as KeyboardEvent).key !== 'Escape') return;
    root.querySelectorAll<HTMLElement>('[data-app-modal="1"]').forEach((modal) => {
      if (modal.style.display !== 'none'
        && modal.getAttribute('data-app-modal-close-on-escape') === '1') {
        setModalOpen(modal, false);
      }
    });
  });

  // --- Alert dismiss ---
  root.querySelectorAll<HTMLElement>('.sp-alert-close').forEach((btn) => {
    btn.addEventListener('click', () => {
      const alert = btn.closest('.sp-alert') as HTMLElement | null;
      if (!alert) return;
      setVisible(alert, false);
      const id = alert.getAttribute('data-app-id');
      if (id) store.setSlot(id, 'isVisible', false);
    });
  });

  // --- Toastit ---
  function showToast(message: string, severity: string, duration: number): void {
    let container = doc?.querySelector('.sp-toast-container') as HTMLElement | null;
    if (!container) {
      container = doc!.createElement('div');
      container.className = 'sp-toast-container';
      // Ruudunlukija ilmoittaa uudet toastit häiritsemättä (polite).
      container.setAttribute('role', 'status');
      container.setAttribute('aria-live', 'polite');
      doc!.body.appendChild(container);
    }
    const toast = doc!.createElement('div');
    const sev = ['info', 'success', 'warning', 'error'].includes(severity) ? severity : 'info';
    toast.className = `sp-toast sp-toast--${sev}`;
    // Viesti omaan spaniinsa (textContent → ei markup-injektiota) jotta
    // sulje-nappi asettuu flexissä oikeaan reunaan viestin viereen.
    const text = doc!.createElement('span');
    text.className = 'sp-toast-message';
    text.textContent = message;
    toast.appendChild(text);
    const close = doc!.createElement('button');
    close.type = 'button';
    close.className = 'sp-toast-close';
    close.setAttribute('aria-label', 'Dismiss');
    close.textContent = '×';
    close.addEventListener('click', () => toast.remove());
    toast.appendChild(close);
    container.appendChild(toast);
    // Rajaa näkyvä pino: karsi vanhimmat kun katto ylittyy.
    while (container.children.length > TOAST_STACK_MAX) {
      container.firstElementChild?.remove();
    }
    const ms = duration > 0 ? duration : duration === 0 ? 0 : 4000;
    if (ms > 0) setTimeout(() => toast.remove(), ms);
  }

  // --- Lifecycle-actionit (on-click; API-ajoitukset tulevat WP4.1:ssä) ---
  interface LifecycleActionEntry {
    operation?: string;
    targets?: string[];
    timing?: string;
    textValue?: string;
    tabIndex?: number;
    toastMessage?: string;
    toastMessageExpression?: string;
    toastSeverity?: string;
    toastDuration?: number;
    navigateTarget?: string;
    navigateMode?: string;
    linkUrl?: string;
    linkTarget?: string;
    variableName?: string;
    valueSource?: string;
    literalValue?: unknown;
    valueExpression?: string;
    linkUrlExpression?: string;
    documentSourceId?: string;
    documentPayloadExpression?: string;
    messageType?: string;
    messageData?: unknown;
    messageDataExpression?: string;
  }
  function substitutePlaceholders(template: string, formValues: Record<string, string>): string {
    return template.replace(/\$\{form\.([A-Za-z_$][A-Za-z0-9_$-]*)\}/g,
      (_m, name: string) => formValues[name] ?? '');
  }
  /**
   * Aja yksi lifecycle-action tracen alle (WP-DT2). Kaikki kortit kulkevat
   * tästä — nappien on-click, lomakkeen on-success/on-api-fail, rivitoiminnot
   * ja App-tason on-load — joten yksi kirjauskohta riittää.
   */
  function runLifecycleAction(
    action: LifecycleActionEntry,
    button: HTMLElement | null,
    extra?: ExprState,
  ): void {
    const event = tracer.record(
      'lifecycle-action',
      button?.getAttribute('data-app-id') || 'app',
      (action.targets ?? []).join(','),
      { operation: action.operation },
    );
    if (!event) {
      applyLifecycleAction(action, button, extra);
      return;
    }
    tracer.withParent(event.id, () => applyLifecycleAction(action, button, extra));
  }
  /**
   * Kortin lausekkeiden evaluointitila (WP-KH3). Rivitoiminnon kortti saa
   * klikatun rivin nimellä `row` — sama nimi kuin repeaterilla, jotta
   * kirjoittajalla on yksi sanasto. Muut kortit (nappi, lomake, on-load) eivät
   * anna lisätilaa ja näkevät vain globaalin storen: `row` ei vuoda niihin.
   * Sama yksi rivi kuin AppRuntime.evalExpressionissä.
   */
  const actionState = (extra?: ExprState): ExprState =>
    extra ? { ...store.state, ...extra } : store.state;
  function applyLifecycleAction(
    action: LifecycleActionEntry,
    button: HTMLElement | null,
    extra?: ExprState,
  ): void {
    const targets = (action.targets ?? [])
      .map((t) => byAppId(t))
      .filter((el): el is HTMLElement => el != null);
    switch (action.operation) {
      case 'show':
      case 'hide':
      case 'toggle':
        for (const el of targets) {
          const isModal = el.getAttribute('data-app-modal') === '1';
          const currentlyVisible = el.style.display !== 'none';
          const next = action.operation === 'show' ? true
            : action.operation === 'hide' ? false : !currentlyVisible;
          if (isModal) {
            setModalOpen(el, next);
          } else {
            setVisible(el, next);
            const id = el.getAttribute('data-app-id');
            if (id) store.setSlot(id, 'isVisible', next);
          }
        }
        return;
      case 'set-text':
        for (const el of targets) el.textContent = action.textValue ?? '';
        return;
      case 'switch-tab':
        for (const el of targets) {
          if (el.classList.contains('sp-tabs')) activateTab(el, action.tabIndex ?? 0);
        }
        return;
      case 'show-toast': {
        // App-tason on-load-actionilla ei ole nappia eikä siis ympäröivää
        // lomaketta — placeholderit jäävät silloin korvaamatta (WP-ABP6).
        const formEl = button?.closest('.sp-form') ?? null;
        const formId = formEl?.getAttribute('data-app-id');
        const formValues = (formId ? store.getSlot(formId, 'values') : {}) as Record<string, string> || {};
        // WP-VV4: toastMessageExpression voittaa toastMessagen, kuten
        // linkUrlExpression linkUrlin (D9 = A: lauseke erillisessä kentässä, ei
        // ${...}-templatointia). EROAA linkistä tyhjän tuloksen kohdalla (D2 = B):
        // tyhjä tulos putoaa toastMessageen, koska tekstitön toast on huonompi
        // kuin geneerinen — "palvelimen viesti tai geneerinen" on yksi kortti.
        // Teksti menee textContentiin, joten lauseke ei voi tuottaa markupia;
        // pituus rajataan samaan kattoon kuin palvelimen virheviesti (D3).
        const expr = typeof action.toastMessageExpression === 'string'
          ? action.toastMessageExpression.trim() : '';
        const evaluated = expr ? evalExpression(expr, actionState(extra)) : undefined;
        const fromExpr = evaluated == null ? '' : String(evaluated).trim();
        const message = fromExpr
          ? truncateMessage(fromExpr)
          : substitutePlaceholders(action.toastMessage ?? '', formValues);
        showToast(message, action.toastSeverity ?? 'info', action.toastDuration ?? 4000);
        return;
      }
      case 'navigate': {
        let target = action.navigateTarget ?? '';
        if (!target) return;
        if (!target.startsWith('/')) target = '/' + target;
        try {
          if (action.navigateMode === 'replace') history.replaceState({}, '', target);
          else history.pushState({}, '', target);
          // pushState ei laukaise popstatea → params on synkattava käsin.
          // Ilman tätä osoite vaihtuisi mutta näkymä ei (WP-ABP7).
          syncParams();
        } catch {
          // history estetty (esim. sandbox-iframe ilman lupaa) — no-op.
        }
        return;
      }
      case 'open-link': {
        // WP-AR5 (D8 = B): avaus kulkee sillan yli parentille, koska Appin
        // iframe on sandboxattu ilman allow-popupsia eikä window.open toimi
        // siellä. Ilman siltaa (kehystämätön esikatselu) tämä on no-op — sama
        // kuin datalähteillä.
        // linkUrlExpression (WP-KL2, löydös E-5): rivikohtainen osoite ei ollut
        // ilmaistavissa lainkaan, koska linkUrl luetaan kiinteänä merkkijonona.
        // Erillinen kenttä eikä taikamerkki linkUrlin sisällä (D9 = A): alusta
        // hankkiutui juuri eroon ${...}-templatoinnista (E-7), eikä toista
        // rinnakkaista syntaksia oteta takaisin.
        //
        // Sanitointi (sanitizeUrl, vain http(s)/mailto/tel/suhteelliset) tehdään
        // parentissa vastaanotettaessa, siis AINA evaluoinnin jälkeen — lauseke ei
        // voi ohittaa skeema-allowlistia.
        const expr = typeof action.linkUrlExpression === 'string'
          ? action.linkUrlExpression.trim() : '';
        const evaluated = expr ? evalExpression(expr, actionState(extra)) : undefined;
        const url = expr
          ? (evaluated == null ? '' : String(evaluated))
          : (action.linkUrl ?? '');
        if (!url || !bridge) return;
        bridge.openLink(url, action.linkTarget === 'same' ? 'same' : 'new');
        return;
      }
      case 'open-document':
      case 'download-document': {
        // WP-KU3 (D1 = A, D2 = B): Appin CSP estää kehykset ja sandbox estää
        // lataukset, joten kumpikaan ei ole tehtävissä täällä. Nimetään vain
        // datalähde ja hyötykuorma; parent hakee tavut ja näyttää/tallentaa.
        //
        // documentPayloadExpression on lauseke eikä kiinteä runko, koska kohde
        // on lähes aina rivikohtainen ("näytä VALITUN kuitin tiedosto"). Sama
        // ratkaisu ja sama syy kuin linkUrlExpressionilla — eikä ${...}-
        // templatointia oteta takaisin (löydös E-7).
        const sourceId = typeof action.documentSourceId === 'string'
          ? action.documentSourceId.trim() : '';
        if (!sourceId || !bridge) return;
        const payloadExpr = typeof action.documentPayloadExpression === 'string'
          ? action.documentPayloadExpression.trim() : '';
        const payload = payloadExpr ? evalExpression(payloadExpr, actionState(extra)) : undefined;
        if (action.operation === 'download-document') {
          bridge.downloadDocument(sourceId, payload);
        } else {
          bridge.openDocument(sourceId, payload);
        }
        return;
      }
      case 'notify-embedder': {
        // upotussivun-tapahtumaviesti-plan.md WP-UV2: tapahtuma upottavalle
        // sivulle (asiakkaan Tag Manager). Lähetyspäätös on sillan (vain
        // serving + kehystetty, D1/D4 = A); tässä rakennetaan ja tarkistetaan
        // viesti. Lauseke (D2 = B) evaluoidaan kortin omassa tilassa, joten
        // lomakkeen on-success-kortti näkee `lomake.values`in ja vastauksen.
        // Rike → koko viesti jää lähtemättä ja syy konsoliin (myös
        // esikatselussa, jossa tekijä sen näkee).
        if (!bridge) return;
        const expr = typeof action.messageDataExpression === 'string'
          ? action.messageDataExpression.trim() : '';
        const built = buildEmbedderMessage(
          action.messageType,
          action.messageData,
          expr ? { value: evalExpression(expr, actionState(extra)) } : undefined,
        );
        if (!built.ok) {
          console.warn('[app-runtime] notify-embedder: viestiä ei lähetetty —', built.reason);
          return;
        }
        bridge.notifyEmbedder(built.message);
        return;
      }
      case 'refresh':
        // Tuntematon id on no-op (kuten tuntematon operaatiokin): poistettu
        // taulukko ei saa kaataa napin muita actioneita.
        for (const targetId of action.targets ?? []) reloaders.get(targetId)?.();
        return;
      case 'set-variable': {
        if (!action.variableName) return;
        const value = action.valueSource === 'expression'
          ? evalExpression(action.valueExpression ?? '', actionState(extra))
          : action.literalValue;
        setVariable(action.variableName, value);
        return;
      }
      default:
        // Tuntematon operaatio — ohita (eteenpäin-yhteensopivuus).
    }
  }
  /**
   * Aja annetut lifecycle-actionit yhdellä ajoituksella.
   *
   * <p>Erotettu attribuuttiluvusta WP-HV3:ssa: napin kortit tulevat DOMista,
   * mutta dynamic-tablen RIVITOIMINNON kortit tulevat JSON-configista, koska
   * runtime renderöi rivinapin itse. Ilman jakoa rivitoiminto olisi joutunut
   * joko kopioimaan tämän silmukan tai kirjoittamaan kortit takaisin DOMiin
   * vain lukeakseen ne heti uudelleen.
   */
  function runLifecycleActions(
    actions: LifecycleActionEntry[],
    host: HTMLElement | null,
    timing: string,
    extra?: ExprState,
  ): void {
    if (!Array.isArray(actions)) return;
    for (const action of actions) {
      if ((action?.timing ?? 'on-click') === timing) {
        runLifecycleAction(action, host, extra);
      }
    }
  }
  // Aja napin lifecycle-actionit annetulla ajoituksella. on-click laukeaa
  // klikistä; on-success/on-api-fail laukeavat trigger-api-vastauksesta alla.
  function runLifecycleActionsByTiming(button: HTMLElement, timing: string): void {
    let actions: LifecycleActionEntry[];
    try {
      actions = JSON.parse(button.getAttribute('data-app-lifecycle-actions') || '[]');
    } catch {
      return;
    }
    runLifecycleActions(actions, button, timing);
  }
  /**
   * Nappien kytkentä yhdelle alipuulle (lifecycle, trigger-api, submit-form).
   * Erotettu funktioksi WP-HV5:ssä: `repeater`in rivillä voi olla nappi, ja
   * bootin jälkeen syntynyt nappi jäisi muuten kokonaan kuolleeksi.
   */
  const wireActionButtons = (root: ParentNode): void => {
  root.querySelectorAll<HTMLElement>('button[data-app-lifecycle-actions]').forEach((button) => {
    button.addEventListener('click', () => runLifecycleActionsByTiming(button, 'on-click'));
  });

  // --- Button trigger-api (AB-WP4.1) ---
  // Erillään lifecycle-actioneista: data-app-action="trigger-api" kutsuu
  // deklaroitua datalähdettä (data-app-api-id) sillan kautta.
  root.querySelectorAll<HTMLElement>('button[data-app-action="trigger-api"]').forEach((button) => {
    initResponseSlots(store, button.getAttribute('data-app-id'));
    button.addEventListener('click', () => {
      const sourceId = button.getAttribute('data-app-api-id') || '';
      if (!bridge || !sourceId) return;
      // Kaksoiskutsun esto (WP-UV8, D6 = B): kesken kutsun ei uutta kutsua, ja
      // nappi on pois käytöstä kuten rivitoiminnossa. Disabloitu nappi ei saa
      // klikkiä lainkaan; attribuuttitarkistus kattaa ohjelmallisen polun.
      if (button.hasAttribute('data-app-busy')) return;
      const payload = buttonPayload(button, store);
      const buttonId = button.getAttribute('data-app-id');
      const control = button as HTMLButtonElement;
      const wasDisabled = control.disabled;
      button.setAttribute('data-app-busy', '1');
      control.disabled = true;
      const done = (): void => {
        button.removeAttribute('data-app-busy');
        if (!wasDisabled) control.disabled = false;
      };
      // Vastauksen ajoitukset: 2xx → on-success, muu/virhe → on-api-fail.
      // Tämä kytkee esim. "näytä success-toast tallennuksen jälkeen".
      // Vastausslotit julkaistaan ENNEN kortteja, jotta on-api-fail-kortin
      // lauseke näkee tämän kutsun virheen eikä edellisen (WP-VV2).
      bridge.invoke(sourceId, payload).then(
        (res) => {
          done();
          const outcome = describeResponse(res);
          publishResponseSlots(store, buttonId, outcome);
          runLifecycleActionsByTiming(button, outcome.ok ? 'on-success' : 'on-api-fail');
        },
        () => {
          done();
          publishResponseSlots(store, buttonId, describeResponse(null));
          runLifecycleActionsByTiming(button, 'on-api-fail');
        },
      );
    });
  });

  // --- Button submit-form (B1) ---
  // Lähettää lähimmän ylälomakkeen synteettisellä submit-eventillä, jonka
  // form-käsittelijä (yllä) validoi ja POSTaa sillan kautta — nappi itse
  // pysyy type="button"ina. Lomakkeen ulkopuolella nappi no-oppaa.
  root.querySelectorAll<HTMLElement>('button[data-app-action="submit-form"]').forEach((button) => {
    button.addEventListener('click', () => {
      const form = button.closest('form');
      if (form) submitFormProgrammatically(form);
    });
  });
  };
  wireActionButtons(root);

  // --- Kello (WP-HV4, päätös D4 = C) ---
  root.querySelectorAll<HTMLElement>('.sp-clock[data-app-id]').forEach((clock) => {
    wireClock(clock, store);
  });

  // --- Käyttäjätoimet (WP-UA4) ---
  // Ainoa komponentti jonka sisältö riippuu KATSOJASTA: data tulee sillan
  // omasta operaatiosta (ei Appin datalähteestä), joten sama julkaistu App
  // näyttää kahdelle käyttäjälle eri listan. Reload menee samaan rekisteriin
  // kuin taulukoilla, jotta refresh-lifecycle yltää myös tähän.
  root.querySelectorAll<HTMLElement>('.sp-user-actions[data-app-id]').forEach((host) => {
    const reload = wireUserActions(host, bridge, store,
      (actions, target, timing) =>
        runLifecycleActions(actions as LifecycleActionEntry[], target, timing));
    const hostId = host.getAttribute('data-app-id');
    if (hostId && reload) reloaders.set(hostId, reload);
  });

  // --- Repeater: toistuva kenttäryhmä (WP-HV5, päätös D6 = A) ---
  root.querySelectorAll<HTMLElement>('.sp-repeater[data-app-id]').forEach((host) => {
    wireRepeater(host, {
      doc,
      store,
      wireFormFields,
      registerBindings,
      wireActionButtons,
    });
  });

  // --- Dynamic-table mount-fetch (AB-WP4.1) ---
  // Reload-rekisteri (WP-ABP3): haku oli tähän asti vain komponentin oma
  // sisäinen asia (ajastin / rivitoiminnon 2xx), joten lifecycle-actionilla
  // ei ollut mitään mihin tarttua. Nyt jokainen hakeva komponentti jättää
  // reload-funktionsa tähän, ja refresh-operaatio hakee sen id:llä.
  root.querySelectorAll<HTMLElement>('.sp-dynamic-table[data-app-api-id]').forEach((table) => {
    // WP-HV3: rivitoiminnon lifecycle-kortit ajetaan boot-sulkeuman ajurilla,
    // joka näkee reloaders-rekisterin ja muuttujat — ilman sitä rivitoiminto ei
    // yltäisi mihinkään oman taulunsa ulkopuolelle.
    const reload = wireTableFetch(table, bridge, store,
      (actions, host, timing, extra) =>
        runLifecycleActions(actions as LifecycleActionEntry[], host, timing, extra));
    const tableId = table.getAttribute('data-app-id');
    if (tableId && reload) reloaders.set(tableId, reload);
  });

  // --- Chart (AB-WP6.1) ---
  // Inline-data-chart piirtyy heti; data-app-api-id-chart hakee rivit sillan
  // kautta (sama mount-fetch-malli kuin dynamic-table) ja piirtää uudelleen.
  root.querySelectorAll<HTMLElement>('.sp-chart').forEach((chart) => {
    const reload = wireChart(chart, bridge, store);
    const chartId = chart.getAttribute('data-app-id');
    if (chartId && reload) reloaders.set(chartId, reload);
  });

  // --- Rivillä sallitut komponentit (WP-RP2): image, progress, stat, avatar,
  //     date range, file upload, multi-select, radio group. Repeaterin rivien
  //     kopiot kytkee repeater itse (ks. wireRowComponents), joten bootti ohittaa ne.
  wireRowComponents(root, store, null);

  // --- Pagination (AB-WP6.4) ---
  root.querySelectorAll<HTMLElement>('.sp-pagination').forEach((pag) => {
    wirePagination(pag, store);
  });

  // --- Data grid (AB-WP6.13) — bindingit alustettu yllä (bound data/columns
  //     jo storessa). ---
  root.querySelectorAll<HTMLElement>('.sp-data-grid').forEach((grid) => {
    wireDataGrid(grid, store, bridge);
  });

  // --- Kanban (AB-WP6.14) ---
  root.querySelectorAll<HTMLElement>('.sp-kanban').forEach((kanban) => {
    // WP-KH2: siirron lifecycle-kortit ajetaan boot-sulkeuman ajurilla, kuten
    // rivitoiminnon kortit — ilman sitä siirto ei yltäisi boardin ulkopuolelle.
    wireKanban(kanban, store, bridge, (msg, sev) => showToast(msg, sev, 4000),
      (actions, host, timing, extra) =>
        runLifecycleActions(actions as LifecycleActionEntry[], host, timing, extra));
  });

  // --- Gantt (AB-WP6.15) ---
  root.querySelectorAll<HTMLElement>('.sp-gantt').forEach((gantt) => {
    wireGantt(gantt, store);
  });

  // --- Stepper (AB-WP6.11) — bindingit alustettu yllä, joten bound
  //     currentStepIndex/canAdvance ovat jo storessa. ---
  root.querySelectorAll<HTMLElement>('.sp-stepper').forEach((stepper) => {
    wireStepper(stepper, store, bridge);
  });

  // --- App-tason on-load-actionit (WP-ABP6, päätös D4 = A) ---
  // Ajetaan bootin VIIMEISENÄ: sidonnat on ensievaluoitu ja kaikki
  // komponentit kytketty, joten action näkee valmiin tilan. Erityisesti
  // refresh vaatii tämän — reload-rekisteri täyttyy vasta taulukoiden
  // kytkennässä, joka on tätä ennen.
  //
  // "Appin käynnistys" ei kuulu millekään yksittäiselle solmulle, joten
  // kortit asuvat app-tasolla metadata.onLoadissa eivätkä napin propeissa
  // (päätös D4 = A). Siksi runLifecycleAction saa napikseen nullin.
  const onLoadActions = window.fasthub?.appOnLoad;
  if (Array.isArray(onLoadActions)) {
    for (const action of onLoadActions as LifecycleActionEntry[]) {
      if (!action || typeof action !== 'object') continue;
      try {
        runLifecycleAction(action, null);
      } catch {
        // Yksi rikkinäinen kortti ei saa estää muita eikä bootin loppua.
      }
    }
  }

  // Käsittelemättömät virheet traceen (WP-DT2): ilman näitä kaatunut
  // käsittelijä katkaisee ketjun paneelissa ilman jälkeä siitä miksi.
  window.addEventListener('error', (e) => {
    tracer.record('error', 'page', '', { isRoot: true, message: e.message });
  });
  window.addEventListener('unhandledrejection', (e) => {
    const reason: unknown = (e as PromiseRejectionEvent).reason;
    tracer.record('error', 'page', '', {
      isRoot: true,
      message: reason instanceof Error ? reason.message : String(reason),
    });
  });

  // Mount-ketju päättyy tähän: bootin jälkeen kirjatut eventit ovat omia
  // ketjujaan (klikki, lomake, ajastin).
  tracer.setCurrentParent(null);

  const runtime: AppRuntime = {
    version: __APP_RUNTIME_VERSION__,
    store,
    trace: tracer,
    evalExpression: (src, extraState) =>
      evalExpression(src, extraState ? { ...store.state, ...extraState } : store.state),
  };
  return runtime;
}

/**
 * Kello (WP-HV4, päätös D4 = C): julkaisee `now`- ja `today`-slotit valitulla
 * välillä ja kirjoittaa halutun tekstin.
 *
 * <p>Tämä on ainoa tikittävä komponentti. Lausekekielen `now()` evaluoituu vain
 * silloin kun sidonta muutenkin evaluoituu, mikä riittää "onko myöhässä"
 * -vertailuun mutta ei käynnissä olevaan kelloon. Automaattinen tikitys
 * jokaiselle `now()`-sidonnalle olisi tehnyt niistä kaikista ikuisia
 * ajastimia — tämä tekee tikityksestä näkyvän valinnan.
 *
 * <p>Palvelin ei renderöi kellonaikaa lainkaan: se olisi väärä siihen mennessä
 * kun sivu on selaimessa, ja väärä kello on huonompi kuin tyhjä.
 */
function wireClock(host: HTMLElement, store: Store): void {
  const id = host.getAttribute('data-app-id') || '';
  if (!id) return;
  const text = host.querySelector<HTMLElement>('.sp-clock-text');
  const display = host.getAttribute('data-app-clock-display') || 'time';
  const interval = Math.min(
    3_600_000,
    Math.max(1000, parseInt(host.getAttribute('data-app-clock-interval') || '1000', 10) || 1000),
  );

  const tick = (): void => {
    const nowIso = nowLocal();
    const todayIso = todayLocal();
    store.setSlot(id, 'now', nowIso);
    store.setSlot(id, 'today', todayIso);
    if (!text) return;
    // nowLocal() on `YYYY-MM-DDTHH:mm:ss`, joten osat leikataan siitä.
    const timePart = nowIso.slice(11, 19);
    switch (display) {
      case 'none': text.textContent = ''; break;
      case 'date': text.textContent = todayIso; break;
      case 'datetime': text.textContent = `${todayIso} ${timePart}`; break;
      default: text.textContent = timePart;
    }
  };

  store.setSlot(id, 'isVisible', host.style.display !== 'none');
  tick();
  setInterval(tick, interval);
}

/**
 * Kytke yksi `user-actions` (WP-UA4): hae katsojalle osoitetut käyttäjätoimet,
 * piirrä kortit ja lähetä vastaus sillan yli.
 *
 * <p>Palautusarvo on reload-funktio (tai null jos kytkentä ei onnistunut), joka
 * menee reload-rekisteriin kuten taulukoilla — silloin lifecycle-actionin
 * `refresh` yltää tähänkin.
 *
 * <p>Kortit piirretään DOM-APIlla eikä innerHTML-merkkijonoina: sisältö tulee
 * palvelimelta (viesti, konteksti, vaihtoehdot) ja se voi sisältää mitä
 * tahansa merkkejä. `textContent` sulkee injektion rakenteellisesti, eikä
 * escapointia tarvitse muistaa joka kohdassa.
 */
function wireUserActions(
  host: HTMLElement,
  bridge: BridgeClient | null,
  store: Store,
  runLifecycle: RowLifecycleRunner,
): (() => void) | null {
  const doc = host.ownerDocument;
  const list = host.querySelector<HTMLElement>('.sp-ua-list');
  if (!list) return null;

  const hostId = host.getAttribute('data-app-id') || '';
  const queue = host.getAttribute('data-app-ua-queue') || '';
  const showResolved = host.getAttribute('data-app-ua-resolved') === 'true';
  const refreshSec = parseInt(host.getAttribute('data-app-ua-refresh') || '0', 10) || 0;
  const maxItems = Math.max(1, parseInt(host.getAttribute('data-app-ua-max') || '20', 10) || 20);
  const emptyAttr = host.getAttribute('data-app-empty') || '';

  let lifecycleActions: unknown[] = [];
  const lifecycleScript = host.querySelector('script.sp-app-ua-lifecycle');
  if (lifecycleScript) {
    try {
      const parsed = JSON.parse(lifecycleScript.textContent || '[]');
      if (Array.isArray(parsed)) lifecycleActions = parsed;
    } catch {
      // rikkinäinen kortti-config — vastaaminen toimii silti
    }
  }

  const publish = (slot: string, value: unknown): void => {
    if (hostId) store.setSlot(hostId, slot, value);
  };
  publish('isLoading', false);
  publish('error', '');
  publish('pendingCount', 0);
  publish('lastRespondedId', '');

  const emptyText = (): string => {
    // Sidottu tyhjän tilan teksti tulee slotista (palvelimen attribuutti on
    // vain alkuarvo, jota sidonta ei koskaan päivittäisi); tyhjä = runtimen
    // sisäänrakennettu lokalisoitu teksti.
    const bound = hostId ? store.getSlot(hostId, 'emptyMessage') : undefined;
    const value = typeof bound === 'string' && bound ? bound : emptyAttr;
    return value || resolveMessage('userActionEmpty');
  };

  const status = (text: string, cls = 'sp-ua-status'): void => {
    list.replaceChildren();
    const el = doc.createElement('div');
    el.className = cls;
    el.textContent = text;
    list.appendChild(el);
  };

  const render = (actions: UserActionItem[]): void => {
    list.replaceChildren();
    if (actions.length === 0) {
      status(emptyText());
      return;
    }
    for (const action of actions) {
      list.appendChild(buildUserActionCard(doc, action, submit));
    }
  };

  let latest: UserActionItem[] = [];

  const load = (): void => {
    if (!bridge) return;
    publish('isLoading', true);
    bridge.listUserActions(queue || undefined, showResolved).then(
      (raw) => {
        publish('isLoading', false);
        publish('error', '');
        latest = parseUserActions(raw).slice(0, maxItems);
        publish('data', latest);
        publish('pendingCount', latest.filter(isPending).length);
        render(latest);
      },
      (err: Error) => {
        publish('isLoading', false);
        // Identiteetitön konteksti (julkinen standalone-serving) ei ole virhe
        // vaan puuttuva kirjautuminen — ohje on käyttäjälle hyödyllisempi kuin
        // virheilmoitus, ja tämä on ainoa kohta joka tietää eron.
        const noIdentity = err?.message === NO_IDENTITY;
        publish('error', noIdentity ? '' : (err?.message || resolveMessage('userActionFailed')));
        status(noIdentity ? resolveMessage('userActionSignIn') : resolveMessage('userActionFailed'));
      },
    );
  };

  function submit(action: UserActionItem, answer: UserActionAnswer, card: HTMLElement): void {
    if (!bridge) return;
    runLifecycle(lifecycleActions, host, 'on-click');
    const button = card.querySelector<HTMLButtonElement>('.sp-ua-respond');
    if (button) button.disabled = true;
    bridge.respondUserAction(action.id, answer).then(
      () => {
        publish('lastRespondedId', action.id);
        runLifecycle(lifecycleActions, host, 'on-success');
        load();
      },
      (err: Error) => {
        if (button) button.disabled = false;
        showCardError(card, err?.message || resolveMessage('userActionFailed'));
        runLifecycle(lifecycleActions, host, 'on-api-fail');
        // Konfliktin (joku ehti vastata ensin) jälkeen lista on vanhentunut,
        // joten se haetaan uudelleen — muuten kortti jäisi näkyviin vaikka
        // toimi on jo ratkaistu.
        load();
      },
    );
  }

  load();
  if (refreshSec > 0) {
    setInterval(load, refreshSec * 1000);
  }
  return load;
}

/** Yksi kortti: otsikko, viesti, konteksti, vaihtoehdot, kommentti ja nappi. */
function buildUserActionCard(
  doc: Document,
  action: UserActionItem,
  submit: (action: UserActionItem, answer: UserActionAnswer, card: HTMLElement) => void,
): HTMLElement {
  const card = doc.createElement('div');
  card.className = 'sp-ua-card';
  card.setAttribute('data-app-ua-id', action.id);

  const head = doc.createElement('div');
  head.className = 'sp-ua-card-head';
  const title = doc.createElement('span');
  title.className = 'sp-ua-card-title';
  title.textContent = action.title;
  head.appendChild(title);
  const deadline = formatDeadline(action.expiresAt);
  if (deadline) {
    const due = doc.createElement('span');
    const overdue = isOverdue(action.expiresAt);
    due.className = overdue ? 'sp-ua-due sp-ua-due--overdue' : 'sp-ua-due';
    due.textContent = overdue
      ? resolveMessage('userActionOverdue')
      : resolveMessage('userActionDue', deadline);
    head.appendChild(due);
  }
  card.appendChild(head);

  if (action.message && action.message !== action.title) {
    const message = doc.createElement('p');
    message.className = 'sp-ua-message';
    message.textContent = action.message;
    card.appendChild(message);
  }

  if (action.context.length > 0) {
    const context = doc.createElement('div');
    context.className = 'sp-ua-context';
    for (const row of action.context) {
      const label = doc.createElement('span');
      label.className = 'sp-ua-context-label';
      label.textContent = row.label;
      const value = doc.createElement('span');
      value.className = 'sp-ua-context-value';
      value.textContent = row.value;
      context.appendChild(label);
      context.appendChild(value);
    }
    card.appendChild(context);
  }

  if (action.continuation) {
    // Odottava prosessi (user-action:await): kerro että reitti jatkaa vastauksesta,
    // ja punaisena jos jatkoa ei voitu ajaa (ORPHANED/FAILED) — se on ihmisen tehtävä.
    const failed = action.continuation.status === 'ORPHANED' || action.continuation.status === 'FAILED';
    const cont = doc.createElement('div');
    cont.className = failed ? 'sp-ua-continuation sp-ua-continuation--failed' : 'sp-ua-continuation';
    cont.setAttribute('data-app-ua-continuation', action.continuation.status);
    cont.textContent = failed
      ? resolveMessage('userActionContinuationFailed', action.continuation.detail || action.continuation.status)
      : resolveMessage('userActionWaiting', action.continuation.routeId);
    card.appendChild(cont);
  }

  if (!isPending(action)) {
    // Ratkaistu toimi on kuitti, ei lomake: kontrollit jätetään pois kokonaan
    // eikä vain estetä — estetty nappi lupaa että jotain voisi vielä tapahtua.
    const answered = doc.createElement('div');
    answered.className = 'sp-ua-answered';
    answered.textContent = `${resolveMessage('userActionAnswered')}: ${action.answer || '-'}`
      + (action.answeredBy ? ` (${action.answeredBy})` : '');
    card.appendChild(answered);
    return card;
  }

  const inputName = `ua-${action.id}`;
  const options = doc.createElement('div');
  options.className = 'sp-ua-options';
  for (const option of action.options) {
    const wrapper = doc.createElement('label');
    wrapper.className = 'sp-ua-option';
    const input = doc.createElement('input');
    input.type = action.optionType === 'multiple' ? 'checkbox' : 'radio';
    input.name = inputName;
    input.value = option.key;
    const text = doc.createElement('span');
    text.textContent = option.label;
    wrapper.appendChild(input);
    wrapper.appendChild(text);
    options.appendChild(wrapper);
  }
  if (action.options.length > 0) card.appendChild(options);

  let comment: HTMLTextAreaElement | null = null;
  if (action.allowComment) {
    comment = doc.createElement('textarea');
    comment.className = 'sp-input sp-ua-comment';
    comment.rows = 2;
    comment.placeholder = resolveMessage('userActionComment');
    card.appendChild(comment);
  }

  const actions = doc.createElement('div');
  actions.className = 'sp-ua-actions';
  const button = doc.createElement('button');
  button.type = 'button';
  button.className = 'sp-button sp-button--primary sp-ua-respond';
  button.textContent = resolveMessage('userActionRespond');
  button.addEventListener('click', () => {
    const selected = Array.from(options.querySelectorAll<HTMLInputElement>('input'))
      .filter((input) => input.checked)
      .map((input) => input.value);
    const built = buildAnswer(action, selected, comment ? comment.value : '');
    if ('error' in built) {
      showCardError(card, resolveMessage(
        built.error === 'choice' ? 'userActionChoiceRequired' : 'required',
      ));
      return;
    }
    showCardError(card, '');
    submit(action, built.answer, card);
  });
  actions.appendChild(button);
  card.appendChild(actions);

  const error = doc.createElement('div');
  error.className = 'sp-ua-error';
  error.hidden = true;
  card.appendChild(error);
  return card;
}

/** Näytä (tai piilota) yhden kortin virheteksti. */
function showCardError(card: HTMLElement, text: string): void {
  const error = card.querySelector<HTMLElement>('.sp-ua-error');
  if (!error) return;
  error.textContent = text;
  error.hidden = !text;
}

/**
 * Kytke omalla kytkentäfunktiollaan varustetut komponentit, joita repeaterin rivillä
 * saa olla (WP-RP2, päätös D5 = A). Sama funktio palvelee bootia ja rivejä:
 * `rowOwner` = `null` kytkee vain rivien ULKOPUOLISET elementit, ja rivi kutsuu tätä
 * itsellään, jolloin vain sen omat elementit kytketään. Näin alkurivejä ei kytketä
 * kahdesti (repeater rakentaa ne ennen bootin passeja), eikä uudelleen rakennettu
 * rivi jää kytkemättä.
 *
 * <p>Syötteiden perusjoukko (teksti, numero, checkbox, valinta, tekstialue, haku)
 * kytkeytyy {@code wireFormFields}issa, ja teksti-/esityskomponentit (heading,
 * paragraph, badge, link) sidontojen kautta — niillä ei ole omaa kytkentää.
 */
function wireRowComponents(root: ParentNode, store: Store, rowOwner: HTMLElement | null): void {
  const mine = (el: HTMLElement): boolean => el.closest('.sp-repeater-row') === rowOwner;
  const each = (selector: string, wire: (el: HTMLElement) => void): void => {
    root.querySelectorAll<HTMLElement>(selector).forEach((el) => {
      if (mine(el)) wire(el);
    });
  };
  // Image + Progress (WP-ABP8)
  each('.sp-image', (el) => wireImage(el, store));
  each('[data-app-progress]', (el) => wireProgress(el, store));
  // Stat / KPI (AB-WP6.6), Avatar (AB-WP6.7)
  each('.sp-stat', (el) => wireStat(el, store));
  each('.sp-avatar', (el) => wireAvatar(el, store));
  // Date range (AB-WP6.9), File upload (AB-WP6.10)
  each('.sp-daterange', (el) => wireDateRange(el, store));
  each('.sp-file-upload', (el) => wireFileUpload(el, store));
  // Multi-select (WP-ABP9), Radio group (AB-WP6.12)
  each('.sp-multi', (el) => wireMultiSelect(el, store));
  each('.sp-radio-group', (el) => wireRadioGroup(el, store));
}

/** Yhden id:n slotit storesta (tyhjä jos ei ole) — vain luku, ei luo tilaa. */
function slotsOfState(store: Store, id: string): Record<string, unknown> {
  const slots = store.state[id];
  return slots && typeof slots === 'object' ? slots as Record<string, unknown> : {};
}

/**
 * Kytke yksi `repeater` (WP-HV5, päätös D6 = A).
 *
 * <p>Palvelin renderöi lapset KERRAN inerttiin `<template>`en; tämä kloonaa sen
 * kerran per rivi, skooppaa kloonin id:t (`total` → `total__r2`) ja ajaa
 * kloonille saman kytkennän jonka `boot` ajaa dokumentille. Ilman skooppausta
 * rivin 3 sidonta kirjoittaisi rivin 0 elementtiin, koska `byAppId` palauttaa
 * aina ensimmäisen osuman — ja se vika olisi täysin hiljainen.
 *
 * <p>Kytkentäfunktiot tulevat parametreina, koska ne elävät `boot`in
 * sulkeumassa (store, silta, reloaders). Tämä on ainoa komponentti joka
 * tarvitsee niitä ajossa: kaikki muut markup on olemassa jo bootissa.
 */
function wireRepeater(
  host: HTMLElement,
  deps: {
    doc: Document;
    store: Store;
    wireFormFields: (scope: ParentNode) => void;
    registerBindings: (scope: ParentNode, scopeMap?: ReadonlyMap<string, string>) => void;
    wireActionButtons: (scope: ParentNode) => void;
  },
): void {
  const { doc, store, wireFormFields, registerBindings, wireActionButtons } = deps;
  const id = host.getAttribute('data-app-id') || '';
  const template = host.querySelector<HTMLTemplateElement>('template.sp-repeater-template');
  const rowsBox = host.querySelector<HTMLElement>('[data-app-repeater-rows]');
  if (!id || !template || !rowsBox) return;

  const minRows = parseInt(host.getAttribute('data-app-repeater-min') || '0', 10) || 0;
  const maxRows = parseInt(host.getAttribute('data-app-repeater-max') || '50', 10) || 50;
  const canRemove = host.getAttribute('data-app-repeater-remove') === '1';
  const removeLabel = host.getAttribute('data-app-repeater-remove-label') || resolveMessage('repeaterRemove');
  /** Oliko poistonapin teksti rakentajan oma (vaikuttaa saavutettavaan nimeen). */
  const customRemoveLabel = removeLabel !== resolveMessage('repeaterRemove');
  const emptyText = host.getAttribute('data-app-repeater-empty') || '';
  const horizontal = host.getAttribute('data-app-repeater-direction') === 'horizontal';
  const gap = host.getAttribute('data-app-repeater-gap') || '8px';
  const rowKeyPath = host.getAttribute('data-app-repeater-row-key') || '';
  const addBtn = host.querySelector<HTMLButtonElement>('[data-app-repeater-add-btn]');

  /** Alkurivit: literaali items-lohko, täydennettynä minRowsiin asti. */
  const initialItems = (): Record<string, unknown>[] => {
    const script = host.querySelector('script.sp-repeater-items');
    if (!script) return [];
    try {
      return parseRepeaterRows(JSON.parse(script.textContent || '[]'));
    } catch {
      return [];
    }
  };

  let rows: Record<string, unknown>[] = padToMinRows(initialItems(), minRows);

  /**
   * Ilmoita ympäröivälle lomakkeelle. Formin keräys on delegoitu ja lukee
   * kentät vasta ajohetkellä, joten yksi kupliva eventti riittää — rivejä ei
   * tarvitse rekisteröidä lomakkeeseen erikseen.
   *
   * <p>`dirty` erottaa käyttäjän teon datan saapumisesta: rivin lisäys ja
   * poisto ovat muokkauksia, mutta ensimmäinen renderöinti ja sidotun
   * `items`-taulukon saapuminen eivät ole. Ilman eroa lomake olisi `isDirty`
   * heti latauduttuaan, ja "onko käyttäjä muuttanut mitään" -sidonnat
   * valehtelisivat.
   */
  const notifyForm = (dirty: boolean): void => {
    host.dispatchEvent(dirty
      ? new Event('input', { bubbles: true })
      : new CustomEvent(REPEATER_ROWS_EVENT, { bubbles: true }));
  };

  /**
   * Rakenna rivi IRRALLAAN ja palauta se skooppimäppäyksensä kanssa.
   * Kytkentä on tarkoituksella erillinen vaihe: sidonnan ensimmäinen
   * evaluointi kirjoittaa DOMiin `byAppId`n kautta, joka kysyy koko
   * dokumentilta — irrallinen rivi ei löydy sieltä, joten kytkentä ennen
   * liittämistä jättäisi rivin sidotut tekstit tyhjiksi. Slotti asettuisi
   * silti, joten vika olisi näkymätön storesta katsottuna.
   */
  const buildRow = (
    data: Record<string, unknown>,
    index: number,
  ): { rowEl: HTMLElement; scopeMap: Map<string, string> } => {
    const rowEl = doc.createElement('div');
    rowEl.className = horizontal ? 'sp-repeater-row sp-repeater-row--horizontal' : 'sp-repeater-row';
    rowEl.setAttribute('data-app-row-index', String(index));
    rowEl.setAttribute('data-app-row-key', rowKeyOf(data, rowKeyPath, index));

    const body = doc.createElement('div');
    body.className = 'sp-repeater-row-body';
    body.style.display = 'flex';
    body.style.flexDirection = horizontal ? 'row' : 'column';
    body.style.gap = gap;
    // Vaakarivi tasataan YLÄREUNASTA: otsikot samalle viivalle ja syötteet niiden alle.
    // Alareunatasauksessa (ennen) kentän alle ilmestyvä virhe tai vihje nosti sen
    // syötteen ja laski muita — rivi hyppi joka validoinnilla (löydös 2026-09-27).
    body.style.alignItems = horizontal ? 'flex-start' : 'stretch';
    body.appendChild(template.content.cloneNode(true));

    // Rividata storeen ENNEN sidontojen rekisteröintiä, jotta ensimmäinen
    // evaluointi näkee sen — muuten rivin otsikko välähtäisi tyhjänä.
    const stateId = rowStateId(id, index);
    // WP-RP1: indeksi kierrätetään poistossa, joten edellisen samalla indeksillä
    // olleen rivin avaimet on pyyhittävä — muuten `${row.x}` näyttäisi poistetun
    // rivin arvon rivillä, jonka omassa datassa x:ää ei ole.
    for (const k of Object.keys(slotsOfState(store, stateId))) {
      if (!(k in data) && k !== 'index') store.setSlot(stateId, k, undefined);
    }
    for (const [k, v] of Object.entries(data)) store.setSlot(stateId, k, v);
    store.setSlot(stateId, 'index', index);

    const scopeMap = applyRowScope(body, id, index);
    presetRowInputs(body, data);
    rowEl.appendChild(body);

    if (canRemove) {
      const remove = doc.createElement('button');
      remove.type = 'button';
      remove.className = 'sp-repeater-row-remove';
      remove.textContent = removeLabel;
      // WP-RP5: jokaisella poistonapilla oli sama saavutettava nimi, joten
      // ruudunlukijalla ei voinut tietää mikä rivi lähtee.
      remove.setAttribute('aria-label', customRemoveLabel
        ? `${removeLabel} ${index + 1}`
        : resolveMessage('repeaterRemoveRow', index + 1));
      remove.addEventListener('click', () => {
        edited = true;
        harvest();
        rows = padToMinRows(rows.filter((_, i) => i !== index), minRows);
        render();
        notifyForm(true);
      });
      rowEl.appendChild(remove);
    }
    return { rowEl, scopeMap };
  };

  /** Kytkentä liitetylle riville: sama kolmikko jonka boot ajaa dokumentille. */
  const wireRow = (rowEl: HTMLElement, scopeMap: Map<string, string>): void => {
    wireFormFields(rowEl);
    registerBindings(rowEl, scopeMap);
    wireActionButtons(rowEl);
    // WP-RP2: ilman tätä radioryhmä, monivalinta, tiedostolataus ym. toimivat vain
    // alkuriveillä (bootin passi) ja lakkasivat toimimasta ensimmäisessä lisäyksessä,
    // kun kaikki rivit rakennettiin uudelleen.
    wireRowComponents(rowEl, store, rowEl);
  };

  /**
   * Rivin kentän muutos rivin dataan (WP-RP1, D1 = A). Rivit rakennetaan
   * uudelleen jokaisessa lisäyksessä ja poistossa, ja ne esitäytetään `rows`ista —
   * ennen tätä käyttäjän kirjoittamat arvot eivät koskaan päätyneet sinne, joten
   * "Lisää rivi" tyhjensi kaikki rivit. Samalla rivin tila päivittyy, jolloin
   * `${row.nimi}` näyttää kirjoitetun arvon eikä alkuperäistä.
   */
  const syncRow = (rowEl: HTMLElement): void => {
    const index = Number(rowEl.getAttribute('data-app-row-index'));
    if (!Number.isInteger(index) || index < 0 || index >= rows.length) return;
    const current = readRowInputs(rowEl);
    rows[index] = { ...rows[index], ...current };
    const stateId = rowStateId(id, index);
    for (const [k, v] of Object.entries(current)) store.setSlot(stateId, k, v);
    publishValue();
  };
  /**
   * Repeaterin oma arvo (WP-RP4, D3 = A): rivit käyttäjän nykyisillä arvoilla —
   * rividata + syötteiden arvot. Näin repeateria voi käyttää myös lomakkeen
   * ulkopuolella (summa, napin payload, näkyvyysehto). Uusi taulukko joka kerta,
   * jotta tilaajat heräävät (store vertaa identiteettiä).
   */
  function publishValue(): void {
    store.setSlot(id, 'value', rows.map((r) => ({ ...r })));
  }
  /**
   * Onko käyttäjä muokannut rivejä (WP-RP4, D2 = B). Muokattua repeateria ei
   * päivitetä sidotusta datasta — ennen tätä esim. lähdetaulun autoRefresh pyyhki
   * keskeneräiset muokkaukset hiljaa. Nollautuu, kun ympäröivä lomake lähetetään
   * onnistuneesti tai nollataan; silloin viimeisin sidottu data otetaan käyttöön.
   */
  let edited = false;
  /** Kaikkien rivien arvot talteen ennen uudelleenrakennusta. */
  const harvest = (): void => {
    rowsBox.querySelectorAll<HTMLElement>(':scope > .sp-repeater-row').forEach(syncRow);
  };
  const onRowEdit = (e: Event): void => {
    const rowEl = (e.target as HTMLElement | null)?.closest<HTMLElement>('.sp-repeater-row');
    if (rowEl && rowEl.parentElement === rowsBox) {
      edited = true;
      syncRow(rowEl);
    }
  };
  rowsBox.addEventListener('input', onRowEdit);
  rowsBox.addEventListener('change', onRowEdit);

  /** Templaten komponentti-id:t — rivin skoopattujen id:iden pohja siivousta varten. */
  const templateIds = collectScopeIds(template.content);
  let renderedCount = 0;
  /**
   * Poista riveiltä, joita ei enää ole, niiden skoopatut slotit ja rivitila
   * (WP-RP1). Muuten poistetun rivin `kentta__r2` jäisi storeen ja sitä lukeva
   * sidonta näyttäisi arvon, jota ruudulla ei ole.
   */
  const dropStaleRows = (from: number): void => {
    for (let i = from; i < renderedCount; i++) {
      for (const cid of templateIds) delete store.state[scopedId(cid, i)];
      delete store.state[rowStateId(id, i)];
    }
  };

  const render = (): void => {
    dropStaleRows(rows.length);
    renderedCount = rows.length;
    rowsBox.textContent = '';
    rowsBox.style.display = 'flex';
    rowsBox.style.flexDirection = 'column';
    rowsBox.style.gap = gap;
    if (rows.length === 0 && emptyText) {
      const empty = doc.createElement('div');
      empty.className = 'sp-repeater-empty';
      empty.textContent = emptyText;
      rowsBox.appendChild(empty);
    }
    rows.forEach((data, i) => {
      const { rowEl, scopeMap } = buildRow(data, i);
      rowsBox.appendChild(rowEl);
      wireRow(rowEl, scopeMap);
    });
    store.setSlot(id, 'rowCount', rows.length);
    // Rivit luetaan DOMista heti rakennuksen jälkeen: uuden rivin kentät ja pohjan
    // oletusarvot päätyvät riviin, jolloin value on samaa muotoa kuin lomakkeen
    // payload ({hours: null} eikä {}).
    harvest();
    publishValue();
    if (addBtn) addBtn.disabled = rows.length >= maxRows;
  };

  addBtn?.addEventListener('click', () => {
    if (rows.length >= maxRows) return;
    edited = true;
    harvest();
    rows = rows.concat([{}]);
    render();
    notifyForm(true);
  });

  store.setSlot(id, 'isVisible', host.style.display !== 'none');
  render();
  // Lomake laski arvonsa ennen kuin rivejä oli olemassa (form-passi ajetaan
  // ennen tätä), joten sen on laskettava ne uudelleen — muuten `values` jäisi
  // pysyvästi rivittömäksi eikä mikään kertoisi siitä.
  notifyForm(false);

  // Sidottu items: bindings-lohko on jo evaluoitu (registerBindings ajettiin
  // yllä), joten alkuarvo luetaan slotista JA muutokset tilataan. Pelkkä
  // subscribe jäisi sokeaksi ensimmäiselle arvolle.
  const applyItems = (): void => {
    const bound = store.getSlot(id, 'items');
    if (bound === undefined) return;
    // D2 = B: muokattu repeater odottaa; viimeisin arvo luetaan slotista, kun
    // muokkaukset on lähetetty tai lomake nollattu (release).
    if (edited) return;
    rows = padToMinRows(parseRepeaterRows(bound), minRows);
    render();
    notifyForm(false);
  };
  applyItems();
  store.subscribe(`${id}.items`, applyItems);

  const form = host.closest<HTMLElement>('form');
  const formId = form?.getAttribute('data-app-id') || '';
  if (formId) {
    // submitCount kasvaa jokaisesta vastauksesta; lastSubmitStatus asetetaan
    // ennen sitä. (lastSubmitStatus itse ei herättäisi toista peräkkäistä
    // onnistumista, koska arvo ei muutu.)
    store.subscribe(`${formId}.submitCount`, () => {
      if (!edited || store.getSlot(formId, 'lastSubmitStatus') !== 'success') return;
      edited = false;
      applyItems();
    });
  }
  // Natiivi reset palauttaa kenttien oletusarvot VASTA tämän käsittelijän jälkeen,
  // joten uudelleenrakennus mikrotaskissa — muuten reset tyhjentäisi juuri
  // esitäytetyt kentät.
  form?.addEventListener('reset', () => {
    queueMicrotask(() => {
      edited = false;
      const bound = store.getSlot(id, 'items');
      rows = padToMinRows(bound === undefined ? initialItems() : parseRepeaterRows(bound), minRows);
      render();
      notifyForm(false);
    });
  });
}


/**
 * Kytke yksi dynamic-table hakemaan rivinsä sillan kautta ja täyttämään tbody.
 * Statusrivi (data-app-table-status) päivittyy Loading→tyhjä/virhe. autoRefresh
 * (data-app-refresh, sekunteina) uusii haun. Ilman siltaa: status kertoo että
 * esikatselu vaatii mockin / kehystystä.
 */
function wireTableFetch(
  table: HTMLElement,
  bridge: BridgeClient | null,
  store: Store,
  runRowLifecycle: RowLifecycleRunner,
): (() => void) | null {
  const sourceId = table.getAttribute('data-app-api-id') || '';
  const rowKey = table.getAttribute('data-app-row-key') || 'id';
  // Tyhjän tilan teksti on bindable (katalogi: emptyMessage), joten se on
  // luettava slotista eikä attribuutista — attribuutti on vain palvelimen
  // renderöimä alkuarvo eikä sidonta olisi koskaan päivittänyt sitä (F-I).
  const emptyAttr = table.getAttribute('data-app-empty') || 'No data';
  const refreshSec = parseInt(table.getAttribute('data-app-refresh') || '0', 10) || 0;
  const tbody = table.querySelector<HTMLElement>('[data-app-table-body]');
  const status = table.querySelector<HTMLElement>('[data-app-table-status]');
  if (!sourceId || !tbody) return null;

  // Katalogin dokumentoimat fetch-slotit (data/rowCount/isLoading/error) — muut
  // komponentit sitovat näihin (esim. stat ${tbl.rowCount}). 'data' julkaistaan
  // vain per fetch (ei recompute-silmukkaa — vrt. data-gridin ownUpdate-oppi).
  const hostId = table.getAttribute('data-app-id') || '';
  const publish = (slot: string, value: unknown) => {
    if (hostId) store.setSlot(hostId, slot, value);
  };
  publish('rowCount', 0);
  publish('isLoading', false);
  publish('error', '');

  let columns: TableColumn[] = [];
  const colScript = table.querySelector('script.sp-app-table-columns');
  if (colScript) {
    try {
      const parsed = JSON.parse(colScript.textContent || '[]');
      if (Array.isArray(parsed)) columns = parsed as TableColumn[];
    } catch {
      // rikkinäinen columns-config — renderöi tyhjin sarakkein
    }
  }

  // --- Järjestäminen ja sivutus (WP-KL5/KL6, löydös E-2) -------------------
  // Asetelma oli nurinkurinen: inline-datan data-grid — jonka rivimäärän
  // rakentaja itse kirjoittaa — osasi järjestää ja sivuttaa, ja palvelimelta
  // hakeva dynamic-table — jonka rivimäärää rakentaja EI hallitse — ei osannut
  // kumpaakaan. `D4` = C: selain järjestää haetun vastauksen HETI, ja sortedBy/
  // sortDirection julkaistaan slotteina, jotta saman voi kytkeä myös palvelimen
  // sort-parametriin isolle aineistolle. Molemmat mallit yhtä aikaa on ainoa
  // joka on oikein sekä pienellä että isolla aineistolla.
  const head = table.querySelector<HTMLElement>('thead');
  let sortKey = '';
  let sortDir: SortDir = '';

  /**
   * Sarakkeen vertailutyyppi. Eksplisiittinen `type` voittaa; muuten se
   * päätellään ensimmäisestä ei-tyhjästä arvosta. Ilman päättelyä numerosarake
   * järjestyisi merkkijonona ("100" < "20") hiljaa ja väärin — juuri se luokka
   * jota tämä korjaussarja metsästää.
   */
  const columnType = (col: TableColumn, rows: Record<string, unknown>[]): string => {
    const declared = (col as { type?: unknown }).type;
    if (typeof declared === 'string' && declared) return declared;
    for (const row of rows) {
      const v = resolvePath(row, col.path);
      if (v == null || v === '') continue;
      if (typeof v === 'number') return 'number';
      if (typeof v === 'boolean') return 'boolean';
      if (typeof v === 'string' && /^\d{4}-\d{2}-\d{2}/.test(v)) return 'date';
      return 'text';
    }
    return 'text';
  };

  /** dynamic-tablen {header,path} → data-gridin GridColumn, jotta sortRows kelpaa. */
  const sortColumns = (rows: Record<string, unknown>[]): GridColumn[] =>
    columns.map((c) => ({
      key: c.path ?? '', label: c.header ?? '', type: columnType(c, rows),
      sortable: true, filterable: false, hidden: false,
    }));

  const numSlotOr = (slot: string, attr: string): number => {
    const v = hostId ? store.getSlot(hostId, slot) : undefined;
    return typeof v === 'number' ? v : num(table.getAttribute(attr), 0);
  };

  // Request-parametrit (AB-WP4.1): binding-arvoiset uusivat haun muuttuessaan.
  let params: RequestParameter[] = [];
  const paramScript = table.querySelector('script.sp-app-table-params');
  if (paramScript) {
    try {
      const parsed = JSON.parse(paramScript.textContent || '[]');
      if (Array.isArray(parsed)) params = parsed as RequestParameter[];
    } catch {
      // rikkinäinen params-config — haetaan ilman parametreja
    }
  }

  const setStatus = (text: string) => {
    if (status) status.textContent = text;
  };
  const emptyMessage = (): string => {
    const v = hostId ? store.getSlot(hostId, 'emptyMessage') : undefined;
    return typeof v === 'string' && v !== '' ? v : emptyAttr;
  };

  // --- Rivin valinta (WP-AR3, löydös F-C) ---------------------------------
  // Katalogi ja SKILL.md §7 ovat luvanneet selectedRow/selectedRowId alusta
  // asti, mutta mikään ei julkaissut niitä: sidonta ei edes pudonnut
  // fallbackiin vaan renderöi sanan "undefined". Semantiikka on D2 = A:
  // klikkaus valitsee, valinta näkyy, ja se säilyy uuden haun yli jos sama
  // rowKeyPath-arvo on yhä tuloksissa — sama sääntö kuin selectin
  // vaihtoehdoilla (SKILL.md §7.2), jottei alusta käyttäydy kahdella tavalla.
  let selectedRowId: string | null = null;
  let currentRows: Record<string, unknown>[] = [];
  const rowIdOf = (row: Record<string, unknown>): string | null => {
    const v = resolvePath(row, rowKey);
    return v == null ? null : String(v);
  };
  const paintSelection = (): void => {
    tbody.querySelectorAll<HTMLElement>('tr[data-app-row-id]').forEach((tr) => {
      const on = tr.getAttribute('data-app-row-id') === selectedRowId;
      tr.classList.toggle('sp-dt-row--selected', on);
      tr.setAttribute('aria-selected', String(on));
    });
  };
  const publishSelection = (): void => {
    const row = selectedRowId == null
      ? null
      : currentRows.find((r) => rowIdOf(r) === selectedRowId) ?? null;
    publish('selectedRowId', selectedRowId ?? '');
    publish('selectedRow', row);
    paintSelection();
  };
  publishSelection();
  tbody.addEventListener('click', (e) => {
    const target = e.target as HTMLElement | null;
    // Rivitoiminnon nappi EI valitse riviä: "kuittaa tämä" ja "tarkastele
    // tätä" ovat eri aikeita, ja niiden sekoittaminen tekisi kuittauksesta
    // sivuvaikutuksellisen.
    if (target?.closest('.sp-dt-action-btn, .sp-dt-action-menu')) return;
    const tr = target?.closest?.('tr[data-app-row-id]') as HTMLElement | null;
    if (!tr || !tbody.contains(tr)) return;
    const id = tr.getAttribute('data-app-row-id');
    // Saman rivin uudelleenklikkaus poistaa valinnan — muuten valinnasta ei
    // pääse eroon ilman uutta hakua.
    selectedRowId = id === selectedRowId ? null : id;
    publishSelection();
  });

  // Juokseva pyyntönumero (WP-AR2). Ilman tätä JOKAINEN vastaus kirjoitti rivit,
  // myös vanhentunut: rajattu taulu jäi näyttämään rajaamatonta dataa aina kun
  // hitaampi vanha vastaus saapui viimeisenä. Todennettu 3/5 latauksessa —
  // parametriton vastaus oli 242 riviä ja siksi usein hitaampi kuin oikea 16.
  // (hr-portaali-demo-app-plan.md, löydös F-J.)
  let requestSeq = 0;

  /**
   * Järjestä → sivuta → piirrä. Yksi paikka, jotta otsikkoklikkaus, sivutuksen
   * slotti ja uusi haku kulkevat samaa reittiä. Palauttaa NÄKYVIEN rivien määrän.
   */
  const paintRows = (): number => {
    const cols = sortColumns(currentRows);
    const sorted = sortRows(currentRows, cols, sortKey, sortDir);
    const paged = applyPagination(sorted, numSlotOr('visibleOffset', 'data-app-offset'),
      numSlotOr('visiblePageSize', 'data-app-page-size'));
    renderTableRows(tbody, paged, columns, rowKey, rowActions);
    // rowCount on KOKO vastaus, ei näkyvä sivu: pagination.totalRows sidotaan
    // siihen, ja sivutettu luku tekisi sivupainikkeista itseensä viittaavan
    // silmukan (SKILL.md §3 kultainen sääntö).
    publish('rowCount', currentRows.length);
    publish('sortedBy', sortKey);
    publish('sortDirection', sortDir);
    paintSelection();
    return paged.length;
  };

  const buildHead = (): void => {
    if (!head) return;
    head.querySelectorAll<HTMLElement>('th[data-app-col-path]').forEach((th) => {
      const path = th.getAttribute('data-app-col-path') || '';
      th.classList.add('sp-dt-sortable');
      const old = th.querySelector('.sp-dt-sort-ind');
      if (old) old.remove();
      if (path === sortKey && sortDir) {
        const ind = th.ownerDocument.createElement('span');
        ind.className = 'sp-dt-sort-ind';
        ind.textContent = sortDir === 'asc' ? ' ▲' : ' ▼';
        th.appendChild(ind);
      }
      th.setAttribute('aria-sort', path === sortKey && sortDir
        ? (sortDir === 'asc' ? 'ascending' : 'descending') : 'none');
    });
  };

  // Sivutuksen slotit piirtävät rivit uudelleen ilman uutta hakua — sivun vaihto
  // ei saa maksaa verkkokutsua kun rivit ovat jo muistissa (sama kuin data-gridissä).
  if (hostId) {
    store.subscribe(`${hostId}.visibleOffset`, () => paintRows());
    store.subscribe(`${hostId}.visiblePageSize`, () => paintRows());
  }

  head?.addEventListener('click', (e) => {
    const th = (e.target as HTMLElement | null)?.closest?.('th[data-app-col-path]') as HTMLElement | null;
    if (!th || !head.contains(th)) return;
    const nx = cycleSort(sortKey, sortDir, th.getAttribute('data-app-col-path') || '');
    sortKey = nx.key;
    sortDir = nx.dir;
    buildHead();
    paintRows();
  });
  buildHead();

  // Row-action-napit (sp-app-table-row-actions-config) uusivat haun onnistuessa,
  // joten niille annetaan viittaus load():iin. load viittaa takaisin rowActionsiin;
  // ristiviittaus on turvallinen (onClick-sulkeuma ajetaan vasta klikatessa).
  const load = () => {
    if (!bridge) {
      setStatus('Preview requires a mock for this table.');
      return;
    }
    const mine = ++requestSeq;
    setStatus(resolveMessage('loading'));
    publish('isLoading', true);
    publish('error', '');
    // Parametrit lasketaan JOKA haussa tuoreesta storen tilasta (reaktiivinen).
    const { query, body } = buildRequestParams(params, (expr) => evalExpression(expr, store.state));
    const hasBody = Object.keys(body).length > 0;
    const hasQuery = Object.keys(query).length > 0;
    bridge.invoke(sourceId, hasBody ? body : undefined, hasQuery ? query : undefined).then(
      (res) => {
        // Vanhentunut vastaus: uudempi haku on jo lähtenyt. Ei riveihin, ei
        // slotteihin, eikä isLoadingiin — sen omistaa nyt tuore haku.
        if (mine !== requestSeq) return;
        // WP-VV5: ei-2xx EI ole tyhjä tulos. Ennen tätä 400 meni
        // onnistumishaaraan: extractRows → [], ruutuun emptyMessage ja
        // error-slot vieläpä tyhjennettiin — "ei rivejä" näytti samalta kuin
        // "haku hylättiin". Rivit jäävät ennalleen kuten hylätyssä kutsussa.
        const outcome = describeResponse(res);
        if (!outcome.ok) {
          const text = outcome.errorMessage || resolveMessage('loadFailed');
          setStatus(text);
          publish('isLoading', false);
          publish('error', text);
          return;
        }
        const rows = extractRows(res.responseBody);
        currentRows = rows;
        const n = paintRows();
        setStatus(n === 0 ? emptyMessage() : '');
        // Valinta säilyy vain jos sama rivi on yhä tuloksissa (D2 = A).
        if (selectedRowId != null && !rows.some((r) => rowIdOf(r) === selectedRowId)) {
          selectedRowId = null;
        }
        publish('data', rows);
        // rowCount julkaistaan paintRowsissa KOKO vastauksena. Tässä oli ennen
        // `publish('rowCount', n)`, ja n on nyt NÄKYVIEN rivien määrä — se
        // ylikirjoitti kokonaisluvun sivun koolla, jolloin sivutukseen sidottu
        // pagination näytti "1–10 / 10" 43 rivin vastauksesta.
        publish('isLoading', false);
        publishSelection();
      },
      () => {
        if (mine !== requestSeq) return;
        const text = describeResponse(null).errorMessage;
        setStatus(text);
        publish('isLoading', false);
        publish('error', text);
      },
    );
  };

  // Hakujen koalesointi saman tickin sisällä (WP-AR2, D5 = B). Syy on
  // alustuksessa: syötteet (radio-group, select, search) julkaisevat value-
  // slottinsa VASTA omassa kytkennässään, joka ajetaan tämän jälkeen samassa
  // synkronisessa alustuspyyhkäisyssä. Suoraan kutsuttu load() lähti siis aina
  // ilman sidottuja parametreja, ja jokainen sidottu taulu teki kaksi hakua
  // joka latauksella. Microtask odottaa pyyhkäisyn loppuun, jolloin lähtee
  // yksi haku oikeilla parametreilla.
  let loadScheduled = false;
  const scheduleLoad = () => {
    if (loadScheduled) return;
    loadScheduled = true;
    queueMicrotask(() => {
      loadScheduled = false;
      load();
    });
  };

  const rowActions = buildRowActions(table, bridge, store, load, runRowLifecycle);

  // Reaktiivisuus: uusi haku kun minkä tahansa binding-parametrin riippuvuus muuttuu.
  for (const p of params) {
    if (p && p.source === 'binding' && p.binding) {
      try {
        for (const dep of Object.keys(collectDeps(parse(p.binding)))) {
          store.subscribe(dep, scheduleLoad);
        }
      } catch {
        // parse-virhe → parametri jää alkuarvoonsa, ei tilausta
      }
    }
  }

  scheduleLoad();
  if (refreshSec > 0) {
    setInterval(load, refreshSec * 1000);
  }
  return load;
}

/**
 * Lue dynamic-tablen rivitoiminnot (script.sp-app-table-row-actions — taulukko,
 * jonka renderöijä on normalisoinut `rowActions`ista tai vanhasta kolmikosta
 * muotoon {label, apiId, mapping, lifecycleActions}) ja rakenna
 * {@link RowAction}-lista (WP-KH4). Kolme polkua per toiminto:
 *
 * <ul>
 *   <li>apiId + silta → klikkaus mäppää rivin request-rungoksi
 *       ({@link buildRowActionBody}), POSTaa datalähteeseen, 2xx → taulukko uusii
 *       itsensä ({@link reload}); kortit ajoituksineen (WP-HV3);</li>
 *   <li>ei apiId:tä mutta kortteja → lifecycle-only: klikkaus ajaa on-click-kortit
 *       ilman verkkokutsua, busy-tilaa ja reloadia — ei ole 2xx:ää johon sitoutua.
 *       Toimii myös ilman siltaa (show/hide/set-variable eivät tarvitse sitä);
 *       validaattori on hylännyt on-success/on-api-fail-kortit tällaiselta;</li>
 *   <li>muuten → pelkkä label ilman onClickia → disabloitu nappi (esikatselu
 *       ilman kehystystä, tai kuollut toiminto).</li>
 * </ul>
 * Ei configia tai tyhjä → tyhjä lista (ei toimintosaraketta).
 */
function buildRowActions(
  table: HTMLElement,
  bridge: BridgeClient | null,
  store: Store,
  reload: () => void,
  runRowLifecycle: RowLifecycleRunner,
): RowAction[] {
  const script = table.querySelector('script.sp-app-table-row-actions');
  if (!script) return [];
  let configs: unknown;
  try {
    configs = JSON.parse(script.textContent || '[]');
  } catch {
    return [];
  }
  if (!Array.isArray(configs)) return [];
  const actions: RowAction[] = [];
  for (const raw of configs) {
    if (!raw || typeof raw !== 'object') continue;
    const config = raw as {
      apiId?: string;
      label?: string;
      mapping?: RowActionMappingEntry[];
      lifecycleActions?: unknown;
    };
    const apiId = config.apiId || '';
    const label = config.label || 'Update';
    const mapping = Array.isArray(config.mapping) ? config.mapping : [];
    // WP-HV3: rivitoiminnon lifecycle-kortit. Napilla nämä tulevat DOMista, mutta
    // rivinapin renderöi runtime itse, joten ne kulkevat samassa configissa.
    const lifecycle = Array.isArray(config.lifecycleActions) ? config.lifecycleActions : [];
    if (!apiId && lifecycle.length > 0) {
      actions.push({
        label,
        onClick: (row, button) => runRowLifecycle(lifecycle, button, 'on-click', { row }),
      });
      continue;
    }
    if (!bridge || !apiId) {
      actions.push({ label }); // ei elävää toimintoa → disabloitu nappi (sarakkeet pysyvät linjassa)
      continue;
    }
    actions.push(buildApiRowAction(label, apiId, mapping, lifecycle, bridge, store, reload, runRowLifecycle));
  }
  return actions;
}

/** API-kutsuva rivitoiminto — WP-HV3:n kulku sellaisenaan. */
function buildApiRowAction(
  label: string,
  apiId: string,
  mapping: RowActionMappingEntry[],
  lifecycle: unknown[],
  bridge: BridgeClient,
  store: Store,
  reload: () => void,
  runRowLifecycle: RowLifecycleRunner,
): RowAction {
  return {
    label,
    onClick: (row, button) => {
      // WP-KH3: klikattu rivi näkyy nimellä `row` sekä binding-mäppäyksessä
      // että korteissa. Ennen tätä rivi kulki vain rowPath-mäppäyksen kautta
      // pyynnön runkoon, ja kortit evaluoituivat pelkkää globaalia storea
      // vasten — rivikohtainen "katso tämä tiedosto" ei ollut ilmaistavissa.
      const rowState: ExprState = { row };
      const body = buildRowActionBody(mapping, row,
        (expr) => evalExpression(expr, { ...store.state, ...rowState }));
      button.setAttribute('data-app-busy', '1');
      button.disabled = true;
      const done = () => {
        button.removeAttribute('data-app-busy');
        button.disabled = false;
      };
      // on-click ennen kutsua, kuten trigger-api-napillakin.
      runRowLifecycle(lifecycle, button, 'on-click', rowState);
      // WP-VV3: vastaus kulkee korteille rivin rinnalla (`row` + `status`,
      // `response`, `errorMessage`), jotta on-api-fail-kortti voi näyttää
      // palvelimen oman viestin.
      bridge.invoke(apiId, body).then(
        (res) => {
          done();
          const outcome = describeResponse(res);
          // D3 = A: oman taulun lataus säilyy AINA. Sen poistaminen
          // lifecycleActionsin läsnä ollessa rikkoisi jokaisen olemassa olevan
          // rivitoiminnon sillä hetkellä kun siihen lisätään ensimmäinen toast.
          if (outcome.ok) reload();
          runRowLifecycle(lifecycle, button, outcome.ok ? 'on-success' : 'on-api-fail',
            { ...rowState, ...responseState(outcome) });
        },
        () => {
          done();
          runRowLifecycle(lifecycle, button, 'on-api-fail',
            { ...rowState, ...responseState(describeResponse(null)) });
        },
      );
    },
  };
}

/**
 * Kytke yksi chart piirtämään itsensä (AB-WP6.1). Inline-data (staticData-
 * script) piirtyy heti; data-app-api-id-chart hakee rivit sillan kautta ja
 * uusii autoRefreshin mukaan. Slotit data/pointCount/isLoading/error
 * päivittyvät storeen, jotta muut komponentit voivat sitoutua kaavion tilaan.
 * Ilman siltaa API-chart kertoo statuksessa että esikatselu vaatii mockin.
 */
function wireChart(
  chart: HTMLElement,
  bridge: BridgeClient | null,
  store: Store,
): (() => void) | null {
  const id = chart.getAttribute('data-app-id') || '';
  const type = (chart.getAttribute('data-app-chart-type') || 'bar') as ChartType;
  const labelPath = chart.getAttribute('data-app-label-path') || 'label';
  const valuePath = chart.getAttribute('data-app-value-path') || 'value';
  // WP-KL7 (löydös E-4): useampi sarja samaan kaavioon. `valuePaths` on
  // valinnainen ja voittaa `valuePath`in kun se on annettu — yksisarjaiset Appit
  // eivät muutu millään tavalla.
  const valuePaths = ((): { path: string; label?: string }[] => {
    const raw = chart.getAttribute('data-app-value-paths') || '';
    if (!raw) return [{ path: valuePath }];
    try {
      const parsed = JSON.parse(raw);
      if (Array.isArray(parsed) && parsed.length > 0) {
        return parsed
          .map((v) => (typeof v === 'string' ? { path: v } : v))
          .filter((v) => v && typeof v.path === 'string' && v.path !== '');
      }
    } catch {
      // Rikkinäinen config → yksi sarja vanhalla polulla.
    }
    return [{ path: valuePath }];
  })();
  const height = parseInt(chart.getAttribute('data-app-height') || '240', 10) || 240;
  const palette = resolvePalette(chart.getAttribute('data-app-palette'));
  const showValues = chart.getAttribute('data-app-show-values') === '1';
  // Sama kuin dynamic-tablessa (WP-AR6): attribuutti on vain palvelimen
  // renderöimä alkuarvo, sidottu emptyMessage elää slotissa.
  const emptyAttr = chart.getAttribute('data-app-empty') || 'No data';
  const apiId = chart.getAttribute('data-app-api-id') || '';
  const refreshSec = parseInt(chart.getAttribute('data-app-refresh') || '0', 10) || 0;
  const canvas = chart.querySelector<HTMLElement>('[data-app-chart-canvas]');
  const status = chart.querySelector<HTMLElement>('[data-app-chart-status]');
  if (!canvas) return null;

  const opts: ChartOptions = { type, height, palette, showValues };
  const setStatus = (t: string) => { if (status) status.textContent = t; };
  const setSlot = (slot: string, v: unknown) => { if (id) store.setSlot(id, slot, v); };
  const emptyMessage = (): string => {
    const v = id ? store.getSlot(id, 'emptyMessage') : undefined;
    return typeof v === 'string' && v !== '' ? v : emptyAttr;
  };

  const draw = (rows: Record<string, unknown>[]) => {
    if (valuePaths.length > 1) {
      const series = toChartSeries(rows, labelPath, valuePaths);
      drawChart(canvas, series, opts);
      // `data` pysyy ENSIMMÄISEN sarjan pisteinä: sidonnat osoittavat siihen jo,
      // ja slotin muodon vaihtaminen rikkoisi ne hiljaa. `pointCount` on
      // kategorioiden määrä, ei sarjojen — se on mitä lukija tarkoittaa.
      const first = series[0]?.points ?? [];
      setSlot('data', first);
      setSlot('pointCount', first.length);
      setStatus(series.every((x) => x.points.length === 0) ? emptyMessage() : '');
      return;
    }
    const points = toChartPoints(rows, labelPath, valuePath);
    drawChart(canvas, points, opts);
    setSlot('data', points);
    setSlot('pointCount', points.length);
    setStatus(points.length === 0 ? emptyMessage() : '');
  };

  setSlot('isVisible', chart.style.display !== 'none');
  setSlot('error', '');

  if (apiId) {
    if (!bridge) {
      setSlot('isLoading', false);
      setStatus('Preview requires a mock for this chart.');
      return null;
    }
    // WP-SF4 (D4 = C, löydös T-E): hakuparametrit samassa muodossa kuin
    // dynamic-tablessa. Ennen tätä kaavio haki datalähteen ILMAN yhtään
    // parametria, joten suodatetussa näkymässä taulu seurasi suodatinta ja
    // kaavio ei — eikä mikään kertonut miksi kaksi lukua eroavat.
    let params: RequestParameter[] = [];
    const paramScript = chart.querySelector('script.sp-app-chart-params');
    if (paramScript) {
      try {
        const parsed = JSON.parse(paramScript.textContent || '[]');
        if (Array.isArray(parsed)) params = parsed as RequestParameter[];
      } catch {
        // rikkinäinen lohko → haetaan ilman parametreja, kuten ennenkin
      }
    }
    const load = () => {
      setSlot('isLoading', true);
      setStatus(resolveMessage('loading'));
      const { query, body } = buildRequestParams(params, (expr) => evalExpression(expr, store.state));
      bridge.invoke(apiId, Object.keys(body).length > 0 ? body : undefined, query).then(
        (res) => {
          setSlot('isLoading', false);
          // WP-VV5: ei-2xx ei piirrä tyhjää kaaviota eikä nollaa erroria.
          const outcome = describeResponse(res);
          if (!outcome.ok) {
            const text = outcome.errorMessage || resolveMessage('loadFailed');
            setSlot('error', text);
            setStatus(text);
            return;
          }
          setSlot('error', '');
          draw(extractRows(res.responseBody));
        },
        () => {
          const text = describeResponse(null).errorMessage;
          setSlot('isLoading', false);
          setSlot('error', text);
          setStatus(text);
        },
      );
    };
    load();
    // Sidottu parametri uusii haun muuttuessaan — sama tilaus kuin taululla.
    for (const p of params) {
      if (p && p.source === 'binding' && p.binding) {
        try {
          for (const dep of Object.keys(collectDeps(parse(p.binding)))) {
            store.subscribe(dep, load);
          }
        } catch {
          // parse-virhe → parametri jää alkuarvoonsa, ei tilausta
        }
      }
    }
    if (refreshSec > 0) setInterval(load, refreshSec * 1000);
    return load;
  }

  // Ei API:a → inline-data piirtyy heti.
  setSlot('isLoading', false);
  const dataScript = chart.querySelector('script.sp-app-chart-data');
  draw(chartStaticRows(dataScript?.textContent));
  // WP-SF4: sidottu staticData piirtää uudelleen kun lauseke muuttuu — näin
  // kaavion voi syöttää esim. piilotetun dynamic-tablen data-slotista, jolloin
  // sama haku palvelee sekä taulua että kaaviota yhdellä pyynnöllä.
  if (id) {
    store.subscribe(`${id}.staticData`, () => {
      const v = store.getSlot(id, 'staticData');
      // Lauseke voi tuottaa joko JSON-merkkijonon tai valmiin taulukon
      // (esim. toisen komponentin data-slotti) — kumpikin kelpaa.
      draw(chartStaticRows(typeof v === 'string' ? v : JSON.stringify(v ?? [])));
    });
  }
  // Inline-datalla ei ole mitään haettavaa → ei refresh-kohde (WP-ABP3).
  return null;
}

/** Lue luku tolerantisti (number tai numeerinen merkkijono), muuten fallback. */
function num(v: unknown, fallback: number): number {
  if (typeof v === 'number' && Number.isFinite(v)) return v;
  if (typeof v === 'string' && v.trim() !== '') {
    const n = Number(v);
    if (Number.isFinite(n)) return n;
  }
  return fallback;
}

/**
 * Kytke yksi pagination-kontrolli (AB-WP6.4). Piirtää prev/[numerot]/next +
 * "X–Y of Z" -tekstin ja julkaisee page/offset/pageSize-slotit. totalRows/
 * pageSize luetaan bindingin (jos sidottu, jo storessa) tai attribuutin
 * mukaan; binding-muutokset (esim. Taulun rowCount) päivittävät kontrollin
 * store-tilauksen kautta. pageSize-muutos nollaa sivun 1:een.
 */
function wirePagination(pag: HTMLElement, store: Store): void {
  const id = pag.getAttribute('data-app-id') || '';
  if (!id) return;
  const controls = pag.querySelector<HTMLElement>('[data-app-pagination-controls]');
  if (!controls) return;
  const rangeEl = pag.querySelector<HTMLElement>('[data-app-pagination-range]');
  const showNumbers = pag.getAttribute('data-app-show-numbers') === '1';
  const doc = pag.ownerDocument;

  const attrTotal = parseInt(pag.getAttribute('data-app-total-rows') || '0', 10) || 0;
  const attrSize = parseInt(pag.getAttribute('data-app-page-size') || '25', 10) || 25;
  let totalRows = num(store.getSlot(id, 'totalRows'), attrTotal);
  let pageSize = num(store.getSlot(id, 'pageSize'), attrSize);
  let page = 1;

  const publish = () => {
    store.setSlot(id, 'page', page);
    store.setSlot(id, 'offset', offsetOf(page, pageSize));
    store.setSlot(id, 'pageSize', pageSize);
  };

  const mkButton = (
    text: string,
    targetPage: number,
    opts: { active?: boolean; disabled?: boolean; ariaLabel?: string } = {},
  ): HTMLButtonElement => {
    const b = doc.createElement('button');
    b.type = 'button';
    b.className = 'sp-pagination-button' + (opts.active ? ' sp-pagination-button--active' : '');
    b.textContent = text;
    if (opts.disabled) b.disabled = true;
    else b.setAttribute('data-app-page', String(targetPage));
    if (opts.active) b.setAttribute('aria-current', 'page');
    if (opts.ariaLabel) b.setAttribute('aria-label', opts.ariaLabel);
    return b;
  };

  const render = () => {
    const pageCount = pageCountOf(totalRows, pageSize);
    page = clampPage(page, pageCount);
    controls.textContent = '';
    controls.appendChild(mkButton('‹', page - 1, { disabled: page <= 1, ariaLabel: 'Previous page' }));
    if (showNumbers) {
      for (const item of pageItems(page, pageCount)) {
        if (item === ELLIPSIS) {
          const span = doc.createElement('span');
          span.className = 'sp-pagination-ellipsis';
          span.textContent = '…';
          controls.appendChild(span);
        } else {
          controls.appendChild(mkButton(String(item), item, { active: item === page }));
        }
      }
    }
    controls.appendChild(mkButton('›', page + 1, { disabled: page >= pageCount, ariaLabel: 'Next page' }));
    if (rangeEl) rangeEl.textContent = rangeText(page, pageSize, totalRows);
    publish();
  };

  store.setSlot(id, 'isVisible', pag.style.display !== 'none');
  render();

  controls.addEventListener('click', (e) => {
    const target = (e.target as HTMLElement).closest('button[data-app-page]') as HTMLElement | null;
    if (!target) return;
    const p = parseInt(target.getAttribute('data-app-page') || '', 10);
    if (!Number.isFinite(p)) return;
    page = p;
    render();
  });

  // Reaktiivisuus tilataan VASTA alkurenderin jälkeen, jottei ensipublishin
  // pageSize-setSlot laukaise turhaa toista renderiä.
  store.subscribe(`${id}.totalRows`, () => {
    totalRows = num(store.getSlot(id, 'totalRows'), totalRows);
    render();
  });
  store.subscribe(`${id}.pageSize`, () => {
    const ns = num(store.getSlot(id, 'pageSize'), pageSize);
    if (ns !== pageSize) {
      pageSize = ns;
      page = 1; // pageSize-muutos → takaisin sivulle 1
    }
    render();
  });
}

/**
 * Kytke yksi stat-KPI (AB-WP6.6): muotoile value/delta (stat.ts) ja päivitä
 * ne reaktiivisesti value/delta-slottien (bindingit) muuttuessa. Palvelin
 * renderöi vain tyhjät kohteet + raakakonfigin; muotoilu on vain täällä.
 */
function wireStat(stat: HTMLElement, store: Store): void {
  const id = stat.getAttribute('data-app-id') || '';
  if (!id) return;
  const valueEl = stat.querySelector<HTMLElement>('[data-app-stat-value]');
  const deltaEl = stat.querySelector<HTMLElement>('[data-app-stat-delta]');
  const deltaContainer = stat.querySelector<HTMLElement>('.sp-stat-delta');
  const valueFormat = stat.getAttribute('data-app-value-format') || 'number';
  const deltaFormat = stat.getAttribute('data-app-delta-format') || 'percent';
  const deltaMode = stat.getAttribute('data-app-delta-direction') || 'auto';

  const attrValue = parseFloat(stat.getAttribute('data-app-value') || '0') || 0;
  const attrDelta = parseFloat(stat.getAttribute('data-app-delta') || '0') || 0;
  // WP-SF5: `text`-formaatti näyttää arvon sellaisenaan, joten sitä EI saa
  // pakottaa luvuksi matkalla — muuten merkkijono kuolisi nollaksi juuri siinä
  // kohdassa jonka korjaus poisti (löydös T-D).
  const isText = valueFormat === 'text';
  const attrText = stat.getAttribute('data-app-value-text') || '';
  const readValue = (fallback: number | string): number | string => {
    const raw = store.getSlot(id, 'value');
    if (isText) return raw == null ? fallback : (raw as number | string);
    return num(raw, typeof fallback === 'number' ? fallback : 0);
  };
  let value: number | string = readValue(isText ? attrText : attrValue);
  let delta = num(store.getSlot(id, 'delta'), attrDelta);

  const renderValue = () => {
    if (valueEl) valueEl.textContent = formatStatValue(value, valueFormat, currentLocale());
  };
  const renderDelta = () => {
    if (!deltaEl) return;
    deltaEl.textContent = formatDelta(delta, deltaFormat, currentLocale());
    if (deltaContainer) {
      deltaContainer.className = 'sp-stat-delta sp-stat-delta--' + resolveDeltaDirection(delta, deltaMode);
    }
  };

  store.setSlot(id, 'isVisible', stat.style.display !== 'none');
  store.setSlot(id, 'value', value);
  store.setSlot(id, 'delta', delta);
  renderValue();
  renderDelta();

  store.subscribe(`${id}.value`, () => {
    value = readValue(value);
    renderValue();
  });
  store.subscribe(`${id}.delta`, () => {
    delta = num(store.getSlot(id, 'delta'), delta);
    renderDelta();
  });
}

/**
 * Kuva (WP-ABP8): virhefallback. Sama kuvio kuin avatarilla — rikkinäinen
 * URL ei saa jättää selaimen omaa rikkinäistä kuvaikonia näkyviin, ja
 * `hasError` on slot jotta App voi reagoida siihen (esim. piilottaa kortin).
 *
 * <p>HUOM: julkaistun Appin CSP on `img-src 'self' data:` (päätös D1 = A),
 * joten TOISEN ORIGININ kuva päätyy tänne aina — se ei ole rikkinäinen URL
 * vaan estetty pyyntö. Editori varoittaa siitä jo rakennushetkellä.
 */
function wireImage(host: HTMLElement, store: Store): void {
  const id = host.getAttribute('data-app-id') || '';
  const img = host.querySelector<HTMLImageElement>('img.sp-image-img');
  const fallback = host.querySelector<HTMLElement>('[data-app-image-fallback]');
  if (!img) return;
  const fail = () => {
    img.style.display = 'none';
    if (fallback) fallback.style.display = '';
    if (id) store.setSlot(id, 'hasError', true);
  };
  if (id) {
    store.setSlot(id, 'hasError', false);
    store.setSlot(id, 'isVisible', host.style.display !== 'none');
  }
  img.addEventListener('error', fail);
  img.addEventListener('load', () => {
    if (fallback) fallback.style.display = 'none';
    img.style.display = '';
    if (id) store.setSlot(id, 'hasError', false);
  });
  // Jo epäonnistunut lataus (kuva ehti valmiiksi ennen kytkentää): naturalWidth
  // on 0 vain kun lataus kaatui, joten tämä ei osu vielä lataamattomaan.
  if (img.complete && img.getAttribute('src') && img.naturalWidth === 0) fail();
}

/**
 * Edistymispalkki (WP-ABP8). Oma kytkentänsä kuten statilla: palkin leveys ja
 * "12 / 50" -teksti ovat johdettuja arvoja, eivät suoraa tekstinkirjoitusta,
 * joten ne eivät kulje applySlotToDom-haaran kautta.
 */
function wireProgress(host: HTMLElement, store: Store): void {
  const id = host.getAttribute('data-app-id') || '';
  if (!id) return;
  const bar = host.querySelector<HTMLElement>('[data-app-progress-bar]');
  const text = host.querySelector<HTMLElement>('[data-app-progress-text]');
  const track = host.querySelector<HTMLElement>('.sp-progress-track');
  const max = num(host.getAttribute('data-app-progress-max'), 100) || 100;

  const render = () => {
    const value = num(store.getSlot(id, 'value'), 0);
    const pct = Math.max(0, Math.min(100, (value / max) * 100));
    if (bar) bar.style.width = `${pct}%`;
    if (text) text.textContent = `${trimNumber(value)} / ${trimNumber(max)}`;
    if (track) track.setAttribute('aria-valuenow', String(value));
  };

  if (store.getSlot(id, 'value') === undefined) {
    // Palvelin renderöi alkuarvon leveydeksi; luetaan se takaisin slottiin
    // jotta lausekkeet näkevät sen ennen ensimmäistä sidontapäivitystä.
    const pct = parseFloat((bar?.style.width || '0').replace('%', '')) || 0;
    store.setSlot(id, 'value', (pct / 100) * max);
  }
  store.setSlot(id, 'isVisible', host.style.display !== 'none');
  render();
  store.subscribe(`${id}.value`, render);
}

/** Kokonaisluku ilman ".0"-häntää; muuten yksi desimaali (ProgressRendererin peili). */
function trimNumber(d: number): string {
  return Number.isInteger(d) ? String(d) : d.toFixed(1);
}

/**
 * Kytke yksi avatar (AB-WP6.7): laske initiaalit + taustaväri effektiivisestä
 * nimestä ja hoida kuvan virhefallback (img error → initiaalit näkyviin,
 * hasImageError-slot true). Reaktiivinen name/imageUrl/statusDot kulkee
 * applySlotToDom-casejen kautta (bindingit).
 */
function wireAvatar(av: HTMLElement, store: Store): void {
  const id = av.getAttribute('data-app-id') || '';
  if (!id) return;
  const initEl = av.querySelector<HTMLElement>('.sp-avatar-initials');
  const img = av.querySelector<HTMLImageElement>('img.sp-avatar-img');

  // Effektiivinen nimi: bound-arvo (binding-init asetti slotin) tai attribuutti.
  const boundName = store.getSlot(id, 'name');
  const name = typeof boundName === 'string' ? boundName : (av.getAttribute('data-app-name') || '');
  if (initEl) {
    initEl.textContent = initialsOf(name);
    initEl.style.background = colorFromName(name);
  }
  if (img && !img.getAttribute('alt')) img.alt = name;

  store.setSlot(id, 'isVisible', av.style.display !== 'none');
  store.setSlot(id, 'hasImageError', false);

  const showInitials = () => {
    if (img) img.style.display = 'none';
    if (initEl) initEl.style.display = '';
    store.setSlot(id, 'hasImageError', true);
  };
  if (img) {
    img.addEventListener('error', showInitials);
    // Jo epäonnistunut ennen listeneriä (välimuistista): complete + naturalWidth 0.
    if (img.getAttribute('src') && img.complete && img.naturalWidth === 0) showInitials();
  }
}

/** Paikallinen tämä-päivä wire-formaatissa (YYYY-MM-DD). */
function todayISO(): string {
  const d = new Date();
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

/**
 * Date-range (AB-WP6.9): kaksi natiivia date-inputtia + preset-valikko.
 * Julkaisee startValue/endValue/daysCount/activePreset/isVisible ja pitää
 * valikon synkassa valitun välin kanssa. Päivälogiikka on dateRange.ts:ssä
 * (puhdas); "tänään" resolvoidaan paikallisesti. data-app-daterange-count =
 * wire-up-marker (guardrail etsii tämän bundlesta).
 */
function wireDateRange(dr: HTMLElement, store: Store): void {
  const id = dr.getAttribute('data-app-id') || '';
  if (!id) return;
  const startInput = dr.querySelector<HTMLInputElement>('input[data-app-role="start"]');
  const endInput = dr.querySelector<HTMLInputElement>('input[data-app-role="end"]');
  if (!startInput || !endInput) return;
  const preset = dr.querySelector<HTMLSelectElement>('[data-app-daterange-preset]');
  const countEl = dr.querySelector<HTMLElement>('[data-app-daterange-count]');
  const today = todayISO();

  const publish = (): void => {
    const s = startInput.value;
    const e = endInput.value;
    store.setSlot(id, 'startValue', s);
    store.setSlot(id, 'endValue', e);
    const dc = daysBetween(s, e);
    store.setSlot(id, 'daysCount', dc);
    const active = detectPreset(s, e, today);
    store.setSlot(id, 'activePreset', active);
    if (preset) {
      // 'custom'/'' → Custom-optio (arvo ""), muuten preset-avain.
      preset.value = active === '' || active === 'custom' ? '' : active;
    }
    if (countEl) countEl.textContent = dc > 0 ? `${dc} ${dc === 1 ? 'day' : 'days'}` : '';
  };

  // Alkuarvo: eksplisiittiset start+end voittavat; muuten defaultPreset.
  if (!startInput.value && !endInput.value) {
    const dp = dr.getAttribute('data-app-default-preset') || 'none';
    const r = resolvePreset(dp, today);
    if (r) {
      startInput.value = r.start;
      endInput.value = r.end;
    }
  }

  store.setSlot(id, 'isVisible', dr.style.display !== 'none');
  publish();

  const onEdit = (): void => publish();
  startInput.addEventListener('input', onEdit);
  startInput.addEventListener('change', onEdit);
  endInput.addEventListener('input', onEdit);
  endInput.addEventListener('change', onEdit);
  if (preset) {
    preset.addEventListener('change', () => {
      const r = resolvePreset(preset.value, today);
      if (r) {
        startInput.value = r.start;
        endInput.value = r.end;
      }
      publish();
    });
  }
}

/** Lue tiedosto data-URL:ksi; virhe → tyhjä (ei kaada muiden lukua). */
function readAsDataURL(file: File): Promise<string> {
  return new Promise((resolve) => {
    const r = new FileReader();
    r.onload = () => resolve(String(r.result));
    r.onerror = () => resolve('');
    r.readAsDataURL(file);
  });
}

/**
 * File-upload (AB-WP6.10): natiivi <input type=file>. Valinnan muuttuessa lukee
 * tiedostot Base64:ksi, pudottaa maxSizeMb-rajan ylittävät inline-virheellä ja
 * julkaisee files (taulukko { name, type, size, dataBase64 }) + hasFiles.
 * Form kerää arvon files-slotista (ks. recompute). data-app-file-max-mb =
 * wire-up-marker (guardrail etsii tämän bundlesta).
 */
function wireFileUpload(fu: HTMLElement, store: Store): void {
  const id = fu.getAttribute('data-app-id') || '';
  if (!id) return;
  const input = fu.querySelector<HTMLInputElement>('input.sp-file-input[type="file"]');
  if (!input) return;
  const errorEl = fu.querySelector<HTMLElement>('.sp-file-error');
  const listEl = fu.querySelector<HTMLElement>('.sp-file-list');
  const maxMb = num(input.getAttribute('data-app-file-max-mb'), 10);

  store.setSlot(id, 'isVisible', fu.style.display !== 'none');
  store.setSlot(id, 'files', []);
  store.setSlot(id, 'hasFiles', false);

  // Tyhjän tilan teksti on sama jonka palvelin jo renderöi (WP-FU3); runtime
  // kirjoittaa sen takaisin kun valinta katoaa. Sanasto on molemmilla sama,
  // ja AppBuilderMessageCatalogTest pinnaa arvot toisiinsa.
  const showNoSelection = (): void => {
    if (listEl) listEl.textContent = resolveMessage('fileNone');
  };

  // Lomakkeen nollaus (`resetOnSuccess`) tyhjentää natiivin syötteen muttei
  // laukaise 'change'ia. Ilman tätä juuri lähetetty tiedosto jäisi sekä
  // näkyviin että files-slottiin, jolloin seuraava lähetys veisi saman
  // tiedoston uudelleen — hiljaa, koska mikään ei näytä rikkoutuneelta.
  fu.closest('form')?.addEventListener('reset', () => {
    store.setSlot(id, 'files', []);
    store.setSlot(id, 'hasFiles', false);
    if (errorEl) errorEl.textContent = '';
    showNoSelection();
  });

  input.addEventListener('change', () => {
    const picked = Array.from(input.files || []);
    const skipped: string[] = [];
    const reads: Promise<PickedFile | null>[] = picked.map((f) => {
      if (!isWithinSize(f.size, maxMb)) {
        skipped.push(f.name);
        return Promise.resolve(null);
      }
      return readAsDataURL(f).then((dataUrl) => ({
        name: f.name,
        type: f.type,
        size: f.size,
        dataBase64: extractBase64(dataUrl),
      }));
    });
    void Promise.all(reads).then((results) => {
      const kept = results.filter((r): r is PickedFile => r !== null);
      store.setSlot(id, 'files', kept);
      store.setSlot(id, 'hasFiles', kept.length > 0);
      if (errorEl) errorEl.textContent = describeSkipped(skipped, maxMb);
      if (kept.length > 0) {
        if (listEl) listEl.textContent = kept.map((k) => k.name).join(', ');
      } else {
        showNoSelection();
      }
      // Natiivi 'change' ei kuplii 'input'-eventtinä; herätä ympäröivä Form
      // uudelleenlaskentaan, jotta se kerää tuoreen files-taulukon.
      input.dispatchEvent(new Event('input', { bubbles: true }));
    });
  });
}

/**
 * Stepper (AB-WP6.11): Tabs-tyylinen konttikomponentti + askelindikaattori +
 * Prev/Next/Submit. Hallitsee aktiivista askelta, edistymistä (completedSteps)
 * ja julkaisee slotit. currentStepIndex + canAdvance ovat reaktiivisia
 * (store-subscribet). data-app-step-count = wire-up-marker.
 */
function wireStepper(stepper: HTMLElement, store: Store, bridge: BridgeClient | null): void {
  const id = stepper.getAttribute('data-app-id') || '';
  if (!id) return;
  const count = num(stepper.getAttribute('data-app-step-count'), 0);
  if (count <= 0) return;
  const linear = stepper.getAttribute('data-app-linear') === '1';
  const submitApi = stepper.getAttribute('data-app-submit-api') || '';
  const steps = stepper.querySelectorAll<HTMLElement>('.sp-stepper-step');
  const panels = stepper.querySelectorAll<HTMLElement>('.sp-stepper-panel');
  const prevBtn = stepper.querySelector<HTMLButtonElement>('[data-app-stepper-prev]');
  const nextBtn = stepper.querySelector<HTMLButtonElement>('[data-app-stepper-next]');
  const labels = Array.from(steps).map((s) => s.querySelector('.sp-stepper-label')?.textContent || '');

  let current = -1; // pakota ensimmäinen render
  let completed: number[] = [];

  const canAdvanceNow = (): boolean => store.getSlot(id, 'canAdvance') !== false;

  const updateNav = (): void => {
    if (prevBtn) prevBtn.disabled = current <= 0;
    if (nextBtn) {
      const isLast = current >= count - 1;
      nextBtn.textContent = isLast
        ? nextBtn.getAttribute('data-app-submit-label') || 'Submit'
        : nextBtn.getAttribute('data-app-next-label') || 'Next';
      // Submit-nappia ei disabloida canAdvancella; vain eteenpäin-Nextiä.
      nextBtn.disabled = !isLast && !canAdvanceNow();
    }
  };

  const render = (): void => {
    steps.forEach((s, i) => {
      s.classList.toggle('sp-stepper-step--active', i === current);
      s.classList.toggle('sp-stepper-step--done', completed.includes(i));
    });
    panels.forEach((p, i) => {
      p.style.display = i === current ? '' : 'none';
    });
    updateNav();
  };

  const publish = (): void => {
    store.setSlot(id, 'currentStepIndex', current);
    store.setSlot(id, 'currentStepKey', labels[current] ?? '');
    store.setSlot(id, 'isFirstStep', current === 0);
    store.setSlot(id, 'isLastStep', current === count - 1);
    store.setSlot(id, 'completedSteps', completed.slice());
  };

  const goToStep = (target: number, markCompleted?: number): void => {
    if (markCompleted !== undefined) completed = addCompleted(completed, markCompleted);
    const clamped = Math.max(0, Math.min(target, count - 1));
    current = clamped;
    render();
    publish();
  };

  // Alkuindeksi: bound currentStepIndex (jos numero) tai data-app-initial-step.
  const boundInitial = store.getSlot(id, 'currentStepIndex');
  const initial = typeof boundInitial === 'number'
    ? boundInitial
    : num(stepper.getAttribute('data-app-initial-step'), 0);
  if (store.getSlot(id, 'canAdvance') === undefined) store.setSlot(id, 'canAdvance', true);
  store.setSlot(id, 'lastStepSubmitted', false);
  initResponseSlots(store, id);
  store.setSlot(id, 'isVisible', stepper.style.display !== 'none');
  goToStep(initial);

  steps.forEach((s, i) => {
    s.addEventListener('click', () => {
      if (canClickTo(i, current, linear)) goToStep(i);
    });
  });
  prevBtn?.addEventListener('click', () => goToStep(current - 1));
  nextBtn?.addEventListener('click', () => {
    if (current >= count - 1) {
      store.setSlot(id, 'lastStepSubmitted', true);
      if (bridge && submitApi) {
        // Sama kytkentä kuin napin payloadilla. Stepper jäi F-A/F-E-kierroksesta
        // väliin, ja sen `${...}`-teksti meni palvelimelle sellaisenaan
        // (CRM-harjoituksen löydös E-7). Kutsu on wireStepperin sisällä, koska
        // kattavuusportti lukee komponentin oman kytkentäfunktion rungon.
        // `lastStepSubmitted` tarkoittaa yhä "Submit painettiin" (D5 = A);
        // palvelimen vastaus kulkee sen rinnalla lastStatus-slotteihin (WP-VV2).
        bridge.invoke(submitApi, resolveJsonPayload(
          id, 'submitParameters', store,
          stepper.getAttribute('data-app-submit-params') || '',
        )).then(
          (res) => publishResponseSlots(store, id, describeResponse(res)),
          () => publishResponseSlots(store, id, describeResponse(null)),
        );
      }
      return;
    }
    if (canAdvanceNow()) goToStep(current + 1, current);
  });

  // Reaktiiviset override-bindingit (asetetaan storeen bindings-reEvalissa).
  store.subscribe(`${id}.currentStepIndex`, () => {
    const v = store.getSlot(id, 'currentStepIndex');
    if (typeof v === 'number' && v !== current) goToStep(v);
  });
  store.subscribe(`${id}.canAdvance`, updateNav);
  // goToStep(initial) julkaisi currentStepIndexin, mikä sai canAdvance-bindingin
  // laskemaan arvon uudelleen (esim. lomakkeen validiteetista) — mutta tuo
  // kaskadi tapahtui ennen kuin yllä oleva subscribe oli rekisteröity, joten
  // Next-napin tila jäi vanhaksi. Synkataan kertaalleen tuoreesta storesta.
  updateNav();
}

/**
 * Radio-group (AB-WP6.12): natiivit type=radio jaetulla HTML-nimellä. Julkaisee
 * valitun radion arvon value-slottiin; valinnan muuttuessa herättää ympäröivän
 * Formin kuplivalla input-eventillä. sp-radio-input = wire-up-marker.
 */
function wireMultiSelect(group: HTMLElement, store: Store): void {
  const id = group.getAttribute('data-app-id') || '';
  if (!id) return;
  // Delegoitu kuuntelu, kuten radio-groupissa (WP-ABP1:n GOTCHA): sidottu
  // `options` rakentaa rastit uudelleen, jolloin per-input-kuuntelijat
  // osoittaisivat poistettuihin elementteihin.
  const publish = (): void => {
    const values: string[] = [];
    group.querySelectorAll<HTMLInputElement>('input.sp-multi-input').forEach((cb) => {
      if (cb.checked) values.push(cb.value);
    });
    store.setSlot(id, 'values', values);
    store.setSlot(id, 'count', values.length);
  };
  store.setSlot(id, 'isVisible', group.style.display !== 'none');
  publish();
  group.addEventListener('change', (e) => {
    const target = e.target as HTMLElement | null;
    if (!target || !target.classList?.contains('sp-multi-input')) return;
    publish();
    target.dispatchEvent(new Event('input', { bubbles: true }));
  });
}

function wireRadioGroup(group: HTMLElement, store: Store): void {
  const id = group.getAttribute('data-app-id') || '';
  if (!id) return;
  // Kuuntelu on DELEGOITU ryhmäelementille eikä yksittäisille radioille, ja
  // publish() hakee radiot joka kerta uudelleen (WP-ABP1). Syy: sidottu
  // `options` rakentaa radiot uudelleen, jolloin boottiin kiinnitetyt
  // per-radio-kuuntelijat osoittaisivat poistettuihin elementteihin ja
  // ryhmä lakkaisi julkaisemasta arvoaan. change kuplii, joten delegointi
  // riittää.
  const publish = (): void => {
    let val = '';
    group.querySelectorAll<HTMLInputElement>('input.sp-radio-input').forEach((r) => {
      if (r.checked) val = r.value;
    });
    store.setSlot(id, 'value', val);
  };
  store.setSlot(id, 'isVisible', group.style.display !== 'none');
  publish();
  group.addEventListener('change', (e) => {
    const target = e.target as HTMLElement | null;
    if (!target || !target.classList?.contains('sp-radio-input')) return;
    publish();
    target.dispatchEvent(new Event('input', { bubbles: true }));
  });
}

/**
 * Data grid (AB-WP6.13): client-side lajittelu/suodatus/sarakevalinta/rivivalinta/
 * sivutus/CSV-vienti. Runtime omistaa thead/tbody:n; datalogiikka on dataGrid.ts:ssä
 * (puhdas). Kuluttaa bound data/columns-slotteja (ei hae itse); reaktiivinen
 * data/columns/offset/pageSize/isLoading/title-muutoksiin. data-app-grid-body =
 * wire-up-marker.
 */
function wireDataGrid(grid: HTMLElement, store: Store, bridge: BridgeClient | null): void {
  const id = grid.getAttribute('data-app-id') || '';
  if (!id) return;
  const doc = grid.ownerDocument;
  const head = grid.querySelector<HTMLElement>('[data-app-grid-head]');
  const body = grid.querySelector<HTMLElement>('[data-app-grid-body]');
  const statusEl = grid.querySelector<HTMLElement>('[data-app-grid-status]');
  const titleEl = grid.querySelector<HTMLElement>('[data-app-grid-title]');
  const colsMenu = grid.querySelector<HTMLElement>('[data-app-grid-cols-menu]');
  const exportBtn = grid.querySelector<HTMLElement>('[data-app-grid-export]');
  if (!head || !body) return;

  const selectable = grid.getAttribute('data-app-selectable') || 'none';
  const rowKey = grid.getAttribute('data-app-row-key') || 'id';
  const showFilters = grid.getAttribute('data-app-show-filters') === '1';
  const exportScope = grid.getAttribute('data-app-export-scope') || 'filtered';

  let sortKey = '';
  let sortDir: SortDir = '';
  const filters: Record<string, string> = {};
  let visibleKeys: Set<string> | null = null; // null = ei vielä alustettu
  const selectedIds = new Set<string>();

  const effectiveColumns = (): GridColumn[] => {
    const bound = store.getSlot(id, 'columns');
    const json = typeof bound === 'string' ? bound : grid.getAttribute('data-app-grid-columns') || '[]';
    return parseColumns(json);
  };
  const allRows = (): Record<string, unknown>[] => coerceRows(store.getSlot(id, 'data'));
  const rowId = (row: Record<string, unknown>): string => String(resolvePath(row, rowKey) ?? '');
  const slotOr = (slot: string, attr: string): string => {
    const v = store.getSlot(id, slot);
    return typeof v === 'string' ? v : grid.getAttribute(attr) || '';
  };
  const numSlotOr = (slot: string, attr: string): number => {
    const v = store.getSlot(id, slot);
    return typeof v === 'number' ? v : num(grid.getAttribute(attr), 0);
  };
  const isLoading = (): boolean => {
    const v = store.getSlot(id, 'isLoading');
    return v === undefined ? grid.getAttribute('data-app-loading') === '1' : !!v;
  };
  const visibleColumns = (cols: GridColumn[]): GridColumn[] =>
    cols.filter((c) => (visibleKeys ? visibleKeys.has(c.key) : !c.hidden));

  const publishSelection = (rows: Record<string, unknown>[]): void => {
    store.setSlot(id, 'selectedRows', rows.filter((r) => selectedIds.has(rowId(r))));
    store.setSlot(id, 'selectedRowIds', [...selectedIds]);
  };

  const selectRow = (rid: string, checked: boolean): void => {
    if (selectable === 'single') {
      selectedIds.clear();
      if (checked) selectedIds.add(rid);
    } else if (checked) {
      selectedIds.add(rid);
    } else {
      selectedIds.delete(rid);
    }
    publishSelection(allRows());
  };

  /**
   * Pidä valintaruudut ja valintatila DOMissa synkassa selectedIdsin kanssa.
   * `aria-selected` + rivin luokka ovat samat kuin dynamic-tablessa — koko E-9:n
   * pointti on että kaksi taulukkoa käyttäytyy samoin.
   */
  const syncSelectionInputs = (): void => {
    if (selectable === 'none') return;
    body.querySelectorAll<HTMLElement>('tr[data-app-row-id]').forEach((row) => {
      const rid = row.getAttribute('data-app-row-id') || '';
      const on = selectedIds.has(rid);
      const input = row.querySelector<HTMLInputElement>('.sp-dg-select-col input');
      if (input) input.checked = on;
      row.classList.toggle('sp-dg-row--selected', on);
      row.setAttribute('aria-selected', on ? 'true' : 'false');
    });
  };

  const renderBody = (cols: GridColumn[], paged: Record<string, unknown>[]): void => {
    const vis = visibleColumns(cols);
    body.textContent = '';
    for (const row of paged) {
      const tr = doc.createElement('tr');
      const rid = rowId(row);
      tr.setAttribute('data-app-row-id', rid);
      if (selectable !== 'none') {
        const td = doc.createElement('td');
        td.className = 'sp-dg-select-col';
        const inp = doc.createElement('input');
        inp.type = selectable === 'single' ? 'radio' : 'checkbox';
        inp.name = `${id}-sel`;
        inp.checked = selectedIds.has(rid);
        inp.addEventListener('change', () => {
          selectRow(rid, inp.checked);
          syncSelectionInputs();
        });
        td.appendChild(inp);
        tr.appendChild(td);

        // Rivin klikkaus valitsee, kuten dynamic-tablessa (löydös E-9). Ennen tätä
        // data-grid valittiin VAIN tästä radiosta, ja master–detail-näkymässä rivin
        // klikkaaminen — koko idiomi — ei tehnyt mitään eikä mikään kertonut miksi.
        // Radio jää paikalleen: monivalinnassa se on yhä nopein tapa napata monta.
        tr.classList.add('sp-dg-row--selectable');
        tr.addEventListener('click', (ev) => {
          // Klikkaus solun sisällä olevaan omaan kontrolliin hoitaa itsensä; ilman
          // tätä valintaruudun klikkaus kulkisi myös tänne ja kumoaisi itsensä.
          const target = ev.target as Element | null;
          if (target && target.closest('input, button, a, select, textarea, label')) return;
          // single: radio-semantiikka — jo valitun rivin klikkaus ei poista valintaa.
          const next = selectable === 'single' ? true : !selectedIds.has(rid);
          selectRow(rid, next);
          syncSelectionInputs();
        });
      }
      for (const col of vis) {
        const td = doc.createElement('td');
        if (col.align) td.style.textAlign = col.align;
        td.textContent = formatCellValue(resolvePath(row, col.key), col.type, currentLocale());
        tr.appendChild(td);
      }
      body.appendChild(tr);
    }
    syncSelectionInputs();
  };

  const updateStatus = (total: number, filteredCount: number): void => {
    if (!statusEl) return;
    if (isLoading()) {
      statusEl.textContent = resolveMessage('loading');
    } else if (total === 0) {
      statusEl.textContent = slotOr('emptyDataMessage', 'data-app-empty-data');
    } else if (filteredCount === 0) {
      statusEl.textContent = slotOr('emptyFilterMessage', 'data-app-empty-filter');
    } else {
      statusEl.textContent = '';
    }
  };

  const recompute = (): void => {
    const cols = effectiveColumns();
    const rows = allRows();
    const filtered = filterRows(rows, filters, cols);
    const sorted = sortRows(filtered, cols, sortKey, sortDir);
    store.setSlot(id, 'filteredData', sorted);
    store.setSlot(id, 'rowCount', sorted.length);
    store.setSlot(id, 'sortedBy', sortKey);
    store.setSlot(id, 'sortDirection', sortDir);
    store.setSlot(id, 'filters', { ...filters });
    store.setSlot(id, 'visibleColumns', visibleColumns(cols).map((c) => c.key));
    publishSelection(rows);
    const paged = applyPagination(sorted, numSlotOr('visibleOffset', 'data-app-offset'),
      numSlotOr('visiblePageSize', 'data-app-page-size'));
    renderBody(cols, paged);
    updateStatus(rows.length, sorted.length);
  };

  const buildHead = (): void => {
    const cols = visibleColumns(effectiveColumns());
    head.textContent = '';
    const tr = doc.createElement('tr');
    if (selectable !== 'none') {
      const th = doc.createElement('th');
      th.className = 'sp-dg-select-col';
      if (selectable === 'multi') {
        const cb = doc.createElement('input');
        cb.type = 'checkbox';
        cb.className = 'sp-dg-select-all';
        cb.addEventListener('change', () => toggleAll(cb.checked));
        th.appendChild(cb);
      }
      tr.appendChild(th);
    }
    for (const col of cols) {
      const th = doc.createElement('th');
      th.setAttribute('data-app-col-key', col.key);
      if (col.align) th.style.textAlign = col.align;
      const span = doc.createElement('span');
      span.textContent = col.label;
      th.appendChild(span);
      if (col.sortable) {
        th.classList.add('sp-dg-sortable');
        if (col.key === sortKey && sortDir) {
          const ind = doc.createElement('span');
          ind.className = 'sp-dg-sort-ind';
          ind.textContent = sortDir === 'asc' ? ' ▲' : ' ▼';
          th.appendChild(ind);
        }
        th.addEventListener('click', () => {
          const nx = cycleSort(sortKey, sortDir, col.key);
          sortKey = nx.key;
          sortDir = nx.dir;
          buildHead();
          recompute();
        });
      }
      tr.appendChild(th);
    }
    head.appendChild(tr);
    if (showFilters) {
      const ftr = doc.createElement('tr');
      ftr.className = 'sp-dg-filter-row';
      if (selectable !== 'none') ftr.appendChild(doc.createElement('th'));
      for (const col of cols) {
        const th = doc.createElement('th');
        if (col.filterable) {
          const inp = doc.createElement('input');
          inp.type = 'text';
          inp.className = 'sp-dg-filter';
          inp.value = filters[col.key] || '';
          inp.addEventListener('input', () => {
            filters[col.key] = inp.value;
            recompute();
          });
          th.appendChild(inp);
        }
        ftr.appendChild(th);
      }
      head.appendChild(ftr);
    }
  };

  const toggleAll = (checked: boolean): void => {
    const cols = effectiveColumns();
    const filtered = sortRows(filterRows(allRows(), filters, cols), cols, sortKey, sortDir);
    selectedIds.clear();
    if (checked) filtered.forEach((r) => selectedIds.add(rowId(r)));
    publishSelection(allRows());
    renderBody(cols, applyPagination(filtered, numSlotOr('visibleOffset', 'data-app-offset'),
      numSlotOr('visiblePageSize', 'data-app-page-size')));
  };

  const buildColsMenu = (): void => {
    const cols = effectiveColumns();
    if (!visibleKeys) visibleKeys = new Set(cols.filter((c) => !c.hidden).map((c) => c.key));
    if (!colsMenu) return;
    colsMenu.textContent = '';
    for (const col of cols) {
      const label = doc.createElement('label');
      label.className = 'sp-dg-col-toggle';
      const cb = doc.createElement('input');
      cb.type = 'checkbox';
      cb.checked = visibleKeys.has(col.key);
      cb.addEventListener('change', () => {
        if (cb.checked) visibleKeys!.add(col.key);
        else visibleKeys!.delete(col.key);
        buildHead();
        recompute();
      });
      const span = doc.createElement('span');
      span.textContent = col.label;
      label.appendChild(cb);
      label.appendChild(span);
      colsMenu.appendChild(label);
    }
  };

  const exportCsv = (): void => {
    const cols = effectiveColumns();
    const rows = allRows();
    const expCols = exportScope === 'all' ? cols : visibleColumns(cols);
    const expRows = exportScope === 'all'
      ? rows
      : sortRows(filterRows(rows, filters, cols), cols, sortKey, sortDir);
    const csv = toCsv(expRows, expCols);
    let name = slotOr('exportFilename', 'data-app-export-filename');
    if (!name) {
      const t = (slotOr('title', '') || titleEl?.textContent || 'export').trim().replace(/\s+/g, '-') || 'export';
      const d = new Date();
      const iso = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
      name = `${t}-${iso}`;
    }
    if (!name.endsWith('.csv')) name += '.csv';
    // Vienti kulkee sillan yli: Appin iframe on sandbox="allow-scripts" ILMAN
    // allow-downloadsia, joten tässä rakennettu <a download> ei lataa mitään.
    // Selain estää latauksen HEITTÄMÄTTÄ, joten aiempi try/catch ei voinut
    // havaita sitä — nappi näytti toimimattomalta ilman yhtään virhettä.
    // Parent (tai serving-tilassa silta itse) tekee latauksen.
    if (!bridge) return; // kehystämätön esikatselu — sama sopimus kuin muilla siltapoluilla
    bridge.saveText(name, csv, 'csv');
  };

  // Alustus: 'data'-passthrough ei-bound tapaukselle (script-tag).
  if (store.getSlot(id, 'data') === undefined) {
    const initEl = grid.querySelector('.sp-data-grid-initial');
    store.setSlot(id, 'data', coerceRows(initEl?.textContent || '[]'));
  }
  store.setSlot(id, 'isVisible', grid.style.display !== 'none');
  buildColsMenu();
  buildHead();
  recompute();
  exportBtn?.addEventListener('click', exportCsv);

  // Reaktiivisuus.
  store.subscribe(`${id}.data`, recompute);
  store.subscribe(`${id}.columns`, () => {
    visibleKeys = null; // uudet sarakkeet → uudelleenalustus
    buildColsMenu();
    buildHead();
    recompute();
  });
  store.subscribe(`${id}.visibleOffset`, recompute);
  store.subscribe(`${id}.visiblePageSize`, recompute);
  store.subscribe(`${id}.isLoading`, recompute);
  store.subscribe(`${id}.title`, () => {
    if (titleEl) titleEl.textContent = String(store.getSlot(id, 'title') ?? '');
  });
}

/**
 * Kanban (AB-WP6.14): sarakkeet + raahattavat kortit. Runtime omistaa boardin;
 * logiikka on kanban.ts:ssä (puhdas). Raahaus natiivilla HTML5 drag-dropilla
 * (draggedId-sulkeuma + dataTransfer-fallback). Kuluttaa bound columns/cards-
 * slotteja; reaktiivinen niiden + readOnly/emptyText-muutoksiin. Feedback-loop
 * vältetty ownUpdate-lipulla (kortin siirto julkaisee cards-slotin muttei
 * uudelleenrakenna omasta setSlotista). data-app-kanban-board = wire-up-marker.
 */
function wireKanban(
  kanban: HTMLElement,
  store: Store,
  bridge: BridgeClient | null,
  notify: (message: string, severity: string) => void,
  runLifecycle: RowLifecycleRunner,
): void {
  const id = kanban.getAttribute('data-app-id') || '';
  if (!id) return;
  const doc = kanban.ownerDocument;
  const board = kanban.querySelector<HTMLElement>('[data-app-kanban-board]');
  if (!board) return;
  const colWidth = num(kanban.getAttribute('data-app-col-width'), 280);

  let columns: KanbanColumn[] = parseKanbanColumns(readInitial('columns'));
  let cards: KanbanCard[] = parseCards(readInitial('cards'));
  let selectedId = '';
  let draggedId = '';
  let ownUpdate = false;

  function readInitial(kind: 'columns' | 'cards'): unknown {
    const slot = store.getSlot(id, kind);
    if (slot !== undefined) return slot;
    const el = kanban.querySelector(kind === 'columns' ? '.sp-kanban-cols' : '.sp-kanban-cards');
    return el?.textContent || '[]';
  }
  const isReadOnly = (): boolean => {
    const v = store.getSlot(id, 'readOnly');
    return v === undefined ? kanban.getAttribute('data-app-readonly') === '1' : !!v;
  };
  const emptyText = (): string => {
    const v = store.getSlot(id, 'emptyText');
    return typeof v === 'string' ? v : kanban.getAttribute('data-app-empty') || '';
  };

  const publishCards = (): void => {
    ownUpdate = true;
    store.setSlot(id, 'cards', cards);
    ownUpdate = false;
    store.setSlot(id, 'columnCounts', columnCounts(cards, columns));
  };

  /**
   * Siirron tallennuskonfigi (WP-KL4, löydös E-1). Sama muoto ja sama lukutapa
   * kuin dynamic-tablen rivitoiminnolla — konfigi puuttuu → board on entiseen
   * tapaan pelkkä näkymä, ja rakentaja voi yhä tallentaa siirron omalla napilla.
   */
  const moveConfig = ((): {
    apiId: string;
    mapping: RowActionMappingEntry[];
    lifecycle: unknown[];
  } | null => {
    const script = kanban.querySelector('script.sp-app-kanban-move');
    if (!script) return null;
    try {
      const parsed = JSON.parse(script.textContent || '{}');
      const apiId = typeof parsed.apiId === 'string' ? parsed.apiId : '';
      if (!apiId) return null;
      return {
        apiId,
        mapping: Array.isArray(parsed.mapping) ? parsed.mapping : [],
        // WP-KH2: siirron lifecycle-kortit (moveLifecycleActions) samassa
        // configissa kuin rivitoiminnolla — board renderöi korttinsa itse.
        lifecycle: Array.isArray(parsed.lifecycleActions) ? parsed.lifecycleActions : [],
      };
    } catch {
      return null;
    }
  })();

  const dropCard = (cardId: string, toColumnId: string): void => {
    const card = cards.find((c) => c.id === cardId);
    if (!card || card.columnId === toColumnId) return;
    const fromColumnId = card.columnId;
    const moved = { ...card };
    cards = moveCard(cards, cardId, toColumnId);
    store.setSlot(id, 'lastMovedCard', { cardId, fromColumnId, toColumnId });
    publishCards();
    render();
    if (!moveConfig || !bridge) return;

    // Siirto on jo ruudulla (optimistinen), koska raahaus tuntuu rikkinäiseltä jos
    // kortti jää roikkumaan verkkokutsun ajaksi. `D6` = A: jos kutsu EPÄONNISTUU,
    // siirto perutaan — koko löydöksen ydin oli että ruutu näytti onnistuneelta
    // vaikka mitään ei tallentunut, eikä sitä virhettä toisteta pienemmässä
    // mittakaavassa.
    const revert = (message: string): void => {
      cards = moveCard(cards, cardId, fromColumnId);
      store.setSlot(id, 'lastMovedCard', { cardId, fromColumnId: toColumnId, toColumnId: fromColumnId });
      publishCards();
      render();
      notify(message, 'error');
    };
    const body = buildRowActionBody(
      moveConfig.mapping, moved as unknown as Record<string, unknown>,
      (expr) => evalExpression(expr, store.state),
      { cardId, fromColumnId, toColumnId },
    );
    // WP-KH2: siirto ylsi ennen VAIN omaan boardiinsa. Tallennettu siirto
    // muuttaa käytännössä aina myös jotain muuta sivulla — tunnuslukua,
    // yhteenvetoa — ja ne jäivät vanhentuneiksi ilman mitään merkkiä siitä,
    // eikä rakentajalla ollut mitään mihin refresh-kortin olisi voinut ripustaa.
    // Sama kuvio ja samat ajoitukset kuin rivitoiminnolla (WP-HV3): on-click
    // pudotushetkellä ennen kutsua, on-success / on-api-fail vastauksesta.
    const lifecycle = moveConfig.lifecycle;
    runLifecycle(lifecycle, kanban, 'on-click');
    // WP-VV3: peruutusilmoitus kertoo palvelimen oman viestin kun sellainen on,
    // ja kortit näkevät vastauksen nimillä status/response/errorMessage.
    bridge.invoke(moveConfig.apiId, body).then(
      (res) => {
        const outcome = describeResponse(res);
        if (!outcome.ok) {
          revert(outcome.errorMessage || resolveMessage('moveFailed', res.statusCode));
          // D3 = A: kortit perumisen JÄLKEEN. Peruminen on osa epäonnistumista
          // eikä sen jälkihoitoa — kortit kuvaavat lopputilaa (kortti on
          // takaisin lähtösarakkeessa), eivät välitilaa.
          runLifecycle(lifecycle, kanban, 'on-api-fail', responseState(outcome));
          return;
        }
        runLifecycle(lifecycle, kanban, 'on-success', responseState(outcome));
      },
      () => {
        const outcome = describeResponse(null);
        revert(outcome.errorMessage);
        runLifecycle(lifecycle, kanban, 'on-api-fail', responseState(outcome));
      },
    );
  };

  const selectCard = (cardId: string): void => {
    selectedId = selectedId === cardId ? '' : cardId;
    store.setSlot(id, 'selectedCardId', selectedId);
    board.querySelectorAll<HTMLElement>('.sp-kanban-card').forEach((el) => {
      el.classList.toggle('sp-kanban-card--selected', el.getAttribute('data-app-card-id') === selectedId);
    });
  };

  const buildCard = (card: KanbanCard, readOnly: boolean): HTMLElement => {
    const el = doc.createElement('div');
    el.className = 'sp-kanban-card';
    el.setAttribute('data-app-card-id', card.id);
    if (card.id === selectedId) el.classList.add('sp-kanban-card--selected');
    const title = doc.createElement('div');
    title.className = 'sp-kanban-card-title';
    title.textContent = card.title;
    el.appendChild(title);
    if (card.description) {
      const desc = doc.createElement('div');
      desc.className = 'sp-kanban-card-desc';
      desc.textContent = card.description;
      el.appendChild(desc);
    }
    if (card.badges && card.badges.length) {
      const badges = doc.createElement('div');
      badges.className = 'sp-kanban-card-badges';
      for (const b of card.badges) {
        const chip = doc.createElement('span');
        chip.className = 'sp-kanban-chip';
        chip.textContent = b;
        badges.appendChild(chip);
      }
      el.appendChild(badges);
    }
    el.addEventListener('click', () => selectCard(card.id));
    if (!readOnly) {
      el.draggable = true;
      el.addEventListener('dragstart', (e) => {
        draggedId = card.id;
        if (e.dataTransfer) {
          e.dataTransfer.setData('text/plain', card.id);
          e.dataTransfer.effectAllowed = 'move';
        }
      });
    }
    return el;
  };

  const render = (): void => {
    const readOnly = isReadOnly();
    board.textContent = '';
    for (const column of columns) {
      const colEl = doc.createElement('div');
      colEl.className = 'sp-kanban-col';
      colEl.setAttribute('data-app-col-id', column.id);
      colEl.style.width = `${colWidth}px`;
      const header = doc.createElement('div');
      header.className = 'sp-kanban-col-header';
      if (column.color) header.style.borderTopColor = column.color;
      const nameEl = doc.createElement('span');
      nameEl.className = 'sp-kanban-col-name';
      nameEl.textContent = column.name;
      const countEl = doc.createElement('span');
      countEl.className = 'sp-kanban-col-count';
      const colCards = cardsInColumn(cards, column.id);
      countEl.textContent = String(colCards.length);
      header.appendChild(nameEl);
      header.appendChild(countEl);
      colEl.appendChild(header);

      const list = doc.createElement('div');
      list.className = 'sp-kanban-list';
      list.setAttribute('data-app-col-id', column.id);
      if (!readOnly) {
        // WP-HV2 (löydös K-5, D2 = A): kuuntelijat ovat SARAKKEELLA, eivät
        // listalla. Sama data-app-col-id on molemmilla elementeillä, ja kun
        // kuuntelijat olivat vain sisemmällä, ulompaan osuva valitsin ei tehnyt
        // mitään eikä kertonut miksi — pudotus vain ei rekisteröitynyt.
        // Kuplinnan ansiosta tämä kattaa listan, korttien välit, tyhjän tilan
        // JA sarakkeen otsikon, mikä on myös oikea käytös eikä vain
        // valitsinkorjaus. Korostus jää listalle: se on visuaalinen kohde.
        colEl.addEventListener('dragover', (e) => {
          e.preventDefault();
          if (e.dataTransfer) e.dataTransfer.dropEffect = 'move';
          list.classList.add('sp-kanban-list--over');
        });
        colEl.addEventListener('dragleave', (e) => {
          // Kuplinnassa dragleave laukeaa myös sarakkeen sisäisiä rajoja
          // ylittäessä; korostus saa kadota vasta kun kohdistin poistuu
          // sarakkeesta kokonaan.
          const to = e.relatedTarget;
          if (to instanceof Node && colEl.contains(to)) return;
          list.classList.remove('sp-kanban-list--over');
        });
        colEl.addEventListener('drop', (e) => {
          e.preventDefault();
          list.classList.remove('sp-kanban-list--over');
          const cardId = draggedId || (e.dataTransfer ? e.dataTransfer.getData('text/plain') : '');
          draggedId = '';
          if (cardId) dropCard(cardId, column.id);
        });
      }
      if (colCards.length === 0) {
        const empty = doc.createElement('div');
        empty.className = 'sp-kanban-empty';
        empty.textContent = emptyText();
        list.appendChild(empty);
      } else {
        for (const card of colCards) list.appendChild(buildCard(card, readOnly));
      }
      colEl.appendChild(list);
      board.appendChild(colEl);
    }
  };

  // Alustus: passthrough-slotit ei-bound tapaukselle.
  if (store.getSlot(id, 'columns') === undefined) store.setSlot(id, 'columns', columns);
  if (store.getSlot(id, 'cards') === undefined) {
    ownUpdate = true;
    store.setSlot(id, 'cards', cards);
    ownUpdate = false;
  }
  store.setSlot(id, 'columnCounts', columnCounts(cards, columns));
  store.setSlot(id, 'selectedCardId', '');
  store.setSlot(id, 'isVisible', kanban.style.display !== 'none');
  render();

  // Reaktiivisuus.
  store.subscribe(`${id}.columns`, () => {
    columns = parseKanbanColumns(store.getSlot(id, 'columns'));
    store.setSlot(id, 'columnCounts', columnCounts(cards, columns));
    render();
  });
  store.subscribe(`${id}.cards`, () => {
    if (ownUpdate) return; // oma siirto — ei uudelleenrakenneta
    cards = parseCards(store.getSlot(id, 'cards'));
    store.setSlot(id, 'columnCounts', columnCounts(cards, columns));
    render();
  });
  store.subscribe(`${id}.readOnly`, render);
  store.subscribe(`${id}.emptyText`, render);
}

/**
 * Gantt (AB-WP6.15): tehtäväpalkit päivämääräakselilla + edistymis-overlay +
 * riippuvuusnuolet (SVG). Runtime piirtää; layout-matikka on gantt.ts:ssä
 * (puhdas). Kuluttaa bound tasks-slottia; reaktiivinen tasks/emptyText-
 * muutoksiin. data-app-gantt-body = wire-up-marker.
 */
function wireGantt(gantt: HTMLElement, store: Store): void {
  const id = gantt.getAttribute('data-app-id') || '';
  if (!id) return;
  const doc = gantt.ownerDocument;
  const body = gantt.querySelector<HTMLElement>('[data-app-gantt-body]');
  if (!body) return;
  const SVG_NS = 'http://www.w3.org/2000/svg';
  const scale = (gantt.getAttribute('data-app-scale') || 'week') as GanttScale;
  const rowHeight = num(gantt.getAttribute('data-app-row-height'), 32);
  const showProgress = gantt.getAttribute('data-app-show-progress') === '1';
  const showDeps = gantt.getAttribute('data-app-show-deps') === '1';
  const LABEL_W = 160;
  const AXIS_H = 24;
  const BAR_PAD = 4;

  let tasks: GanttTask[] = parseTasks(readInitial());
  let selectedId = '';

  function readInitial(): unknown {
    const slot = store.getSlot(id, 'tasks');
    if (slot !== undefined) return slot;
    return gantt.querySelector('.sp-gantt-tasks')?.textContent || '[]';
  }
  const emptyText = (): string => {
    const v = store.getSlot(id, 'emptyText');
    return typeof v === 'string' ? v : gantt.getAttribute('data-app-empty') || '';
  };

  const selectTask = (task: GanttTask): void => {
    selectedId = selectedId === task.id ? '' : task.id;
    store.setSlot(id, 'selectedTaskId', selectedId);
    store.setSlot(id, 'selectedTask', selectedId ? task : null);
    body.querySelectorAll<HTMLElement>('.sp-gantt-bar').forEach((el) => {
      el.classList.toggle('sp-gantt-bar--selected', el.getAttribute('data-app-task-id') === selectedId);
    });
  };

  const render = (): void => {
    body.textContent = '';
    if (tasks.length === 0) {
      const empty = doc.createElement('div');
      empty.className = 'sp-gantt-empty';
      empty.textContent = emptyText();
      body.appendChild(empty);
      return;
    }
    const span = taskSpan(tasks)!;
    const pxPerDay = pxPerDayFor(scale);
    const totalPx = span.totalDays * pxPerDay;
    const layouts = new Map<string, { x: number; width: number; rowIndex: number }>();
    tasks.forEach((t, i) => {
      const l = layoutTask(t, span.originMs, pxPerDay);
      if (l) layouts.set(t.id, { x: l.x, width: l.width, rowIndex: i });
    });

    const grid = doc.createElement('div');
    grid.className = 'sp-gantt-grid';

    const labels = doc.createElement('div');
    labels.className = 'sp-gantt-labels';
    labels.style.width = `${LABEL_W}px`;
    const spacer = doc.createElement('div');
    spacer.className = 'sp-gantt-axis-spacer';
    spacer.style.height = `${AXIS_H}px`;
    labels.appendChild(spacer);
    for (const t of tasks) {
      const lab = doc.createElement('div');
      lab.className = 'sp-gantt-label';
      lab.style.height = `${rowHeight}px`;
      lab.textContent = t.name;
      labels.appendChild(lab);
    }
    grid.appendChild(labels);

    const timeline = doc.createElement('div');
    timeline.className = 'sp-gantt-timeline';
    timeline.style.width = `${totalPx}px`;
    const axis = doc.createElement('div');
    axis.className = 'sp-gantt-axis';
    axis.style.height = `${AXIS_H}px`;
    for (const tick of axisTicks(span.originMs, span.totalDays, scale, pxPerDay)) {
      const tk = doc.createElement('div');
      tk.className = 'sp-gantt-tick';
      tk.style.left = `${tick.x}px`;
      tk.textContent = tick.label;
      axis.appendChild(tk);
    }
    timeline.appendChild(axis);

    const tracks = doc.createElement('div');
    tracks.className = 'sp-gantt-tracks';
    tracks.style.height = `${tasks.length * rowHeight}px`;
    tasks.forEach((t, i) => {
      const info = layouts.get(t.id);
      if (!info) return;
      const bar = doc.createElement('div');
      bar.className = 'sp-gantt-bar';
      bar.setAttribute('data-app-task-id', t.id);
      if (t.id === selectedId) bar.classList.add('sp-gantt-bar--selected');
      bar.style.left = `${info.x}px`;
      bar.style.width = `${info.width}px`;
      bar.style.top = `${i * rowHeight + BAR_PAD}px`;
      bar.style.height = `${rowHeight - 2 * BAR_PAD}px`;
      if (t.color) bar.style.background = t.color;
      if (showProgress) {
        const prog = doc.createElement('div');
        prog.className = 'sp-gantt-progress';
        prog.style.width = `${t.progress}%`;
        bar.appendChild(prog);
      }
      const lbl = doc.createElement('span');
      lbl.className = 'sp-gantt-bar-label';
      lbl.textContent = t.name;
      bar.appendChild(lbl);
      bar.addEventListener('click', () => selectTask(t));
      tracks.appendChild(bar);
    });

    if (showDeps) {
      const svg = doc.createElementNS(SVG_NS, 'svg');
      svg.setAttribute('class', 'sp-gantt-deps');
      svg.setAttribute('width', String(totalPx));
      svg.setAttribute('height', String(tasks.length * rowHeight));
      const defs = doc.createElementNS(SVG_NS, 'defs');
      const marker = doc.createElementNS(SVG_NS, 'marker');
      marker.setAttribute('id', `${id}-arrow`);
      marker.setAttribute('markerWidth', '8');
      marker.setAttribute('markerHeight', '8');
      marker.setAttribute('refX', '6');
      marker.setAttribute('refY', '3');
      marker.setAttribute('orient', 'auto');
      marker.setAttribute('markerUnits', 'userSpaceOnUse');
      const mp = doc.createElementNS(SVG_NS, 'path');
      mp.setAttribute('d', 'M0,0 L6,3 L0,6 Z');
      mp.setAttribute('class', 'sp-gantt-dep-head');
      marker.appendChild(mp);
      defs.appendChild(marker);
      svg.appendChild(defs);
      for (const t of tasks) {
        const succ = layouts.get(t.id);
        if (!succ) continue;
        for (const depId of t.dependencies) {
          const pred = layouts.get(depId);
          if (!pred) continue;
          const x1 = pred.x + pred.width;
          const y1 = pred.rowIndex * rowHeight + rowHeight / 2;
          const x2 = succ.x;
          const y2 = succ.rowIndex * rowHeight + rowHeight / 2;
          const midX = Math.max(x1 + 8, x2 - 8);
          const path = doc.createElementNS(SVG_NS, 'path');
          path.setAttribute('d', `M${x1},${y1} H${midX} V${y2} H${x2}`);
          path.setAttribute('class', 'sp-gantt-dep');
          path.setAttribute('marker-end', `url(#${id}-arrow)`);
          svg.appendChild(path);
        }
      }
      tracks.appendChild(svg);
    }
    timeline.appendChild(tracks);
    grid.appendChild(timeline);
    body.appendChild(grid);
  };

  if (store.getSlot(id, 'tasks') === undefined) store.setSlot(id, 'tasks', tasks);
  store.setSlot(id, 'selectedTaskId', '');
  store.setSlot(id, 'selectedTask', null);
  store.setSlot(id, 'isVisible', gantt.style.display !== 'none');
  render();

  store.subscribe(`${id}.tasks`, () => {
    tasks = parseTasks(store.getSlot(id, 'tasks'));
    render();
  });
  store.subscribe(`${id}.emptyText`, render);
}

/** Vite define -vakio; testeissä (vitest) määritellään configissa. */
declare const __APP_RUNTIME_VERSION__: string;
